import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

export const CarSVG = React.forwardRef(
  (
    {
      partsDamageMap,
      isLoading,
      setPartName,
      openDamagedPartsModal,
      isInModal,
      clickedPart,
      setClickedPart,
      initialActiveView,
      onActiveViewChange,
    },
    ref
  ) => {
    const svgId = sessionStorage.getItem("svgId");
    const [selectedPart, setSelectedPart] = useState(null);

    const [tooltip, setTooltip] = useState({
      visible: false,
      x: 0,
      y: 0,
      text: "",
      bg: "",
    });

    const onPartLeave = () => {
      setSelectedPart(null);
      setTooltip((prev) => ({ ...prev, visible: false }));
    };

    function formatPartName(str) {
      if (!str) {
        return "-";
      }
      return str
        .replace(/[-_.]/g, " ") // Replace separators with spaces
        .split(" ") // Split into words
        .filter(Boolean) // Remove empty parts
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ) // Capitalize
        .join(" ");
    }
    // const svgRef = useRef(null);

    const onPartHover = (svgId, e) => {
      if (!svgId) return;
      const data = partsDamageMap[svgId];
      setSelectedPart(svgId);

      // Use viewport coordinates so the tooltip works consistently,
      // including when rendered via portal inside the modal.
      const x = e.clientX;
      const y = e.clientY;

      setTooltip({
        visible: true,
        x: x,
        y: y,
        text: `${data?.name || formatPartName(svgId)} - ${
          data?.healthText || "Not Found"
        }`,
        bg: data?.color
          ? data.color.replace(/rgba?\(([^)]+)\)/, (match, p1) => {
              const parts = p1.split(",").map((s) => s.trim());
              if (parts.length === 3) parts.push("0.9");
              else parts[3] = "0.9";
              return `rgba(${parts.join(",")})`;
            })
          : "rgba(110,110,110,0.9)",
      });
    };

    const onPartClickFunc = (svgId) => {
      if (!svgId) return;
      if (setClickedPart) {
        setClickedPart(svgId);
      }
      sessionStorage.setItem("svgId", svgId);
      const data = partsDamageMap[svgId];
      if (setPartName) {
        setPartName(data);
      }
      if (!data) return;
      let damageType;
      if (data.healthText === "Minor Damage") {
        damageType = "minor";
      } else if (data.healthText === "Severe Damage") {
        damageType = "severe";
      } else if (data.healthText === "Moderate Damage") {
        damageType = "moderate";
      } else {
        damageType = "No damage";
      }
      if (damageType && openDamagedPartsModal) {
        openDamagedPartsModal("damaged", damageType, data);
      }
    };

    const getPartStyle = (partId) => {
      if (isLoading || !partId) return;
      const data = partsDamageMap[partId];
      const baseStyle = {
        cursor: "pointer",
        transition: "all 0.3s ease",
        strokeWidth:
          selectedPart === partId ? "3" : clickedPart === partId ? "6" : "1",
      };

      if (data) {
        baseStyle.fill =
          data.healthText === "Good" ? "rgb(255, 255, 255)" : data.color;
        if (selectedPart === partId) {
          baseStyle.stroke = "#2196F3";
          baseStyle.filter = "drop-shadow(0 0 8px rgba(33, 150, 243, 0.6))";
        }
        if (clickedPart === partId) {
          baseStyle.stroke = "#2196F3";
          baseStyle.filter = "drop-shadow(0 0 8px rgba(33, 150, 243, 0.6))";
          baseStyle.fill =
            data.healthText === "Good"
              ? "rgb(224, 224, 224)"
              : data.healthText === "Moderate Damage"
              ? "rgb(251, 191, 36)"
              : data.healthText === "Minor Damage"
              ? "rgb(251, 146, 60)"
              : data.healthText === "Severe Damage"
              ? "rgb(239, 58, 58)"
              : data.color;
        }
      } else {
        baseStyle.fill = "rgb(110, 110, 110)";
      }

      return baseStyle;
    };

    const defaultActiveView =
      initialActiveView !== undefined && initialActiveView !== null
        ? initialActiveView
        : 0;

    const [activeView, setActiveView] = useState(defaultActiveView);
    const svgRefs = [
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
    ];

    // Expose activeView to parent component via ref
    React.useImperativeHandle(ref, () => ({
      getActiveView: () => activeView,
    }));

    // Scroll container ref
    const scrollContainerRef = useRef(null);

    const views = [
      { id: 0, label: "View 1", icon: "①" },
      { id: 1, label: "View 2", icon: "②" },
      { id: 2, label: "View 3", icon: "③" },
      { id: 3, label: "View 4", icon: "④" },
      { id: 4, label: "View 5", icon: "⑤" },
    ];

    const getFrontRearScaleClass = () =>
      isInModal ? "scale-[2]" : "scale-[1.65]";

    const getRearViewPositionClass = () =>
      isInModal ? "-translate-y-2" : "-translate-y-12";

    const scrollToView = (index) => {
      setActiveView(index);
      onActiveViewChange?.(index);
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const viewWidth = container.scrollWidth / views.length;
        container.scrollTo({
          left: viewWidth * index,
          behavior: "smooth",
        });
      }
    };

    const handlePrevious = () => {
      if (activeView > 0) {
        scrollToView(activeView - 1);
      }
    };

    const handleNext = () => {
      if (activeView < views.length - 1) {
        scrollToView(activeView + 1);
      }
    };

    // Track scroll position to update active view
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        const scrollLeft = container.scrollLeft;
        const viewWidth = container.scrollWidth / views.length;
        const newActiveView = Math.round(scrollLeft / viewWidth);
        if (newActiveView !== activeView) {
          setActiveView(newActiveView);
          onActiveViewChange?.(newActiveView);
        }
      };

      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }, [activeView, onActiveViewChange, views.length]);

    // Scroll to initial view when component mounts or initialActiveView changes
    useEffect(() => {
      scrollToView(defaultActiveView);
    }, [defaultActiveView]);

    const tooltipContent =
      tooltip.visible &&
      ReactDOM.createPortal(
        <div
          className="fixed px-3 py-2 text-sm text-white font-semibold rounded shadow-2xl pointer-events-none z-[9999] whitespace-nowrap"
          style={{
            top: isInModal ? tooltip.y - 48 : tooltip.y + 10,
            left: tooltip.x + 10,
            backgroundColor: tooltip.bg,
          }}
        >
          {tooltip.text}
        </div>,
        document.body
      );

    return (
      <div className="w-full">
        {tooltipContent}

        {/* Navigation Container */}
        <div className="relative ">
          {/* Left Arrow */}
          <button
            onClick={handlePrevious}
            disabled={activeView === 0}
            className={`${
              isInModal ? "fixed left-4 top-[24vh]" : "absolute left-0 top-1/2"
            } -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg transition-all ${
              activeView === 0
                ? "opacity-30 cursor-not-allowed"
                : "hover:bg-gray-100 hover:shadow-xl"
            }`}
            aria-label="Previous view"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Right Arrow */}
          <button
            onClick={handleNext}
            disabled={activeView === views.length - 1}
            className={`${
              isInModal
                ? "fixed right-4 top-[24vh]"
                : "absolute right-0 top-1/2"
            } -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg transition-all ${
              activeView === views.length - 1
                ? "opacity-30 cursor-not-allowed"
                : "hover:bg-gray-100 hover:shadow-xl"
            }`}
            aria-label="Next view"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Scrollable Container with 5 separate views */}
          <div
            ref={scrollContainerRef}
            className={`flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide ${
              isInModal ? "h-[39vh]" : "h-[80vh]"
            }`}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {/* Slide 2 - Front view */}
            <div
              className={`order-2 flex-shrink-0 w-full h-full snap-center flex items-center justify-center -rotate-90 overflow-hidden transform-gpu origin-center ${getFrontRearScaleClass()} ${getRearViewPositionClass()}`}
            >
              <svg
                ref={svgRefs[1]}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="-550 -43.5 1584 1086"
                className={isInModal ? "h-auto w-full" : "max-h-full w-full"}
              >
                <g id="Layer_6"></g>
                <g id="Layer_7">
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M164.398,330.789l0.007-0.099c0.145-1.089,2.659-2.841,3.405-3.657
		c0.018-0.02,0.042-0.036,0.062-0.056c-7.65,0.531-14.998,2.638-19.341,4.664c-0.276,0.129-1.056,0.572-1.056,0.572
		c-5.357,3.103-5.05,6.595-5.05,7.12l0.12,28.541c0.314,3.186,4.388,5.719,5.274,6.262c0,0,0.47,0.284,1.664,0.761
		c4.044,1.611,7.328,1.477,14.804,2.056c0.128-9.486,0.198-18.891,0.198-28.195C164.486,342.728,164.457,336.738,164.398,330.789z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M164.398,650.017l0.007,0.1c0.145,1.089,3.105,3.667,3.126,3.687
		c-7.65-0.529-14.658-2.61-19.001-4.639c-0.276-0.13-1.056-0.572-1.056-0.572c-5.357-3.101-5.051-6.595-5.051-7.118l0.121-28.541
		c0.313-3.187,4.388-5.72,5.274-6.263c0,0,0.47-0.283,1.664-0.76c4.044-1.611,7.444-1.479,14.922-2.056
		c-0.082,10.063,0,18.838,0,28.145C164.405,638.027,164.457,644.069,164.398,650.017z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M178.694,379.566c17.519,14.84,43.256,30.869,44.983,31.892
		c4.822,2.856,12.641,9.625,18.291-9.176l7.778-28l6.879-25.556c0.245-0.467,2.27-1.796,2.296-1.882
		c3.698-12.064,10.873-15.627,10.873-15.627c-1.841-4.136-6.741-6.091-6.741-6.091l-11.292-0.965
		c-7.604-0.662-15.438-0.631-23.313-0.631c-16.333,0-29.853-0.131-45.136,1.162h-13.669c-1.213,0.181-1.213,1.23-1.213,1.23
		l-0.181,5.412l6.533,0.693c1.486,0.263,2.896-0.073,2.896,1.755C177.679,342.369,174.736,374.74,178.694,379.566z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-right-fog-lamp"
                    // onClick={(e) => onPartClick("right-fog-lamp", e)}
                    style={getPartStyle("right-fog-lamp")}
                    onClick={(e) => onPartClickFunc("right-fog-lamp", e)}
                    onMouseEnter={(e) => onPartHover("right-fog-lamp", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M222.362,345.827l5.545,30.18l0.265,1.903
		c0.082,1.231-1.156,0.992-1.394,0.867c-0.47-0.256-4.259-3.35-4.167-3.276c0,0-24.275-19.583-28.716-26.628
		c-1.735-2.754-2.896-9.339-3.062-12.5c-0.087-1.644-0.182-7.35,0.204-7.577c0.606-0.355,10.849,2.142,10.846,2.141h0.003
		c7.699,1.685,11.59,2.667,11.595,2.67l0.01,0.002l0.009,0.003C217.568,334.635,221.257,338.823,222.362,345.827z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M234.035,425.753l16.06-18.942c1.025-0.966,2.48,0.584,2.177,1.746
		c-0.137,0.518-1.451,5.482-1.909,7.198c0,0-7.868,29.738-11.317,37.737c-1.407,3.26-6.962,10.544-8.803,13.139
		c0,0-2.877,4.03-3.189,5.6c-0.138,0.688,2.347-36.459,2.347-36.459C229.371,436.172,229.512,431.182,234.035,425.753z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M233.081,554.68l16.06,18.941c1.024,0.966,2.409-0.561,2.104-1.723
		c-0.135-0.519-1.604-6.14-1.839-7.223c-0.542-2.514-8.505-32.509-11.641-37.771c-1.817-3.051-2.174-4.824-8.157-13.256
		c0,0-2.993-4.432-3.307-6.24c-0.119-0.693,2.141,37.251,2.141,37.251C228.415,544.26,228.558,549.25,233.081,554.68z"
                  />
                  <g>
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M176.82,647.143c0,1.828-1.41,1.49-2.896,1.755l-6.532,0.692
			l0.181,5.411c0,0,0,1.049,1.213,1.23h13.666c15.285,1.292,28.803,1.163,45.137,1.163c7.876,0,15.711,0.028,23.314-0.633
			l11.293-0.966c0,0,4.897-1.954,6.739-6.09c0,0-7.175-3.562-10.872-15.628c-0.026-0.086-2.051-1.415-2.296-1.882l-6.881-25.555
			l-7.777-28.001c-5.65-18.802-13.47-12.032-18.29-9.177c-1.728,1.023-27.465,17.053-44.984,31.893
			C173.874,606.187,176.82,638.557,176.82,647.143z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-fog-lamp"
                      style={getPartStyle("left-fog-lamp")}
                      onClick={(e) => onPartClickFunc("left-fog-lamp", e)}
                      onMouseEnter={(e) => onPartHover("left-fog-lamp", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M212.638,647.315l-0.009,0.004l-0.01,0.003
			c-0.006,0.001-3.896,0.983-11.595,2.667h-0.002c0.002,0-10.241,2.498-10.848,2.142c-0.386-0.227-0.291-5.934-0.204-7.575
			c0.167-3.162,1.327-9.746,3.062-12.5c4.44-7.047,28.716-26.63,28.716-26.63c-0.091,0.073,3.698-3.02,4.168-3.274
			c0.236-0.127,1.477-0.365,1.394,0.866l-0.266,1.901l-5.545,30.181C220.398,642.103,216.707,646.292,212.638,647.315z"
                    />
                  </g>
                  <g>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-front-bumper"
                      style={getPartStyle("front-bumper")}
                      onClick={(e) => onPartClickFunc("front-bumper", e)}
                      onMouseEnter={(e) => onPartHover("front-bumper", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M173.823,585.829c0,0,0,0.002,0,0.003V585.829L173.823,585.829
			c-0.171-0.661-1.178-31.218-1.178-31.218s0-44.658,0-64.317c0-16.949,0.731-79.694,1.85-96.539
			c0.179-2.692,3.88-4.062,6.021-2.571c4.84,3.372,14.056,10.679,14.056,10.679s7.594,5.986,11.118,9.099
			c0.011,0.008,0.023,0.016,0.027,0.023c0.23,0.203,0.47,0.403,0.694,0.606c1.232,1.112,2.407,2.235,3.516,3.372
			c1.109,1.138,2.151,2.286,3.113,3.45c0.514,0.62,1,1.245,1.469,1.873c0,0.002,0.003,0.005,0.003,0.008
			c0.412,0.55,0.808,1.104,1.18,1.661c1.519,2.276,2.706,4.615,3.523,7.026h0.017l0.119,0.372l0,0l0.17,0.528h-0.023
			c0.609,2.059,0.951,4.167,0.951,6.342c0,0.776-0.042,1.562-0.128,2.355l0,0l-1.707,51.715l1.707,51.713l0,0
			c0.085,0.794,0.128,1.58,0.128,2.356c0,1.179-0.096,2.34-0.28,3.485c-0.157,0.965-0.39,1.914-0.67,2.855h0.023l-0.17,0.527l0,0
			l-0.119,0.372l-0.016,0.001c-1.017,3.002-2.623,5.89-4.704,8.688c0,0.002-0.003,0.005-0.003,0.007
			c-2.386,3.207-5.389,6.295-8.792,9.304c-0.004,0.009-0.017,0.015-0.024,0.022c-3.526,3.112-19.46,14.854-23.635,17.832
			l-0.023,0.02l-0.031,0.016C182.001,587.493,175.531,592.593,173.823,585.829z"
                    />
                    <polygon
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      points="173.823,585.832 173.823,585.83 173.823,585.829 		"
                    />
                  </g>
                  <g>
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M254.355,476.332c-1.205-0.754-2.506-1.161-3.871-1.161
			c-1.366,0-2.667,0.407-3.872,1.161c-1.808,1.129-3.387,3.053-4.506,5.51c-1.121,2.456-1.782,5.438-1.782,8.651
			c-0.002,4.285,1.177,8.157,3.046,10.92c0.933,1.383,2.037,2.487,3.241,3.241c1.205,0.754,2.506,1.161,3.873,1.162
			c1.363-0.001,2.666-0.408,3.871-1.162c1.807-1.13,3.386-3.052,4.506-5.511c1.121-2.454,1.782-5.438,1.782-8.651
			c0.001-4.286-1.18-8.157-3.049-10.92C256.662,478.19,255.559,477.086,254.355,476.332z"
                    />
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M250.483,476.921c1.197,0,2.342,0.422,3.352,1.148
			c1.52,1.09,2.771,2.845,3.656,4.999c0.885,2.153,1.401,4.71,1.401,7.425c-0.002,3.619-0.917,6.959-2.407,9.436
			c-0.746,1.238-1.639,2.263-2.651,2.986c-1.01,0.728-2.153,1.15-3.352,1.149c-1.197,0.001-2.34-0.423-3.352-1.149
			c-1.519-1.088-2.77-2.845-3.655-4.996c-0.885-2.153-1.4-4.711-1.401-7.426c0.003-3.62,0.916-6.959,2.406-9.436
			c0.746-1.238,1.639-2.263,2.65-2.988S249.284,476.921,250.483,476.921z"
                    />
                  </g>
                  <polygon
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    points="254.702,461.114 254.702,461.113 254.702,461.112 	"
                  />
                  <polygon
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    points="254.702,519.874 254.702,519.874 254.702,519.872 	"
                  />
                  <g>
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M254.975,475.343c-1.365-0.855-2.891-1.34-4.491-1.339
			c-1.603,0-3.126,0.483-4.492,1.339c-1.365,0.856-2.577,2.081-3.588,3.577c-2.021,2.994-3.246,7.076-3.247,11.574
			c0,3.373,0.692,6.514,1.888,9.135c1.196,2.621,2.897,4.73,4.948,6.018c1.365,0.855,2.891,1.339,4.492,1.339
			c1.601,0,3.125-0.482,4.491-1.339c1.365-0.857,2.577-2.082,3.588-3.577c2.02-2.992,3.245-7.075,3.247-11.574
			c-0.001-3.373-0.692-6.514-1.889-9.136S257.024,476.628,254.975,475.343z"
                    />
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M250.484,475.17c1.365,0,2.666,0.407,3.871,1.161
			c1.204,0.753,2.309,1.858,3.24,3.241c1.869,2.763,3.05,6.634,3.048,10.92c0.001,3.214-0.661,6.196-1.782,8.651
			c-1.12,2.458-2.699,4.381-4.506,5.511c-1.205,0.754-2.506,1.161-3.871,1.162c-1.366-0.001-2.667-0.408-3.872-1.162
			c-1.204-0.754-2.31-1.858-3.241-3.241c-1.869-2.762-3.049-6.635-3.048-10.92c0-3.215,0.662-6.195,1.783-8.651
			c1.119-2.457,2.697-4.381,4.505-5.51C247.816,475.578,249.117,475.171,250.484,475.17z"
                    />
                  </g>
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-right-head-lamp"
                    style={getPartStyle("right-head-lamp")}
                    onClick={(e) => onPartClickFunc("right-head-lamp", e)}
                    onMouseEnter={(e) => onPartHover("right-head-lamp", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M284.144,336.178c0,0-1.283-0.441-3.274-0.869
		c0,0-2.723-0.569-5.558-1.134c-2.177-0.433-4.422-0.811-5.52-1.229c-0.391-0.149-4.954,4.335-5.822,7.09
		s-18.247,63.439-18.247,63.439c-0.188,0.548,0.197,2.008,2.066,0.45c2.545-2.116,25.66-26.135,25.66-26.135s0.905-0.742,1.144-1.41
		c0.339-0.947,6.055-30.396,6.796-33.808c0.267-1.219,1.102-3.861,2.162-4.935C284.307,336.878,285.142,336.415,284.144,336.178z"
                  />
                  <path
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M272.332,374.427c0.056-0.364,0.093-0.733,0.093-1.113    c0-4.057-3.287-7.344-7.343-7.345c-4.057,0-7.344,3.288-7.345,7.345c0.001,4.056,3.288,7.344,7.345,7.345    c3.013-0.001,5.6-1.817,6.731-4.413C272.064,375.674,272.236,375.064,272.332,374.427z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M265.082,379.476c-1.703,0-3.241-0.689-4.356-1.806
			c-1.115-1.117-1.806-2.653-1.806-4.356s0.689-3.239,1.806-4.356c1.116-1.116,2.653-1.805,4.356-1.805s3.239,0.688,4.356,1.805
			c1.115,1.117,1.805,2.654,1.806,4.356c-0.001,1.704-0.689,3.241-1.806,4.356C268.323,378.787,266.785,379.476,265.082,379.476z"
                  />
                  <path
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M278.494,350.26c0.056-0.364,0.093-0.733,0.093-1.113    c0-4.056-3.287-7.343-7.343-7.344c-4.057,0-7.344,3.288-7.344,7.344c0,4.057,3.287,7.344,7.344,7.345    c3.013,0,5.599-1.816,6.731-4.413C278.226,351.507,278.398,350.896,278.494,350.26z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M271.244,355.309c-1.704-0.001-3.24-0.688-4.357-1.805
			c-1.114-1.117-1.804-2.653-1.804-4.355c0-1.704,0.688-3.24,1.804-4.357c1.117-1.116,2.653-1.805,4.357-1.805
			c1.703,0,3.239,0.688,4.356,1.805c1.115,1.117,1.805,2.653,1.805,4.357c0,1.702-0.688,3.239-1.805,4.355
			S272.948,355.308,271.244,355.309z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-left-head-lamp"
                    style={getPartStyle("left-head-lamp")}
                    onClick={(e) => onPartClickFunc("left-head-lamp", e)}
                    onMouseEnter={(e) => onPartHover("left-head-lamp", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M284.144,644.713c0,0-1.283,0.442-3.274,0.868
		c0,0-2.723,0.57-5.558,1.134c-2.177,0.434-5.281,1.104-6.379,1.523c-0.391,0.148-5.397-4.437-6.267-7.19
		c-0.869-2.756-16.883-61.822-16.883-61.822c-0.188-0.549,0.197-2.009,2.066-0.451c2.545,2.118,25.241,26.223,25.241,26.223
		s1.594,1.665,1.811,2.303c0.323,0.952,5.746,27.609,6.489,31.021c0.266,1.219,0.625,1.866,1.38,3.173
		C283.517,642.785,285.142,644.478,284.144,644.713z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M278.494,635.562c0.056-0.364,0.093-0.734,0.093-1.113
			c0-4.057-3.287-7.344-7.343-7.345c-4.057,0.001-7.344,3.288-7.344,7.345c0,4.056,3.287,7.343,7.344,7.344
			c3.013-0.001,5.599-1.816,6.731-4.412C278.226,636.809,278.398,636.197,278.494,635.562z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M271.244,640.609c-1.704,0-3.24-0.688-4.357-1.804
			c-1.114-1.118-1.804-2.653-1.804-4.357c0-1.703,0.688-3.239,1.804-4.356c1.117-1.115,2.653-1.804,4.357-1.804
			c1.703,0,3.239,0.688,4.356,1.804c1.115,1.118,1.805,2.653,1.805,4.356c0,1.704-0.688,3.24-1.805,4.357
			C274.485,639.922,272.948,640.609,271.244,640.609z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M272.425,610.361c0-0.38-0.037-0.75-0.093-1.113
			c-0.098-0.638-0.27-1.246-0.519-1.817c-1.134-2.598-3.72-4.412-6.732-4.412c-4.057,0-7.344,3.287-7.344,7.344
			c0,4.055,3.287,7.343,7.344,7.343S272.425,614.416,272.425,610.361z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M260.726,614.718c-1.115-1.116-1.806-2.652-1.806-4.355
			s0.689-3.241,1.806-4.357c1.115-1.114,2.652-1.805,4.355-1.805s3.24,0.689,4.357,1.805c1.114,1.116,1.805,2.654,1.805,4.357
			s-0.689,3.239-1.805,4.355c-1.117,1.116-2.654,1.804-4.357,1.805C263.379,616.521,261.841,615.834,260.726,614.718z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-grill"
                    style={getPartStyle("grill")}
                    onClick={(e) => onPartClickFunc("grill", e)}
                    onMouseEnter={(e) => onPartHover("grill", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M252.823,406.448c0,0-10.759,41.506-14.092,47.683
		c-3.282,6.081-11.278,15.251-11.729,18.339c-0.45,3.09-0.628,17.483-0.628,17.483s-0.537,16.228,0,17.796
		c1.786,5.213,8.803,12.356,12.703,22.129c3.9,9.771,10.77,35.384,13.745,48.769c0,0,0.481,3.716,3.232,6.61l15.396,16.409
		c0,0,2.799,2.532,3.903,1.113c1.507-1.937,1.162-2.989,1.162-2.989c0-5.115-0.555-107.052-0.673-112.842
		c-0.532-26.062,0.735-103.247,0.735-103.247s-0.182-1.818-1.269-4.226c0,0-0.916-0.705-3.956,2.006l-14.196,14.734
		C255.525,398.436,254.652,399.626,252.823,406.448z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M269.713,396c0,0-3.506,3.397-4.246,6.081
		c-0.73,2.647-11.39,58.684-11.39,58.684s-0.922,8.735-5.646,10.954c-3.861,1.813-11.063,6.953-11.011,18.61
		c0.062,13.382,7.365,17.3,10.984,18.893c3.62,1.593,4.966,6.02,5.72,10.884c0.688,4.428,10.218,57.597,10.976,59.247
		c1.187,2.588,2.075,3.707,4.613,6.458c0,0,1.136,1.57,0.627-1.421c-6.439-37.981-6.77-77.191-7.189-94.029
		c-0.448-17.923,4.99-77.316,7.532-93.559C270.684,396.801,271.03,395.094,269.713,396z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-logo"
                    style={getPartStyle("logo")}
                    onClick={(e) => onPartClickFunc("logo", e)}
                    onMouseEnter={(e) => onPartHover("logo", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M253.191,478.87c-0.865-0.619-1.773-0.942-2.708-0.943
		c-0.935,0.001-1.842,0.325-2.707,0.943c-1.296,0.924-2.466,2.522-3.297,4.551c-0.834,2.026-1.332,4.475-1.332,7.072
		c-0.001,3.466,0.888,6.661,2.269,8.948c0.688,1.145,1.497,2.058,2.36,2.675c0.865,0.618,1.772,0.941,2.707,0.943
		c0.934-0.002,1.843-0.325,2.708-0.943c1.295-0.924,2.466-2.522,3.297-4.551c0.833-2.025,1.332-4.474,1.331-7.072
		c0.002-3.465-0.887-6.662-2.268-8.947C254.863,480.401,254.053,479.486,253.191,478.87z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M168.397,326.978l-0.525,0.024c-1.277,1.273-3.259,2.63-3.467,3.688
		v319.426c0.316,1.283,3.126,3.687,3.126,3.687l-0.141-4.212l8.113-0.885c0,0,1.409-0.084,1.315-1.562
		c-5.775-91.81-4.174-138.364-4.174-155.842c0-97.596,5.18-147.547,5.019-156.238c-0.028-1.503,0.405-2.733-1.742-2.9l-7.671-0.825
		L168.397,326.978z"
                  />
                </g>
              </svg>
            </div>

            {/* Slide 1 - Left side view */}
            <div className="order-1 flex-shrink-0 w-full h-full snap-center part-1 flex items-center justify-center overflow-hidden">
              <svg
                ref={svgRefs[0]}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                // viewBox="-200 300 1584 1086"
                viewBox={isInModal ? "-50 300 1584 1086" : "-70 280 1584 1086"}
                className={isInModal ? "h-auto w-full" : "max-h-full w-full"}
              >
                <g id="Layer_2" display="none"></g>
                <g id="Layer_3" display="none"></g>
                <g>
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-left-running-board"
                    style={getPartStyle("left-running-board")}
                    onClick={(e) => onPartClickFunc("left-running-board", e)}
                    onMouseEnter={(e) => onPartHover("left-running-board", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M910.163,917.489c-4.942,5.125-11.219,7.59-18.407,7.383 c0,0-8.218,0.274-14.009,0.469l-73.844,2.472l-226.74,7.307c-2.102,0.074-3.357,0.269-4.205-0.697c0,0,6.221,5.727,12.2,11.265 l298.872-5.987c0.568-3.084,5.357-5.008,7.807-6.281C903.256,927.478,910.163,917.489,910.163,917.489z"
                  />
                  <g>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-rear-door"
                      style={getPartStyle("left-rear-door")}
                      onClick={(e) => onPartClickFunc("left-rear-door", e)}
                      onMouseEnter={(e) => onPartHover("left-rear-door", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M784.854,903.658l1.633,24.735l107.581-3.6 c7.155-0.238,13.771-3.741,17.828-9.438l78.376-110.017c8.16-12.084,12.192-22.779,12.188-32.033 c-0.002-4.354-1.474-10.181-2.664-12.151c-0.025-0.043-0.101-0.104-0.188-0.209c-17.395-21.22-33.119-32.854-50.279-42.396 c-2.311-1.282-6.949-3.845-23.945-7.773c-24.119-5.577-58.578-8.526-123.583-9.369l-12.507,80.791 c-4.015,13.093-7.184,27.553-8.233,41.611c-0.006,0.779-0.009,1.568-0.009,2.365c0,0.066-0.001,0.133-0.001,0.199 c0,4.026,0.053,8.229,0.148,12.596c0.419,18.918,1.719,40.883,3.652,64.675L784.854,903.658z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M921.07,794.444c0.67-0.58,1.587-1.038,2.734-1.362l0.005-0.001 l11.094-3.047c0.206-0.056,0.418-0.085,0.63-0.085c0.222,0,0.442,0.031,0.658,0.094l-0.252,0.812l0.252-0.812l1.95,0.564 l0.213,0.03l0.062-0.002l15.504-1.15c0.264-0.022,0.52-0.107,0.737-0.253l1.261-0.84c0.477-0.316,1.038-0.485,1.616-0.485h13.242 c0.532,0,1.052,0.16,1.481,0.459l2.37,1.637c0.504,0.35,0.803,0.91,0.803,1.513v2.093c0,0.669-0.317,1.299-0.864,1.711 l-1.285,0.964c-0.797,0.599-1.735,0.99-2.733,1.141l-11.433,1.724c-0.159,0.021-0.318,0.033-0.477,0.033 c-0.731,0-1.447-0.25-2.012-0.718l-0.996-0.822c-0.21-0.175-0.479-0.271-0.755-0.271l-0.065,0.002l-14.889,0.799 c-0.515,0.027-1,0.241-1.362,0.599l-1.645,1.625c-0.495,0.489-1.171,0.76-1.875,0.76c-0.107,0-0.215-0.006-0.324-0.018 l-10.774-1.307l-0.077-0.011c-1.178-0.25-2.103-0.639-2.784-1.175c-0.676-0.531-1.115-1.271-1.11-2.066 C919.97,795.76,920.406,795.019,921.07,794.444z"
                    ></path>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-rear-window"
                      style={getPartStyle("left-rear-window")}
                      onClick={(e) => onPartClickFunc("left-rear-window", e)}
                      onMouseEnter={(e) => onPartHover("left-rear-window", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M923.549,772.39c-10.534,2.46-101.449,6.837-115.956,5.958 c-0.89-0.054-0.974-0.911-0.974-0.911l4.926-67.962c0.117-1.384,1.308-2.458,2.75-2.459l0.033,0.001 c2.726-0.502,70.22-1.632,111.929,8.777c1.739,0.435,3.041,1.797,3.044,3.515l-0.007,0.194l-4.214,51.134 C925.056,771.052,923.549,772.39,923.549,772.39z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-quarter-glass"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M987.631,755.637c-8.075,8.059-58.845,16.02-58.845,16.02 c-0.259,0.029-0.517-0.055-0.702-0.229c-0.188-0.175-0.287-0.418-0.271-0.669l4.006-51.828c-0.065-1.988,3.071-0.994,3.22-0.962 c6.994,1.623,34.53,15.321,52.809,35.816C988.522,754.543,987.631,755.637,987.631,755.637z"
                      style={{
                        cursor: "pointer",
                        transition: "0.3s",
                        strokeWidth: 1,
                        fill: "rgb(255, 255, 255)",
                      }}
                    ></path>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-front-door"
                      style={getPartStyle("left-front-door")}
                      onClick={(e) => onPartClickFunc("left-front-door", e)}
                      onMouseEnter={(e) => onPartHover("left-front-door", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M781.189,823.775l0.001-0.036 c0.723-14.184,4.166-28.673,8.104-41.54c0,0-169.952,14.366-224.375,3.508l-6.731,19.885c-1.966,10.817-3.016,26.792-3.016,34.011 c0,8.254,0.616,16.183,1.915,23.751l0.002,0.019l8.904,60.03c0.33,1.448,1.111,3.121,2.277,4.946 c1.168,1.832,2.718,3.815,4.549,5.904c0.521,0.596,1.276,0.939,2.067,0.939l0.088-0.003l211.642-6.738l-1.628-24.677 c-2.086-25.646-3.435-49.175-3.739-69.09c-0.022-1.422-0.038-2.827-0.05-4.21c-0.012-1.388-0.017-2.753-0.017-4.1l0,0 C781.182,825.501,781.184,824.634,781.189,823.775z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M777.699,803.125c0,0.668-0.32,1.299-0.867,1.708l-1.284,0.967 c-0.796,0.599-1.736,0.99-2.732,1.141l-11.434,1.723c-0.159,0.023-0.318,0.035-0.476,0.035c-0.733,0-1.447-0.252-2.012-0.719 l-0.995-0.824c-0.211-0.175-0.48-0.271-0.758-0.27h-0.062h-0.002l-14.89,0.801c-0.514,0.025-1,0.239-1.361,0.597l-1.646,1.624 c-0.497,0.49-1.174,0.762-1.874,0.762c-0.108,0-0.218-0.006-0.327-0.019l-10.773-1.305l-0.078-0.015 c-1.176-0.247-2.103-0.636-2.784-1.175c-0.676-0.529-1.114-1.27-1.111-2.065c0.002-0.814,0.438-1.556,1.102-2.131 c0.67-0.578,1.585-1.035,2.734-1.359l0.006-0.001l11.093-3.047c0.205-0.058,0.417-0.085,0.628-0.085 c0.222,0,0.444,0.029,0.659,0.093l-0.252,0.812l0.253-0.812l1.949,0.564l0.213,0.029l0.061-0.002L756.183,799 c0.265-0.019,0.52-0.104,0.737-0.251l1.261-0.84c0.476-0.315,1.039-0.484,1.615-0.484h13.243c0.53,0,1.051,0.159,1.482,0.46 l2.366,1.636c0.506,0.347,0.807,0.912,0.807,1.512v2.093H777.699z"
                    ></path>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-front-window"
                      style={getPartStyle("left-front-window")}
                      onClick={(e) => onPartClickFunc("left-front-window", e)}
                      onMouseEnter={(e) => onPartHover("left-front-window", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M789.699,707.199c-7.368-1.293-95.544-0.022-164.995,43.44 c-1.4,0.877-4.104,2.447-4.327,3.896c-0.143,0.916-0.639,29.966-0.639,29.966c0.2,1.503,1.376,1.746,2.663,1.969l0,0 c34.46,1.392,149.642-6.363,149.642-6.363c0.573-0.05,1.054-0.443,1.197-0.98l18.077-69.771 C791.538,708.139,790.656,707.367,789.699,707.199z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M564.919,785.707c59.182,10.99,224.375-3.508,224.375-3.508 l12.507-80.791c-14.035-0.955-78.432-1.202-141.53,25.222c0,0-10.857,4.462-39.852,20.05 c-15.808,8.498-35.178,18.979-51.458,28.008c-12.77,7.083-11.379,6.333-13.479,7.513l-0.311,0.212 c-0.988,0.7-1.596,1.888-0.312,2.083C555.888,784.649,564.919,785.707,564.919,785.707z"
                    ></path>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M565.729,781.321c7.456-4.782,39.037-21.364,49.68-25.972 c1.388-0.601,2.479,0.911,2.581,2.411l-0.396,25.629c0.008,1.582-0.423,2.88-1.909,2.918c-29.416,0.732-49.134-2.979-49.704-3.231 C565.409,782.821,565.206,781.658,565.729,781.321z"
                    ></path>
                    <g>
                      <path
                        fill="none"
                        class="part-svg"
                        id="svg-left-quarter-panel"
                        style={getPartStyle("left-quarter-panel")}
                        onClick={(e) =>
                          onPartClickFunc("left-quarter-panel", e)
                        }
                        onMouseEnter={(e) =>
                          onPartHover("left-quarter-panel", e)
                        }
                        onMouseLeave={onPartLeave}
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1122.825,794.256c-1.875-0.181-3.731-0.606-5.488-1.276 c-1.457-0.559-2.74-1.092-3.816-1.598c-1.079-0.508-1.946-0.984-2.604-1.463c-0.436-0.324-0.785-0.638-1.032-1.045 c-0.138-0.232-0.242-0.512-0.242-0.825c-0.005-0.341,0.139-0.683,0.348-0.927c0.208-0.246,0.462-0.42,0.745-0.562 c0.562-0.285,1.274-0.473,2.213-0.628l0.024-0.004c30.266-4.195,69.402-5.448,69.402-5.448l8.656,0.146 c2.018-0.031,1.172-0.183,2.879,0.466l3.382,1.249c1.188,0.116,1.161-0.88,1.161-0.88l-1.593-15.786 c-0.207-1.265,0.447-2.412,0.947-2.945c0,0,3.471-4.122,3.621-4.426c0.158-0.309,0.204-0.53,0.201-0.638 c-0.006-0.108-0.207-0.271-0.443-0.344l-40.019-0.722l-161.373,4.555c1.724,3.845,2.664,7.567,2.664,12.151 c0,9.741-4.508,21.221-13.138,33.366l-78.438,109.854c-13.538,18.508-25.908,17.476-26.854,23.17l56.755,0.045l-0.922-11.607 c-0.601-3.762-0.914-7.613-0.914-11.538c0-41.547,34.875-75.228,77.897-75.229c19.041,0,36.484,6.602,50.016,17.559 l83.585-59.938C1141.979,796.834,1132.82,795.217,1122.825,794.256z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1198.998,863.77l17.425-22.181 c-0.263-0.686-11.518-19.516-11.518-19.516c-0.878-1.521-0.235-0.104-0.94-8.682c-0.138-1.666-1.827-1.554-1.827-1.554 l-1.104,0.044l-7.794,1.422c-1.563,0.334-2.619-0.294-2.913-0.423c-14.528-6.402-29.15-10.66-29.15-10.66 s-0.009-0.004-0.013-0.006c-1.186-0.41-2.397-0.802-3.614-1.188c-0.559-0.177-1.115-0.354-1.68-0.523 c-0.409-0.127-0.83-0.244-1.242-0.366c-1.35-0.396-2.716-0.78-4.099-1.148l-83.587,59.938 c10.711,9.075,18.763,21.013,23.054,34.491l94.161-9.062l10.407-14.943L1198.998,863.77z"
                      ></path>
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1092.764,908.748c0,0.009,0,0.02,0.002,0.029 c0.062,0.804,0.104,1.612,0.14,2.423c0.008,0.196,0.017,0.393,0.021,0.589c0.028,0.824,0.048,1.654,0.048,2.485l3.361,8.572 l14.346-1.332l49.374-6.481c0,0,27.971-4.113,41.114-9.438c3.68-1.49,7.369-5.866,7.369-5.866l-48.483,5.866l6.602-6.994 l12.272-6.829l5.146-7.414l-94.161,9.062C1091.32,898.219,1092.373,903.607,1092.764,908.748z"
                      ></path>
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1216.343,841.59l-21.857,27.823l-15.556,22.357l-12.272,6.829 l-6.602,6.994l48.483-5.866c4.126-1.479,5.252-6.153,5.252-7.695c0,0,4.211-46.007,4.211-46.52 c0.001-1.043-0.849-2.359-0.97-2.658L1216.343,841.59z"
                      ></path>
                    </g>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M347.601,932.028l-0.139,0.004c-1.706,0-2.792-1.418-3.622-2.843 c-10.605-18.2-20.518-32.984-27.991-41.977c-2.767-3.33-5.063-6.357-7.801-9.194c-5.729-6.083-10.986-11.466-14.897-14.665 c-2.45-2.005-5.358-2.643-5.416-2.316v10.924v44.228c0,0,0,3.197-4.139,6.393c-2.702,2.09,19.036,7.729,19.036,7.729 s34.692,7.658,36.142,7.726c2.175,0.101,36.599,0.18,38.807,0.001c2.207-0.177,6.438-1.598,6.15-7.062L347.601,932.028z"
                    ></path>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M348.937,847.815c0,0-2.184-0.278-2.185-0.277l-8.43-1.165 l-4.066-0.563c-5.57-0.771-10.796-1.821-10.796-1.821c-10.621,2.664-19.572,6.519-19.588,6.528c0,0-16.308,7.246-16.139,10.521 c0,0,0.117-0.142,2.438,0.496c3.287,0.904,17.876,16.485,17.876,16.485c3.75,4.245,10.1,11.995,10.1,11.995 c4.348,5.394,11.355,15.985,11.356,15.987c4.117,5.791,12.83,20.729,14.336,23.188c1.328,2.166,2.208,2.837,3.88,2.837l0.846-0.02 l35.163-1.037c-0.138-0.878-0.268-1.747-0.39-2.604c-0.002-0.017-0.004-0.033-0.006-0.049c-0.059-0.414-0.116-0.826-0.171-1.23 c-0.014-0.109-0.027-0.215-0.042-0.323s-0.028-0.22-0.043-0.328c-0.059-0.452-0.114-0.897-0.166-1.332 c-0.006-0.061-0.015-0.12-0.021-0.182c-0.055-0.467-0.103-0.918-0.148-1.361c-0.011-0.111-0.021-0.22-0.032-0.328 c-0.087-0.897-0.151-1.733-0.188-2.493c-0.004-0.081-0.008-0.163-0.011-0.243c-0.016-0.373-0.026-0.73-0.026-1.062 c0-0.724,0.014-1.444,0.035-2.163c0.006-0.229,0.018-0.455,0.026-0.684c0.02-0.496,0.042-0.992,0.071-1.486 c0.015-0.256,0.032-0.512,0.05-0.768c0.033-0.478,0.071-0.954,0.115-1.43c0.021-0.242,0.042-0.483,0.065-0.725 c0.055-0.541,0.115-1.082,0.183-1.622c0.02-0.161,0.036-0.326,0.057-0.488c0.094-0.719,0.196-1.436,0.31-2.146 c0.007-0.04,0.015-0.08,0.021-0.116c0.108-0.666,0.226-1.328,0.352-1.987c0.03-0.162,0.063-0.324,0.097-0.485 c0.108-0.55,0.222-1.099,0.344-1.645c0.037-0.166,0.073-0.331,0.111-0.497c0.132-0.576,0.271-1.149,0.416-1.718 c0.029-0.113,0.056-0.227,0.085-0.34c0.556-2.141,1.209-4.243,1.951-6.308c0.005-0.013,0.009-0.024,0.014-0.038 c5.126-14.223,14.375-26.711,26.614-35.571c-3.97-1.482-9.311-3.466-13.531-5.045C384.276,851.548,366.55,850.049,348.937,847.815 z"
                    ></path>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-fender"
                      style={getPartStyle("left-fender")}
                      onClick={(e) => onPartClickFunc("left-fender", e)}
                      onMouseEnter={(e) => onPartHover("left-fender", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M575.845,937.058c0,0-2.108-1.865-2.886-2.638 c-0.221-0.221-0.362-0.42-0.575-0.667c-3.805-4.396-5.256-7.385-5.256-7.385c-0.825-1.54-1.136-2.968-1.136-2.968l-8.669-58.45 c-1.315-7.666-2.151-15.967-2.151-24.294c0-10.898,1.117-23.552,3.077-35.439l6.671-19.511l-22.64-2.729l-12.875-0.777h-20.231 c-3.138,0.052-62.22,3.395-81.844,7.438c-4.085,0.843-39.522,7.089-43.589,7.993c-43.728,9.724-80.488,29.038-78.35,30.372 c0,0,70.775-9.584,87.876-12.07c0.408-0.062,0.804,0.161,0.949,0.535c0.149,0.373,0.007,0.792-0.338,1.012l-7.724,9.459 c0,0-4.4,3.701-5.794,4.262c-0.033,0.019-23.485,5.571-23.521,5.586l-13.881,2.513c0,0-11.819,2.026-19.499,4.688 c6.648,1.329,18.939,3.084,32.188,4.646c4.602,0.543,9.367,1.071,13.98,1.585c12.029,1.336,23.182,2.68,30.236,3.955l0.149,0.043 c4.621,1.729,9.058,3.387,13.382,5.002c12.659-8.661,28.121-13.754,44.816-13.754c42.86,0.001,77.604,33.554,77.605,74.941 c0,1.142,0.061,2.83,0.121,4.225c0.061,1.396,0.123,2.495,0.123,2.497v0.006l0,0c0.05,1.017,0.072,2.05,0.072,3.1 c0,5.044-0.558,10.468-1.502,16.164l50.524-0.711L575.845,937.058z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M980.534,740.683c-9.412-8.56-22.104-17.384-33.383-23.271 c-0.068-0.034-0.141-0.062-0.211-0.093c-8.912-3.9-21.559-6.542-21.559-6.542c-10.396-2.268-21.896-4.125-33.617-5.297 c-40.73-4.072-83.859-4.072-89.933-4.072l-12.54,80.891c0,0,63.822-1.358,111.425-4.646c37.845-2.615,99.728-16.128,92.26-24.443 C985.813,745.23,980.534,740.683,980.534,740.683z"
                    ></path>
                    <g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1085.974,912.117c-0.012-0.249-0.04-0.496-0.057-0.744 c-0.054-0.883-0.116-1.761-0.208-2.632c-0.04-0.397-0.1-0.792-0.146-1.188c-0.087-0.693-0.169-1.39-0.275-2.077 c-0.09-0.591-0.202-1.176-0.311-1.763c-0.084-0.462-0.163-0.928-0.258-1.389c-0.16-0.789-0.346-1.573-0.536-2.354 c-0.055-0.229-0.104-0.458-0.164-0.687c-6.309-24.606-26.649-44.078-52.183-49.975c-0.202-0.047-0.408-0.093-0.613-0.139 c-0.807-0.179-1.618-0.342-2.434-0.493c-0.407-0.073-0.814-0.146-1.227-0.213c-0.653-0.108-1.31-0.213-1.971-0.306 c-0.564-0.076-1.135-0.145-1.705-0.209c-0.543-0.062-1.087-0.126-1.632-0.174c-0.659-0.062-1.321-0.105-1.984-0.147 c-0.494-0.03-0.99-0.066-1.486-0.089c-1.078-0.049-2.156-0.08-3.245-0.08c-19.478,0-37.104,7.621-49.867,19.947 c-8.776,8.478-15.249,19.17-18.438,31.138c-0.579,2.175-1.052,4.396-1.406,6.648c-0.533,3.381-0.813,6.846-0.813,10.372 c0,1.031,0.031,2.058,0.079,3.078c0.013,0.255,0.025,0.509,0.043,0.761c0.051,0.958,0.123,1.913,0.221,2.857 c0.006,0.061,0.01,0.117,0.017,0.178c0.107,1.037,0.244,2.068,0.399,3.092c0.027,0.172,0.056,0.342,0.082,0.51 c0.158,0.989,0.333,1.977,0.535,2.95c0.005,0.023,0.012,0.049,0.016,0.072c0.221,1.062,0.471,2.114,0.744,3.158 c0.015,0.056,0.027,0.107,0.044,0.161c0.833,3.163,1.894,6.24,3.168,9.21c5.995,13.979,16.666,25.607,30.138,33.069 c6.689,3.707,14.326,5.543,22.059,5.543h32.392c1.989,0,3.968-0.354,5.827-1.044c24.508-9.075,42.432-30.969,44.966-57.187 c0.09-0.917,0.158-1.839,0.211-2.765c0.01-0.16,0.021-0.32,0.03-0.479c0.051-1.037,0.08-2.079,0.082-3.125 c0-0.013,0-0.027,0-0.041c0-0.06-0.006-0.117-0.006-0.175C1086.057,914.295,1086.031,913.203,1085.974,912.117z"
                        ></path>
                        <path
                          fill="none"
                          class="part-svg"
                          id="svg-left-rear-rim"
                          style={getPartStyle("left-rear-rim")}
                          onClick={(e) => onPartClickFunc("left-rear-rim", e)}
                          onMouseEnter={(e) => onPartHover("left-rear-rim", e)}
                          onMouseLeave={onPartLeave}
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1070.668,918.945l-0.002,0.014c0,0.021-0.003,0.042-0.005,0.063 c-0.068,1.017-0.168,2.023-0.291,3.021c-0.036,0.269-0.071,0.533-0.106,0.8c-0.062,0.439-0.126,0.88-0.201,1.315 c-0.097,0.596-0.206,1.188-0.323,1.776l-0.002,0.007c-0.04,0.193-0.078,0.391-0.12,0.585l-0.032,0.161h-0.007 c-4.424,20.143-20.612,36.072-41.298,40.79v0.008l-0.762,0.164l-0.007,0.001c-0.562,0.12-1.126,0.229-1.69,0.334 c-0.055,0.009-0.104,0.018-0.154,0.025c-1.025,0.183-2.061,0.338-3.102,0.467c-0.148,0.02-0.3,0.037-0.448,0.054 c-0.539,0.062-1.082,0.12-1.628,0.165l-0.01,0.001l-0.798,0.067l-0.001-0.01c-1.367,0.1-2.747,0.165-4.141,0.165 c-19.633,0-36.863-9.896-46.663-24.801l-0.015,0.005l-0.418-0.661l0,0c-0.624-0.978-1.21-1.978-1.768-2.996 c-0.01-0.019-0.019-0.037-0.026-0.054c-0.554-1.009-1.068-2.041-1.556-3.087c0-0.004,0-0.006,0-0.006l-0.019-0.04 c-3.067-6.636-4.786-13.979-4.786-21.715c0-13.272,5.031-25.402,13.336-34.739l-0.006-0.006l0.519-0.568l0.005-0.004 c1.449-1.584,2.992-3.083,4.623-4.489l0.002-0.005l0.59-0.506l0.01,0.006c9.695-8.117,22.333-13.042,36.172-13.044 c7.922,0,15.447,1.623,22.259,4.524l0.017,0.006c0,0.001,0.003,0.002,0.003,0.002c0.887,0.379,1.756,0.775,2.611,1.194 c0.016,0.009,0.031,0.019,0.047,0.023c0.855,0.418,1.694,0.856,2.521,1.315l0.006,0.002l0.692,0.386l0,0 c16.216,9.297,27.093,26.369,27.093,45.901C1070.789,916.704,1070.74,917.827,1070.668,918.945z"
                          style={{
                            cursor: "pointer",
                            transition: "0.3s",
                            strokeWidth: 1,
                            fill: "rgb(255, 255, 255)",
                          }}
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1044.519,888.808c-1.479,0.001-2.91,0.711-3.777,1.96 c-3.267,4.711-6.052,9.379-8.217,14.959c-0.092,0.236-0.137,0.484-0.137,0.729c0,0.503,0.188,0.975,0.506,1.35 c0.316,0.371,0.78,0.648,1.32,0.735c5.064,0.817,9.89,1.284,14.622,1.284c4.686,0,9.282-0.459,13.909-1.489 c0.301-0.066,0.545-0.268,0.666-0.542c0.115-0.273,0.09-0.585-0.073-0.836c-3.222-4.986-6.066-8.732-8.789-11.577 c-2.72-2.841-5.328-4.778-8.037-6.11C1045.876,888.958,1045.193,888.808,1044.519,888.808z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1053.158,896.626c2.397,2.505,4.957,5.821,7.834,10.18 c-4.034,0.803-8.047,1.188-12.155,1.188c-4.604,0-9.323-0.455-14.309-1.259l-0.173-0.1l-0.07-0.18l0.017-0.088 c2.1-5.408,4.795-9.932,8.019-14.584c0.502-0.729,1.35-1.145,2.199-1.144c0.387,0.001,0.771,0.084,1.132,0.263 C1048.133,892.124,1050.549,893.905,1053.158,896.626z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1043.698,869.666L1043.698,869.666l-0.692-0.386l-0.006-0.002 c-0.827-0.459-1.666-0.897-2.521-1.315c-0.016-0.006-0.031-0.016-0.047-0.022c-0.855-0.419-1.726-0.816-2.608-1.194 c0,0-0.005-0.002-0.007-0.003l-0.016-0.006l-0.714-0.305l-0.012,0.016c-6.621-2.711-13.889-4.235-21.533-4.235 c-13.839,0.002-26.478,4.928-36.172,13.044l-0.011-0.006l-0.589,0.506l-0.002,0.006c-1.631,1.405-3.174,2.904-4.623,4.488 l-0.005,0.004l-0.519,0.568l0.006,0.006c-8.305,9.337-13.336,21.467-13.336,34.739c0,7.476,1.618,14.579,4.493,21.039 l-0.018,0.011l0.311,0.665l0.016,0.039c0,0,0.003,0.003,0.003,0.007c0.486,1.046,1.002,2.078,1.555,3.087 c0.01,0.018,0.019,0.035,0.027,0.055c0.557,1.019,1.144,2.019,1.767,2.995l0,0l0.419,0.661l0.015-0.005 c9.8,14.903,27.03,24.801,46.663,24.801c1.394,0,2.771-0.065,4.141-0.164l0.001,0.01l0.798-0.068l0.01-0.001 c0.546-0.045,1.089-0.102,1.628-0.165c0.148-0.016,0.3-0.034,0.447-0.054c1.042-0.128,2.076-0.284,3.103-0.466 c0.052-0.01,0.101-0.019,0.153-0.026c0.565-0.104,1.13-0.214,1.691-0.334l0.007-0.001l0.761-0.164v-0.008 c20.687-4.717,36.875-20.647,41.299-40.79h0.007l0.032-0.161l0.119-0.585l0.002-0.007c0.118-0.588,0.229-1.182,0.324-1.776 c0.075-0.437,0.141-0.876,0.201-1.314c0.038-0.268,0.07-0.532,0.106-0.8c0.123-1,0.223-2.007,0.291-3.022 c0.002-0.021,0.005-0.042,0.005-0.063l0.002-0.014l0.049-0.758l-0.009-0.003c0.045-0.866,0.081-1.737,0.081-2.617 C1070.789,896.035,1059.912,878.963,1043.698,869.666z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1015.54,864.046c7.255,0.001,14.167,1.405,20.474,3.937 l-21.489,31.11c-0.396,0.526-1.012,0.807-1.642,0.809c-0.386-0.002-0.771-0.107-1.116-0.329l-30.869-23.17 C990.223,868.704,1002.309,864.047,1015.54,864.046z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M966.365,935.583c-2.688-6.152-4.177-12.914-4.177-20.016 c0-12.684,4.755-24.283,12.62-33.26l23.877,29.82c0.273,0.354,0.404,0.762,0.404,1.168c-0.002,0.583-0.266,1.15-0.771,1.537 L966.365,935.583z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1015.54,967.09c-14.738-0.001-28.068-5.766-37.725-15.091 c-2.69-2.597-5.079-5.479-7.149-8.579l36-14.077c0.215-0.066,0.429-0.103,0.638-0.103c0.855,0.001,1.658,0.533,1.934,1.365 l9.953,36.352C1017.982,967.035,1016.769,967.09,1015.54,967.09z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1053.265,951.999c-6.791,6.559-15.399,11.343-25.033,13.612 l-1.003-37.744c0.029-1.061,0.934-1.905,2.026-1.905l0.056,0.002l0.012,0.001h0.002l38.314,0.691 C1065.419,936.432,1060.337,945.169,1053.265,951.999z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1068.484,921.848c-0.031,0.244-0.064,0.489-0.097,0.733 c-0.062,0.433-0.123,0.864-0.196,1.292c-0.053,0.321-0.113,0.639-0.175,0.955h-0.002l-38.653-0.696l0,0l-0.104-0.002 c-2.128,0-3.874,1.639-3.925,3.703v0.026l1.014,38.152c-0.295,0.059-0.593,0.12-0.893,0.177 c-0.023,0.004-0.049,0.008-0.074,0.012c-1.018,0.185-2.049,0.337-3.09,0.465c-0.126,0.017-0.249,0.031-0.377,0.046 c-0.267,0.03-0.536,0.055-0.806,0.081l-10.055-36.721l-0.004-0.016c-0.532-1.618-2.076-2.648-3.743-2.648 c-0.417,0-0.843,0.067-1.259,0.202l-0.027,0.01l-36.354,14.217c-0.151-0.249-0.301-0.496-0.449-0.742 c-0.291-0.494-0.589-0.985-0.865-1.49c0,0-0.008-0.012-0.01-0.019c-0.414-0.761-0.79-1.541-1.165-2.321l32.272-20.956 l0.026-0.021c0.989-0.745,1.514-1.865,1.512-2.991c0-0.784-0.255-1.586-0.778-2.264l-0.008-0.009l-24.104-30.107 c1.071-1.139,2.194-2.229,3.365-3.271l31.211,23.425l0.023,0.016c0.67,0.437,1.437,0.646,2.188,0.646 c1.216,0.001,2.418-0.547,3.183-1.567l0.012-0.015l21.688-31.402c0.611,0.271,1.224,0.538,1.822,0.832 c0.004,0.002,0.008,0.004,0.008,0.004c0.602,0.294,1.185,0.608,1.765,0.922l-13.449,35.62l-0.01,0.028 c-0.12,0.372-0.178,0.751-0.178,1.123c0,1.548,0.99,2.99,2.568,3.555l0.053,0.02l38.365,8.761 C1068.652,920.362,1068.576,921.107,1068.484,921.848z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1068.834,917.757l-37.909-8.656 c-0.803-0.295-1.303-1.033-1.305-1.822c0.002-0.191,0.03-0.383,0.089-0.572l13.329-35.293c3.729,2.172,7.164,4.768,10.227,7.725 c9.657,9.326,15.627,22.201,15.629,36.432C1068.894,916.302,1068.865,917.03,1068.834,917.757z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1014.472,896.717c4.782-8.713,7.913-17.02,8.819-26.204 c0.031-0.297-0.087-0.584-0.321-0.777c-0.231-0.195-0.546-0.271-0.843-0.2c-5.922,1.377-10.499,2.798-14.151,4.391 c-3.651,1.596-6.384,3.372-8.545,5.435c-0.877,0.833-1.316,1.947-1.316,3.054c0,0.951,0.321,1.908,0.962,2.704 c3.597,4.484,7.302,8.512,12.099,12.261c0.398,0.312,0.887,0.463,1.358,0.46 C1013.304,897.838,1014.072,897.442,1014.472,896.717z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1000.576,883.994c-0.377-0.468-0.562-1.024-0.562-1.582 c0-0.647,0.25-1.274,0.748-1.748c1.982-1.889,4.498-3.54,7.994-5.067c3.222-1.404,7.288-2.702,12.479-3.959 c-1.035,8.376-3.961,16.058-8.439,24.222c-0.047,0.089-0.153,0.146-0.264,0.146l-0.166-0.055 C1007.716,892.317,1004.123,888.419,1000.576,883.994z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M974.481,894.025c-0.273-0.135-0.597-0.13-0.865,0.009 c-0.273,0.139-0.457,0.394-0.497,0.688c-0.629,4.543-0.938,8.361-0.938,11.657c-0.003,5.153,0.758,9.041,2.235,12.329 c0.729,1.624,2.39,2.582,4.124,2.581c0.438,0,0.878-0.061,1.311-0.185c5.628-1.623,10.854-3.508,16.202-6.477 c0.714-0.396,1.089-1.118,1.087-1.828c0.002-0.48-0.173-0.979-0.531-1.382C989.918,903.971,983.025,898.154,974.481,894.025z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M995.112,913.042c-5.188,2.877-10.248,4.706-15.803,6.31 c-0.255,0.071-0.513,0.106-0.769,0.106c-1.021-0.001-1.975-0.562-2.382-1.479c-1.354-3.01-2.08-6.611-2.08-11.6 c0-2.865,0.244-6.191,0.745-10.097c7.714,3.951,14.081,9.36,20.351,16.337l0.069,0.182 C995.242,912.911,995.191,912.998,995.112,913.042z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1028.032,929.062c0,0.082,0.007,0.168,0.018,0.257h-0.002 c1.251,9.787,3.616,18.33,8.322,26.356c0.15,0.26,0.422,0.429,0.728,0.459c0.305,0.031,0.605-0.081,0.81-0.304 c4.036-4.402,6.944-8.103,8.991-11.431c2.048-3.328,3.229-6.292,3.779-9.182c0.05-0.258,0.071-0.516,0.071-0.769 c-0.004-1.891-1.299-3.575-3.196-4.147c-5.609-1.684-11.036-2.934-17.189-3.35l-0.152-0.006c-0.607,0-1.157,0.248-1.544,0.63 C1028.277,927.958,1028.032,928.484,1028.032,929.062z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1030.017,928.862l0.192-0.081l0.019,0.001 c5.968,0.403,11.225,1.611,16.76,3.271c1.113,0.331,1.868,1.333,1.865,2.396c-0.002,0.146-0.014,0.291-0.041,0.438 c-0.506,2.646-1.588,5.389-3.547,8.575c-1.804,2.937-4.368,6.246-7.875,10.151c-4.121-7.435-6.284-15.354-7.458-24.521 l-0.002-0.012l-0.003-0.021L1030.017,928.862z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1007.095,931.521c-0.374-0.259-0.82-0.401-1.29-0.401 c-0.332,0-0.675,0.074-0.991,0.232c-9.087,4.497-16.58,9.571-22.77,16.604c-0.198,0.225-0.273,0.527-0.196,0.815 c0.073,0.288,0.286,0.521,0.572,0.631c8.888,3.362,15.153,4.819,20.36,4.823c1.431,0,2.777-0.111,4.071-0.323 c2.186-0.364,3.71-2.219,3.71-4.308l-0.004-0.209v-0.011c-0.29-5.664-0.938-11.015-2.646-16.74 C1007.771,932.166,1007.469,931.78,1007.095,931.521z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1008.667,949.594c0,1.227-0.904,2.305-2.133,2.5 c-1.188,0.195-2.428,0.297-3.754,0.299c-4.588,0.001-10.293-1.242-18.385-4.221c5.812-6.293,12.771-10.976,21.284-15.19 l0.124-0.029l0.184,0.058l0.101,0.133c1.654,5.549,2.288,10.729,2.575,16.316L1008.667,949.594z"
                        ></path>
                      </g>
                    </g>
                    <g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M528.234,913.469c-0.013-0.241-0.04-0.481-0.056-0.724 c-0.053-0.86-0.114-1.716-0.205-2.567c-0.04-0.386-0.1-0.771-0.146-1.157c-0.084-0.679-0.166-1.355-0.271-2.026 c-0.091-0.575-0.2-1.145-0.308-1.716c-0.083-0.452-0.161-0.907-0.256-1.357c-0.158-0.768-0.34-1.532-0.529-2.293 c-0.053-0.224-0.103-0.444-0.161-0.67c-6.229-23.99-26.314-42.976-51.524-48.726c-0.2-0.045-0.404-0.09-0.605-0.136 c-0.798-0.174-1.598-0.334-2.402-0.479c-0.402-0.073-0.806-0.143-1.21-0.207c-0.646-0.106-1.293-0.207-1.946-0.298 c-0.559-0.075-1.121-0.143-1.685-0.204c-0.535-0.061-1.072-0.124-1.61-0.17c-0.651-0.061-1.306-0.102-1.96-0.146 c-0.489-0.029-0.979-0.063-1.469-0.086c-1.063-0.048-2.129-0.077-3.203-0.077c-19.232,0-36.637,7.431-49.239,19.448 c-8.666,8.266-15.057,18.691-18.206,30.359c-0.573,2.123-1.037,4.284-1.39,6.482c-0.526,3.298-0.804,6.677-0.804,10.113 c0,1.008,0.032,2.006,0.08,3.001c0.012,0.247,0.025,0.495,0.042,0.742c0.053,0.935,0.123,1.864,0.219,2.787 c0.006,0.058,0.01,0.114,0.016,0.173c0.105,1.012,0.242,2.017,0.396,3.015c0.025,0.166,0.053,0.332,0.081,0.496 c0.155,0.965,0.329,1.927,0.529,2.877c0.004,0.023,0.009,0.048,0.013,0.071c0.219,1.034,0.466,2.062,0.734,3.081 c0.013,0.049,0.028,0.104,0.043,0.155c0.823,3.084,1.869,6.082,3.129,8.979c5.919,13.629,16.455,24.968,29.757,32.245 c6.604,3.613,14.146,5.402,21.781,5.402h31.983c1.963,0,3.917-0.348,5.753-1.02c24.198-8.847,41.896-30.194,44.398-55.758 c0.088-0.896,0.157-1.792,0.208-2.694c0.009-0.156,0.021-0.313,0.029-0.47c0.051-1.012,0.079-2.024,0.081-3.046 c0-0.012,0-0.026,0-0.04c0-0.059-0.005-0.115-0.005-0.171C528.316,915.594,528.287,914.528,528.234,913.469z"
                        ></path>
                        <path
                          fill="none"
                          class="part-svg"
                          id="svg-left-front-rim"
                          style={getPartStyle("left-front-rim")}
                          onClick={(e) => onPartClickFunc("left-front-rim", e)}
                          onMouseEnter={(e) => onPartHover("left-front-rim", e)}
                          onMouseLeave={onPartLeave}
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M513.119,920.129l-0.001,0.015c0,0.021-0.002,0.041-0.005,0.062 c-0.066,0.991-0.166,1.974-0.287,2.947c-0.035,0.26-0.069,0.521-0.104,0.778c-0.062,0.43-0.125,0.857-0.198,1.283 c-0.098,0.58-0.205,1.158-0.321,1.73l-0.003,0.009c-0.038,0.188-0.074,0.38-0.117,0.57l-0.032,0.155h-0.006 c-4.368,19.641-20.354,35.174-40.776,39.771v0.009l-0.753,0.157l-0.008,0.002c-0.554,0.118-1.11,0.226-1.669,0.326 c-0.051,0.009-0.101,0.017-0.151,0.025c-1.013,0.178-2.033,0.329-3.062,0.452c-0.146,0.021-0.293,0.038-0.442,0.056 c-0.533,0.061-1.068,0.115-1.607,0.159l-0.008,0.003l-0.788,0.064l-0.002-0.009c-1.351,0.097-2.712,0.159-4.087,0.159 c-19.387,0-36.399-9.648-46.076-24.183l-0.015,0.007l-0.414-0.645l0,0c-0.615-0.955-1.192-1.931-1.742-2.922 c-0.011-0.021-0.02-0.037-0.028-0.053c-0.546-0.985-1.054-1.99-1.534-3.011c0-0.005,0-0.007,0-0.007l-0.02-0.038 c-3.029-6.47-4.726-13.63-4.726-21.173c0-12.94,4.967-24.768,13.167-33.871l-0.005-0.006l0.513-0.556l0.006-0.005 c1.431-1.542,2.955-3.004,4.563-4.375l0.002-0.004l0.583-0.492l0.01,0.004c9.572-7.916,22.052-12.716,35.716-12.717 c7.821,0,15.252,1.582,21.978,4.408l0.016,0.009c0.003,0.001,0.005,0.001,0.005,0.001c0.875,0.366,1.732,0.755,2.577,1.162 c0.017,0.01,0.032,0.02,0.049,0.023c0.845,0.408,1.672,0.836,2.487,1.285l0.008,0.002l0.684,0.374l0,0 c16.011,9.063,26.75,25.711,26.75,44.756C513.24,917.943,513.192,919.039,513.119,920.129z"
                          // style={{
                          //   cursor: "pointer",
                          //   transition: "0.3s",
                          //   strokeWidth: 1,
                          //   fill: "rgb(255, 255, 255)",
                          // }}
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M487.302,890.744c-1.463,0.001-2.875,0.693-3.731,1.909 c-3.226,4.594-5.973,9.145-8.111,14.586c-0.092,0.232-0.135,0.474-0.135,0.711c0,0.492,0.186,0.952,0.499,1.315 c0.312,0.363,0.77,0.635,1.304,0.719c4.999,0.796,9.767,1.253,14.438,1.253c4.626,0,9.164-0.448,13.733-1.452 c0.296-0.067,0.539-0.264,0.656-0.53c0.115-0.267,0.089-0.568-0.071-0.812c-3.182-4.864-5.99-8.518-8.679-11.288 c-2.687-2.771-5.26-4.66-7.936-5.961C488.641,890.889,487.964,890.744,487.302,890.744z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M495.829,898.367c2.369,2.441,4.896,5.675,7.736,9.925 c-3.984,0.78-7.945,1.159-12.001,1.159c-4.546,0-9.207-0.445-14.128-1.229l-0.169-0.096l-0.072-0.177l0.018-0.085 c2.072-5.273,4.734-9.685,7.916-14.22c0.497-0.71,1.334-1.116,2.174-1.114c0.38,0.001,0.76,0.081,1.116,0.256 C490.869,893.977,493.255,895.715,495.829,898.367z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M486.49,872.079L486.49,872.079l-0.683-0.374l-0.009-0.002 c-0.815-0.449-1.644-0.877-2.487-1.285c-0.018-0.005-0.033-0.015-0.049-0.023c-0.845-0.407-1.701-0.795-2.574-1.162 c0,0-0.004,0-0.007-0.001l-0.016-0.008l-0.705-0.297l-0.012,0.016c-6.538-2.643-13.712-4.128-21.261-4.128 c-13.665,0.001-26.144,4.801-35.715,12.717l-0.011-0.004l-0.583,0.492l-0.002,0.004c-1.608,1.371-3.133,2.833-4.563,4.375 l-0.005,0.005l-0.514,0.556l0.005,0.006c-8.199,9.104-13.167,20.931-13.167,33.871c0,7.289,1.598,14.217,4.437,20.515 l-0.018,0.012l0.307,0.646l0.016,0.038c0,0,0.003,0.002,0.003,0.007c0.48,1.021,0.988,2.024,1.534,3.011 c0.009,0.016,0.018,0.033,0.028,0.053c0.549,0.991,1.127,1.967,1.743,2.922l0,0l0.413,0.646l0.015-0.008 c9.677,14.533,26.688,24.183,46.076,24.183c1.375,0,2.737-0.062,4.087-0.159l0.002,0.009l0.788-0.064l0.008-0.002 c0.539-0.044,1.074-0.101,1.607-0.16c0.148-0.018,0.295-0.035,0.442-0.056c1.029-0.123,2.05-0.274,3.062-0.451 c0.051-0.011,0.101-0.019,0.151-0.026c0.56-0.101,1.116-0.208,1.669-0.326l0.008-0.002l0.753-0.157v-0.009 c20.423-4.599,36.408-20.132,40.776-39.771h0.006l0.032-0.155l0.117-0.57l0.003-0.009c0.117-0.572,0.225-1.15,0.321-1.729 c0.074-0.427,0.136-0.854,0.199-1.284c0.036-0.257,0.068-0.52,0.104-0.778c0.121-0.976,0.22-1.956,0.287-2.947 c0.003-0.021,0.005-0.041,0.005-0.062l0.001-0.015l0.05-0.738l-0.01-0.002c0.046-0.847,0.081-1.695,0.081-2.554 C513.24,897.79,502.5,881.144,486.49,872.079z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M458.688,866.6c7.165,0,13.988,1.368,20.215,3.839l-21.219,30.334 c-0.391,0.512-0.998,0.784-1.619,0.786c-0.383-0.001-0.765-0.104-1.104-0.32l-30.479-22.591 C433.689,871.141,445.622,866.6,458.688,866.6z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M410.13,936.349c-2.654-6-4.123-12.591-4.123-19.515 c0-12.365,4.694-23.678,12.463-32.43l23.575,29.076c0.271,0.346,0.398,0.743,0.398,1.139c-0.002,0.569-0.261,1.122-0.76,1.5 L410.13,936.349z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M458.688,967.071c-14.552-0.001-27.716-5.622-37.25-14.714 c-2.655-2.531-5.016-5.342-7.059-8.364l35.545-13.727c0.211-0.066,0.425-0.1,0.63-0.1c0.846,0,1.637,0.518,1.908,1.329 l9.828,35.443C461.099,967.02,459.9,967.071,458.688,967.071z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M495.939,952.357c-6.706,6.394-15.208,11.059-24.721,13.271 l-0.988-36.801c0.027-1.035,0.921-1.858,2-1.858l0.056,0.003h0.01h0.002l37.833,0.675 C507.938,937.177,502.92,945.697,495.939,952.357z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M510.962,922.958c-0.031,0.24-0.061,0.479-0.094,0.717 c-0.06,0.42-0.123,0.842-0.193,1.261c-0.053,0.312-0.114,0.62-0.172,0.93h-0.002l-38.168-0.677l0,0l-0.103-0.004 c-2.102,0-3.825,1.598-3.875,3.609v0.028l1,37.199c-0.291,0.056-0.583,0.117-0.881,0.172c-0.022,0.004-0.048,0.008-0.074,0.01 c-1.005,0.181-2.023,0.332-3.05,0.455c-0.125,0.015-0.246,0.03-0.374,0.043c-0.262,0.031-0.528,0.055-0.793,0.08l-9.929-35.805 l-0.004-0.015c-0.524-1.578-2.05-2.584-3.695-2.583c-0.412,0-0.831,0.064-1.243,0.198l-0.025,0.009l-35.896,13.862 c-0.15-0.243-0.298-0.484-0.444-0.725c-0.287-0.481-0.582-0.963-0.854-1.454c0,0-0.008-0.011-0.01-0.018 c-0.409-0.741-0.781-1.503-1.151-2.265l31.866-20.432l0.029-0.021c0.975-0.729,1.492-1.818,1.491-2.917 c0-0.766-0.252-1.545-0.77-2.207l-0.008-0.008l-23.801-29.355c1.059-1.109,2.168-2.174,3.324-3.188l30.815,22.838l0.025,0.019 c0.66,0.424,1.417,0.63,2.16,0.63c1.199,0.001,2.386-0.535,3.141-1.528l0.011-0.016l21.415-30.619 c0.604,0.263,1.208,0.525,1.8,0.812c0.005,0.003,0.007,0.005,0.007,0.005c0.593,0.286,1.168,0.594,1.744,0.897l-13.281,34.729 l-0.008,0.028c-0.12,0.361-0.177,0.733-0.177,1.095c0,1.51,0.979,2.918,2.536,3.465l0.051,0.021l37.882,8.542 C511.128,921.51,511.055,922.235,510.962,922.958z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M511.311,918.969l-37.434-8.439 c-0.792-0.289-1.286-1.009-1.289-1.778c0.003-0.186,0.03-0.373,0.09-0.557l13.16-34.414c3.683,2.12,7.074,4.648,10.1,7.534 c9.532,9.095,15.428,21.646,15.43,35.521C511.368,917.552,511.34,918.26,511.311,918.969z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M457.632,898.454c4.723-8.496,7.814-16.595,8.709-25.55 c0.029-0.288-0.087-0.568-0.317-0.76c-0.23-0.188-0.538-0.26-0.832-0.194c-5.848,1.342-10.367,2.729-13.975,4.284 c-3.604,1.554-6.301,3.285-8.438,5.296c-0.865,0.812-1.298,1.896-1.298,2.977c0,0.929,0.315,1.86,0.947,2.639 c3.553,4.371,7.21,8.3,11.946,11.952c0.394,0.306,0.877,0.453,1.342,0.449C456.48,899.548,457.236,899.164,457.632,898.454z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M443.911,886.05c-0.372-0.455-0.555-0.999-0.555-1.543 c0-0.632,0.246-1.242,0.737-1.704c1.958-1.842,4.442-3.45,7.895-4.939c3.181-1.372,7.196-2.634,12.322-3.86 c-1.022,8.165-3.912,15.657-8.333,23.615c-0.046,0.089-0.151,0.143-0.261,0.144l-0.164-0.053 C450.962,894.165,447.415,890.363,443.911,886.05z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M418.146,895.831c-0.272-0.131-0.59-0.127-0.856,0.008 c-0.269,0.136-0.451,0.386-0.491,0.674c-0.62,4.429-0.924,8.152-0.924,11.363c-0.001,5.025,0.748,8.816,2.207,12.021 c0.722,1.584,2.36,2.519,4.073,2.518c0.43,0,0.865-0.058,1.292-0.182c5.558-1.581,10.717-3.418,16-6.312 c0.704-0.388,1.075-1.092,1.072-1.781c0.002-0.471-0.171-0.957-0.525-1.349C433.388,905.528,426.58,899.857,418.146,895.831z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M438.514,914.373c-5.12,2.805-10.117,4.589-15.602,6.15 c-0.251,0.069-0.506,0.104-0.757,0.104c-1.008-0.002-1.951-0.55-2.354-1.44c-1.337-2.937-2.054-6.447-2.054-11.312 c0-2.795,0.241-6.036,0.736-9.845c7.617,3.854,13.903,9.127,20.095,15.932l0.066,0.178 C438.644,914.244,438.595,914.329,438.514,914.373z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M471.022,929.991c0,0.081,0.007,0.163,0.018,0.254h-0.002 c1.236,9.541,3.57,17.87,8.217,25.697c0.15,0.251,0.417,0.415,0.718,0.448c0.302,0.029,0.599-0.082,0.798-0.295 c3.984-4.296,6.857-7.903,8.878-11.147c2.021-3.244,3.189-6.136,3.732-8.951c0.049-0.253,0.072-0.504,0.072-0.748 c-0.004-1.846-1.284-3.486-3.158-4.045c-5.54-1.643-10.896-2.86-16.973-3.266l-0.151-0.007c-0.601,0-1.143,0.241-1.523,0.613 C471.263,928.916,471.022,929.43,471.022,929.991z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M472.982,929.799l0.189-0.081l0.02,0.001 c5.892,0.395,11.083,1.573,16.548,3.191c1.099,0.323,1.845,1.3,1.842,2.339c-0.002,0.141-0.014,0.281-0.04,0.424 c-0.5,2.579-1.566,5.253-3.502,8.361c-1.78,2.864-4.313,6.092-7.776,9.898c-4.068-7.248-6.204-14.97-7.362-23.907l-0.002-0.013 l-0.003-0.021L472.982,929.799z"
                        ></path>
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M450.349,932.39c-0.369-0.253-0.812-0.394-1.273-0.394 c-0.329,0-0.668,0.075-0.98,0.228c-8.972,4.387-16.37,9.335-22.482,16.188c-0.195,0.219-0.27,0.516-0.193,0.795 c0.073,0.282,0.284,0.509,0.567,0.617c8.773,3.278,14.96,4.699,20.102,4.702c1.412,0,2.744-0.109,4.021-0.316 c2.158-0.354,3.665-2.161,3.665-4.197l-0.005-0.203V949.8c-0.286-5.523-0.928-10.74-2.615-16.323 C451.016,933.02,450.718,932.642,450.349,932.39z"
                        ></path>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M451.902,950.012c0,1.195-0.894,2.246-2.107,2.438 c-1.17,0.189-2.395,0.288-3.706,0.289c-4.529,0.001-10.163-1.209-18.15-4.114c5.738-6.136,12.607-10.702,21.015-14.812 l0.124-0.03l0.18,0.058l0.1,0.13c1.633,5.41,2.258,10.463,2.542,15.908L451.902,950.012z"
                        ></path>
                      </g>
                    </g>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M1197.291,782.342l-4.387-1.632c0,0-0.459-0.221-0.941-0.108 l-9.016-0.111l-6.872,0.239c-13.114,0.415-29.084,1.683-41.909,2.8c-12.229,1.065-19.8,2.248-20.451,2.3 c-1.653,0.13-2.846,0.675-2.846,0.675c-1.945,0.92-0.985,2.371-0.985,2.371c0.315,0.48,0.661,0.779,1.032,1.045 c0.02,0.031,1.06,0.817,2.901,1.601c2.117,0.899,3.878,1.593,3.878,1.593c1.504,0.604,3.812,0.989,3.812,0.989 c7.571,0.648,14.557,1.882,19.89,2.848c0,0,0.775,0.16,1.14,0.229c10.354,1.952,23.079,6.58,25.688,7.273l1.996,0.672 c9.621,3.164,19.937,7.686,19.937,7.686s0.649,0.425,1.724,0.565c0.754,0.1,2.115-0.207,2.115-0.207l7.038-1.283l0.412-0.017 c0,0,0.185-0.007,0.312-0.188l0.917-1.284c0,0,0.185-0.223,0.35-0.659c0.07-0.188,0-1.081,0-1.081l-1.396-20.615 c0,0,0.071-1.692-0.499-2.939C1200.4,783.493,1197.291,782.342,1197.291,782.342z"
                    ></path>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M303.92,835.551c-0.003,0.006-7.427,4.594-7.431,4.598 c-0.433,0.578-0.215,13.815-0.382,14.2c1.758-0.931,10.258-4.88,10.341-4.88l15.614-5.12l58.298-13.148l5.794-4.262 c0.36-0.331,6.905-10.854,7.112-11.006l-87.876,12.07C305.302,831.466,304.087,835.327,303.92,835.551z"
                    ></path>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M554.861,784.494c0.012-0.008-1.747-0.252,0.168-1.993 c0.039-0.036,0.092-0.061,0.143-0.09c11.943-6.811,79.442-44.156,100.771-53.874c35.863-16.339,94.303-29.412,145.857-27.129 c0.236,0.011,24.084,0,58.557,1.677c25.368,1.235,48.491,3.916,65.026,7.692c8.119,1.854,18.829,4.688,24.353,8.004 c9.785,5.879,27.837,15.175,50.061,42.375l159.993-4.555l-44.415-4.264c0,0-87.179-41.027-174.357-51.152 c-13.611-1.58-77.639-5.764-145.224-7.127c-13.373-0.271-92.769,4.818-141.139,27.021 c-16.018,7.352-110.438,60.755-112.867,61.868L554.861,784.494z"
                    ></path>
                  </g>
                </g>
              </svg>
            </div>

            {/* Slide 3 - Rear view */}
            <div
              className={`order-3 flex-shrink-0 w-full h-full snap-center flex items-center justify-center rotate-90 overflow-hidden transform-gpu origin-center ${getFrontRearScaleClass()} ${getRearViewPositionClass()}`}
            >
              <svg
                ref={svgRefs[2]}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="450 -43.5 1584 1086"
                className={isInModal ? "h-auto w-full" : "max-h-full w-full"}
              >
                <g>
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-rear-bumper"
                    style={getPartStyle("rear-bumper")}
                    onClick={(e) => onPartClickFunc("rear-bumper", e)}
                    onMouseEnter={(e) => onPartHover("rear-bumper", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M1367.034,325.678c0,0,2.162,2.478,3.79,38.093
		c0.001,0.031,0,0.062,0,0.094c0.013,22.031,0.414,125.677,0.414,125.792c0,0.112-1.101,119.971-1.101,119.971
		c0.314,40.79-4.269,42.255-4.269,42.255c-1.772,0.862-4.688,0.627-5.611,0.084c-1.503-0.884-2.506-2.403-2.983-3.47
		c-0.236-0.531-0.39-1.011-0.479-1.354c-0.044-0.174-0.075-0.312-0.095-0.406l-0.021-0.107l-0.007-0.034v-0.003l-1.73-10.541
		l-9.74-9.153c-4.13-4.038-6.531-9.435-6.531-15.206l-0.385-244.655c0-5.746,1.122-11.087,6.717-16.705l9.555-8.976
		c0,0,1.591-9.783,2.125-11.428C1358.275,325.031,1363.948,323.266,1367.034,325.678z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-dickie"
                    style={getPartStyle("rear-dickie")}
                    onClick={(e) => onPartClickFunc("rear-dickie", e)}
                    onMouseEnter={(e) => onPartHover("rear-dickie", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M1341.379,324.6l0.011,0.002c0.003,0,7.762,0.429,16.769,0.429
		c0.848,0,1.709-0.005,2.572-0.015c-0.338,0.139-0.652,0.293-0.944,0.47c-1.5,0.912-2.39,2.222-2.893,3.343
		c-0.498,1.113-0.621,2.043-0.626,2.076l0,0l-1.711,10.448l-9.554,8.976c-4.288,4.028-6.719,9.647-6.719,15.53l0.386,245.682
		c0,5.884,2.431,11.505,6.718,15.532l9.556,8.976l1.711,10.447l0,0c0.009,0.052,0.249,1.616,1.274,3.204
		c0.521,0.802,1.242,1.607,2.243,2.216c0.291,0.175,0.607,0.332,0.944,0.469c-0.864-0.008-1.725-0.013-2.572-0.013
		c-9.009,0-16.766,0.427-16.769,0.427l-0.012,0.002c-21.704,2.204-30.034,3.383-41.237,3.383
		c-19.479-0.002-58.165-8.353-58.165-8.353l7.354-1.517c16.667-4.692,11.327-50.5,14.486-54.547
		c2.38-3.05,10.698-7.014,14.219-9.522c3.514-2.504,6.386-6.729,6.884-12.984c0.495-6.18,0.655-30.09,1.106-53.587l0.166-26.018
		l-0.166-27.659c0,0-0.615-46.212-1.111-52.391c-0.498-6.257-4.332-11.651-7.707-14.337c-4.24-3.375-10.595-6.835-12.688-9.24
		c-4.953-5.691,2.883-48.709-14.587-54.607l-7.902-1.701c0,0,26.773-6.591,57.726-7.627
		C1311.337,321.719,1319.675,322.398,1341.379,324.6z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M1266.799,589.498v-14.861c0,0-20.313-14.033-20.964-27.697
		c0,0-0.375-29.713-0.086-57.283c0.298-28.331,0.086-56.248,0.086-56.248c0.074-5.95,1.183-15.263,20.914-28.993l0.071-16.669
		c0,0,3.415,2.43,4.879,3.375c5.642,3.646,7.131,5.239,7.131,5.239c4.184,4.193,6.471,9.923,6.471,13.246
		c0,0,1.056,27.894,1.196,69.24l0.022,1.388c0.56,13.562-1.025,85.467-1.025,85.467s0.661,11.876-7.37,16.753L1266.799,589.498z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M1233.214,549.025l-0.409-59.37l0.938-60.979
		c0,0,8.619-14.635,31.151-32.317l0.011-10.329l1.916,1.716l-0.071,16.669c0,0-21.283,13.287-20.895,28.13
		c0,0-0.216,96.137-0.091,112.712v0.948c0,0-1.519,11.746,21.035,28.432v14.861l-1.86,1.465l-0.045-9.433
		C1264.895,581.53,1242.362,566.599,1233.214,549.025z"
                  />
                  <g>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-tail-lamp"
                      style={getPartStyle("left-tail-lamp")}
                      onClick={(e) => onPartClickFunc("left-tail-lamp", e)}
                      onMouseEnter={(e) => onPartHover("left-tail-lamp", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M1226.836,641.547c0,0-0.885-77.387,6.378-92.521
			c0,0,5.876,14.191,31.682,32.505l0.045,9.433c0,0-0.558,0.514-0.876,1.017c-0.255,0.403-0.427,1.137-0.521,1.781
			c0,0-0.168,1.31-0.305,2.509c-1.087,9.634,1.241,45.39-13.522,50.046C1249.716,646.315,1230.054,651.748,1226.836,641.547z"
                    />
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M1227.948,641.607c0,0-0.999-75.172,5.617-89.681
			c0,0,6.426,12.972,29.936,30.525v7.872c0,0-0.939,0.417-1.23,0.902c-0.232,0.386-0.389,1.087-0.476,1.705
			c0,0-0.153,1.257-0.276,2.404c-0.99,9.236,1.52,45.312-11.933,49.775l0,0c-0.891,0.315-4.948,1.262-8.278,1.558
			C1235.662,647.174,1229.482,646.287,1227.948,641.607z"
                    />
                    <circle
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      cx="1242.361"
                      cy="631.141"
                      r="11.354"
                    />
                    <circle
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      cx="1242.361"
                      cy="605.468"
                      r="11.354"
                    />
                    <circle
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      cx="1242.362"
                      cy="580.989"
                      r="8.675"
                    />
                  </g>
                  <g>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-tail-lamp"
                      style={getPartStyle("right-tail-lamp")}
                      onClick={(e) => onPartClickFunc("right-tail-lamp", e)}
                      onMouseEnter={(e) => onPartHover("right-tail-lamp", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M1227.109,336.339l0.002,0.834
			c-0.383,12.454,0.505,20.547,0.957,37.105c0.58,21.187,2.185,44.902,5.677,54.389c0,0,9.616-15.623,31.15-32.308l0.011-10.33
			c0,0-0.26-0.279-0.532-0.848c-0.207-0.433-0.541-1.476-0.654-1.996c0,0-0.306-1.255-0.44-2.456
			c-1.088-9.632,1.731-44.694-13.031-49.352l-7.83-1.657C1236.287,329.061,1228.76,330.909,1227.109,336.339z"
                    />
                    <path
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      d="M1227.953,336.279c0,0,1.103,73.746,6.036,89.127
			c0,0,7.04-11.918,29.511-29.971l-0.036-8.769c0,0-0.469-0.58-0.759-1.062c-0.232-0.387-0.707-1.558-0.794-2.176l-0.166-1.761
			c-0.992-9.234,1.882-44.817-11.568-49.281c0-0.001,0,0,0,0c-0.891-0.315-4.958-1.533-8.288-1.829
			C1236.244,330.054,1229.489,331.597,1227.953,336.279z"
                    />
                    <circle
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      cx="1242.361"
                      cy="346.058"
                      r="11.354"
                    />
                    <circle
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      cx="1242.361"
                      cy="371.73"
                      r="11.354"
                    />
                    <circle
                      fill="none"
                      stroke="#231F20"
                      stroke-miterlimit="10"
                      cx="1242.362"
                      cy="396.208"
                      r="8.675"
                    />
                  </g>
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M1367.876,647.177c-0.38,1.281-0.972,4.57-2.466,4.896
		c-0.03,0.007,19.553-0.736,25.812-2.247c8.573-2.069,10.094-8.735,10.094-9.233l-0.172-27.104
		c-0.443-3.025-6.375-5.939-9.812-6.669c-5.793-1.229-21.15-1.952-21.15-1.952c0.067,20.273-0.471,26.808-0.471,26.808
		C1369.178,642.083,1367.876,647.177,1367.876,647.177z"
                  />
                  <path
                    fill="none"
                    stroke="#231F20"
                    stroke-miterlimit="10"
                    d="M1368.059,329.723c-0.545-3.296-1.024-4.045-1.024-4.045
		s-0.555-0.499-1.622-0.832c-0.03-0.01,19.552,0.84,25.811,2.352c8.573,2.069,10.094,8.735,10.094,9.233l-0.171,27.105
		c-0.444,3.026-6.375,5.939-9.812,6.669c-5.792,1.23-20.494,1.92-20.494,1.92c0.051-1.103-0.03-2.355-0.008-3.707
		c0.14-8.278-1.119-23.069-1.119-23.069C1369.011,334.936,1368.059,329.723,1368.059,329.723z"
                  />
                </g>
              </svg>
            </div>

            {/* View 4 - Main group part 1 */}
            {/* <div className="flex-shrink-0 w-full snap-center px-4 flex items-center justify-center part-4">
              <svg
                ref={svgRefs[3]}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="-28.5 -43.5 1584 1086"
                className="h-[40vh] w-full"
              >
                <g>
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-left-running-board"
                    style={getPartStyle("left-running-board")}
                    onClick={(e) => onPartClickFunc("left-running-board", e)}
                    onMouseEnter={(e) => onPartHover("left-running-board", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M910.163,917.489c-4.942,5.125-11.219,7.59-18.407,7.383
		c0,0-8.218,0.274-14.009,0.469l-73.844,2.472l-226.74,7.307c-2.102,0.074-3.357,0.269-4.205-0.697c0,0,6.221,5.727,12.2,11.265
		l298.872-5.987c0.568-3.084,5.357-5.008,7.807-6.281C903.256,927.478,910.163,917.489,910.163,917.489z"
                  />
                  <g>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-rear-door"
                      style={getPartStyle("left-rear-door")}
                      onClick={(e) => onPartClickFunc("left-rear-door", e)}
                      onMouseEnter={(e) => onPartHover("left-rear-door", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M784.854,903.658l1.633,24.735l107.581-3.6
			c7.155-0.238,13.771-3.741,17.828-9.438l78.376-110.017c8.16-12.084,12.192-22.779,12.188-32.033
			c-0.002-4.354-1.474-10.181-2.664-12.151c-0.025-0.043-0.101-0.104-0.188-0.209c-17.395-21.22-33.119-32.854-50.279-42.396
			c-2.311-1.282-6.949-3.845-23.945-7.773c-24.119-5.577-58.578-8.526-123.583-9.369l-12.507,80.791
			c-4.015,13.093-7.184,27.553-8.233,41.611c-0.006,0.779-0.009,1.568-0.009,2.365c0,0.066-0.001,0.133-0.001,0.199
			c0,4.026,0.053,8.229,0.148,12.596c0.419,18.918,1.719,40.883,3.652,64.675L784.854,903.658z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M921.07,794.444c0.67-0.58,1.587-1.038,2.734-1.362l0.005-0.001
			l11.094-3.047c0.206-0.056,0.418-0.085,0.63-0.085c0.222,0,0.442,0.031,0.658,0.094l-0.252,0.812l0.252-0.812l1.95,0.564
			l0.213,0.03l0.062-0.002l15.504-1.15c0.264-0.022,0.52-0.107,0.737-0.253l1.261-0.84c0.477-0.316,1.038-0.485,1.616-0.485h13.242
			c0.532,0,1.052,0.16,1.481,0.459l2.37,1.637c0.504,0.35,0.803,0.91,0.803,1.513v2.093c0,0.669-0.317,1.299-0.864,1.711
			l-1.285,0.964c-0.797,0.599-1.735,0.99-2.733,1.141l-11.433,1.724c-0.159,0.021-0.318,0.033-0.477,0.033
			c-0.731,0-1.447-0.25-2.012-0.718l-0.996-0.822c-0.21-0.175-0.479-0.271-0.755-0.271l-0.065,0.002l-14.889,0.799
			c-0.515,0.027-1,0.241-1.362,0.599l-1.645,1.625c-0.495,0.489-1.171,0.76-1.875,0.76c-0.107,0-0.215-0.006-0.324-0.018
			l-10.774-1.307l-0.077-0.011c-1.178-0.25-2.103-0.639-2.784-1.175c-0.676-0.531-1.115-1.271-1.11-2.066
			C919.97,795.76,920.406,795.019,921.07,794.444z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-rear-window"
                      style={getPartStyle("left-rear-window")}
                      onMouseEnter={(e) => onPartHover("left-rear-window", e)}
                      onClick={(e) => onPartClickFunc("left-rear-window", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M923.549,772.39c-10.534,2.46-101.449,6.837-115.956,5.958
			c-0.89-0.054-0.974-0.911-0.974-0.911l4.926-67.962c0.117-1.384,1.308-2.458,2.75-2.459l0.033,0.001
			c2.726-0.502,70.22-1.632,111.929,8.777c1.739,0.435,3.041,1.797,3.044,3.515l-0.007,0.194l-4.214,51.134
			C925.056,771.052,923.549,772.39,923.549,772.39z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-quarter-glass"
                      style={getPartStyle("left-quarter-glass")}
                      onMouseEnter={(e) => onPartHover("left-quarter-glass", e)}
                      onClick={(e) => onPartClickFunc("left-quarter-glass", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M987.631,755.637c-8.075,8.059-58.845,16.02-58.845,16.02
			c-0.259,0.029-0.517-0.055-0.702-0.229c-0.188-0.175-0.287-0.418-0.271-0.669l4.006-51.828c-0.065-1.988,3.071-0.994,3.22-0.962
			c6.994,1.623,34.53,15.321,52.809,35.816C988.522,754.543,987.631,755.637,987.631,755.637z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-front-door"
                      style={getPartStyle("left-front-door")}
                      onMouseEnter={(e) => onPartHover("left-front-door", e)}
                      onClick={(e) => onPartClickFunc("left-front-door", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M781.189,823.775l0.001-0.036
			c0.723-14.184,4.166-28.673,8.104-41.54c0,0-169.952,14.366-224.375,3.508l-6.731,19.885c-1.966,10.817-3.016,26.792-3.016,34.011
			c0,8.254,0.616,16.183,1.915,23.751l0.002,0.019l8.904,60.03c0.33,1.448,1.111,3.121,2.277,4.946
			c1.168,1.832,2.718,3.815,4.549,5.904c0.521,0.596,1.276,0.939,2.067,0.939l0.088-0.003l211.642-6.738l-1.628-24.677
			c-2.086-25.646-3.435-49.175-3.739-69.09c-0.022-1.422-0.038-2.827-0.05-4.21c-0.012-1.388-0.017-2.753-0.017-4.1l0,0
			C781.182,825.501,781.184,824.634,781.189,823.775z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M777.699,803.125c0,0.668-0.32,1.299-0.867,1.708l-1.284,0.967
			c-0.796,0.599-1.736,0.99-2.732,1.141l-11.434,1.723c-0.159,0.023-0.318,0.035-0.476,0.035c-0.733,0-1.447-0.252-2.012-0.719
			l-0.995-0.824c-0.211-0.175-0.48-0.271-0.758-0.27h-0.062h-0.002l-14.89,0.801c-0.514,0.025-1,0.239-1.361,0.597l-1.646,1.624
			c-0.497,0.49-1.174,0.762-1.874,0.762c-0.108,0-0.218-0.006-0.327-0.019l-10.773-1.305l-0.078-0.015
			c-1.176-0.247-2.103-0.636-2.784-1.175c-0.676-0.529-1.114-1.27-1.111-2.065c0.002-0.814,0.438-1.556,1.102-2.131
			c0.67-0.578,1.585-1.035,2.734-1.359l0.006-0.001l11.093-3.047c0.205-0.058,0.417-0.085,0.628-0.085
			c0.222,0,0.444,0.029,0.659,0.093l-0.252,0.812l0.253-0.812l1.949,0.564l0.213,0.029l0.061-0.002L756.183,799
			c0.265-0.019,0.52-0.104,0.737-0.251l1.261-0.84c0.476-0.315,1.039-0.484,1.615-0.484h13.243c0.53,0,1.051,0.159,1.482,0.46
			l2.366,1.636c0.506,0.347,0.807,0.912,0.807,1.512v2.093H777.699z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-front-window"
                      style={getPartStyle("left-front-window")}
                      onMouseEnter={(e) => onPartHover("left-front-window", e)}
                      onClick={(e) => onPartClickFunc("left-front-window", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M789.699,707.199c-7.368-1.293-95.544-0.022-164.995,43.44
			c-1.4,0.877-4.104,2.447-4.327,3.896c-0.143,0.916-0.639,29.966-0.639,29.966c0.2,1.503,1.376,1.746,2.663,1.969l0,0
			c34.46,1.392,149.642-6.363,149.642-6.363c0.573-0.05,1.054-0.443,1.197-0.98l18.077-69.771
			C791.538,708.139,790.656,707.367,789.699,707.199z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M564.919,785.707c59.182,10.99,224.375-3.508,224.375-3.508
			l12.507-80.791c-14.035-0.955-78.432-1.202-141.53,25.222c0,0-10.857,4.462-39.852,20.05
			c-15.808,8.498-35.178,18.979-51.458,28.008c-12.77,7.083-11.379,6.333-13.479,7.513l-0.311,0.212
			c-0.988,0.7-1.596,1.888-0.312,2.083C555.888,784.649,564.919,785.707,564.919,785.707z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M565.729,781.321c7.456-4.782,39.037-21.364,49.68-25.972
			c1.388-0.601,2.479,0.911,2.581,2.411l-0.396,25.629c0.008,1.582-0.423,2.88-1.909,2.918c-29.416,0.732-49.134-2.979-49.704-3.231
			C565.409,782.821,565.206,781.658,565.729,781.321z"
                    />
                    <g>
                      <path
                        fill="none"
                        class="part-svg"
                        id="svg-left-quarter-panel"
                        style={getPartStyle("left-quarter-panel")}
                        onMouseEnter={(e) =>
                          onPartHover("left-quarter-panel", e)
                        }
                        onClick={(e) =>
                          onPartClickFunc("left-quarter-panel", e)
                        }
                        onMouseLeave={onPartLeave}
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1122.825,794.256c-1.875-0.181-3.731-0.606-5.488-1.276
				c-1.457-0.559-2.74-1.092-3.816-1.598c-1.079-0.508-1.946-0.984-2.604-1.463c-0.436-0.324-0.785-0.638-1.032-1.045
				c-0.138-0.232-0.242-0.512-0.242-0.825c-0.005-0.341,0.139-0.683,0.348-0.927c0.208-0.246,0.462-0.42,0.745-0.562
				c0.562-0.285,1.274-0.473,2.213-0.628l0.024-0.004c30.266-4.195,69.402-5.448,69.402-5.448l8.656,0.146
				c2.018-0.031,1.172-0.183,2.879,0.466l3.382,1.249c1.188,0.116,1.161-0.88,1.161-0.88l-1.593-15.786
				c-0.207-1.265,0.447-2.412,0.947-2.945c0,0,3.471-4.122,3.621-4.426c0.158-0.309,0.204-0.53,0.201-0.638
				c-0.006-0.108-0.207-0.271-0.443-0.344l-40.019-0.722l-161.373,4.555c1.724,3.845,2.664,7.567,2.664,12.151
				c0,9.741-4.508,21.221-13.138,33.366l-78.438,109.854c-13.538,18.508-25.908,17.476-26.854,23.17l56.755,0.045l-0.922-11.607
				c-0.601-3.762-0.914-7.613-0.914-11.538c0-41.547,34.875-75.228,77.897-75.229c19.041,0,36.484,6.602,50.016,17.559
				l83.585-59.938C1141.979,796.834,1132.82,795.217,1122.825,794.256z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1198.998,863.77l17.425-22.181
				c-0.263-0.686-11.518-19.516-11.518-19.516c-0.878-1.521-0.235-0.104-0.94-8.682c-0.138-1.666-1.827-1.554-1.827-1.554
				l-1.104,0.044l-7.794,1.422c-1.563,0.334-2.619-0.294-2.913-0.423c-14.528-6.402-29.15-10.66-29.15-10.66
				s-0.009-0.004-0.013-0.006c-1.186-0.41-2.397-0.802-3.614-1.188c-0.559-0.177-1.115-0.354-1.68-0.523
				c-0.409-0.127-0.83-0.244-1.242-0.366c-1.35-0.396-2.716-0.78-4.099-1.148l-83.587,59.938
				c10.711,9.075,18.763,21.013,23.054,34.491l94.161-9.062l10.407-14.943L1198.998,863.77z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1092.764,908.748c0,0.009,0,0.02,0.002,0.029
				c0.062,0.804,0.104,1.612,0.14,2.423c0.008,0.196,0.017,0.393,0.021,0.589c0.028,0.824,0.048,1.654,0.048,2.485l3.361,8.572
				l14.346-1.332l49.374-6.481c0,0,27.971-4.113,41.114-9.438c3.68-1.49,7.369-5.866,7.369-5.866l-48.483,5.866l6.602-6.994
				l12.272-6.829l5.146-7.414l-94.161,9.062C1091.32,898.219,1092.373,903.607,1092.764,908.748z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1216.343,841.59l-21.857,27.823l-15.556,22.357l-12.272,6.829
				l-6.602,6.994l48.483-5.866c4.126-1.479,5.252-6.153,5.252-7.695c0,0,4.211-46.007,4.211-46.52
				c0.001-1.043-0.849-2.359-0.97-2.658L1216.343,841.59z"
                      />
                    </g>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M347.601,932.028l-0.139,0.004c-1.706,0-2.792-1.418-3.622-2.843
			c-10.605-18.2-20.518-32.984-27.991-41.977c-2.767-3.33-5.063-6.357-7.801-9.194c-5.729-6.083-10.986-11.466-14.897-14.665
			c-2.45-2.005-5.358-2.643-5.416-2.316v10.924v44.228c0,0,0,3.197-4.139,6.393c-2.702,2.09,19.036,7.729,19.036,7.729
			s34.692,7.658,36.142,7.726c2.175,0.101,36.599,0.18,38.807,0.001c2.207-0.177,6.438-1.598,6.15-7.062L347.601,932.028z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M348.937,847.815c0,0-2.184-0.278-2.185-0.277l-8.43-1.165
			l-4.066-0.563c-5.57-0.771-10.796-1.821-10.796-1.821c-10.621,2.664-19.572,6.519-19.588,6.528c0,0-16.308,7.246-16.139,10.521
			c0,0,0.117-0.142,2.438,0.496c3.287,0.904,17.876,16.485,17.876,16.485c3.75,4.245,10.1,11.995,10.1,11.995
			c4.348,5.394,11.355,15.985,11.356,15.987c4.117,5.791,12.83,20.729,14.336,23.188c1.328,2.166,2.208,2.837,3.88,2.837l0.846-0.02
			l35.163-1.037c-0.138-0.878-0.268-1.747-0.39-2.604c-0.002-0.017-0.004-0.033-0.006-0.049c-0.059-0.414-0.116-0.826-0.171-1.23
			c-0.014-0.109-0.027-0.215-0.042-0.323s-0.028-0.22-0.043-0.328c-0.059-0.452-0.114-0.897-0.166-1.332
			c-0.006-0.061-0.015-0.12-0.021-0.182c-0.055-0.467-0.103-0.918-0.148-1.361c-0.011-0.111-0.021-0.22-0.032-0.328
			c-0.087-0.897-0.151-1.733-0.188-2.493c-0.004-0.081-0.008-0.163-0.011-0.243c-0.016-0.373-0.026-0.73-0.026-1.062
			c0-0.724,0.014-1.444,0.035-2.163c0.006-0.229,0.018-0.455,0.026-0.684c0.02-0.496,0.042-0.992,0.071-1.486
			c0.015-0.256,0.032-0.512,0.05-0.768c0.033-0.478,0.071-0.954,0.115-1.43c0.021-0.242,0.042-0.483,0.065-0.725
			c0.055-0.541,0.115-1.082,0.183-1.622c0.02-0.161,0.036-0.326,0.057-0.488c0.094-0.719,0.196-1.436,0.31-2.146
			c0.007-0.04,0.015-0.08,0.021-0.116c0.108-0.666,0.226-1.328,0.352-1.987c0.03-0.162,0.063-0.324,0.097-0.485
			c0.108-0.55,0.222-1.099,0.344-1.645c0.037-0.166,0.073-0.331,0.111-0.497c0.132-0.576,0.271-1.149,0.416-1.718
			c0.029-0.113,0.056-0.227,0.085-0.34c0.556-2.141,1.209-4.243,1.951-6.308c0.005-0.013,0.009-0.024,0.014-0.038
			c5.126-14.223,14.375-26.711,26.614-35.571c-3.97-1.482-9.311-3.466-13.531-5.045C384.276,851.548,366.55,850.049,348.937,847.815
			z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-left-fender"
                      style={getPartStyle("left-fender")}
                      onClick={(e) => onPartClickFunc("left-fender", e)}
                      onMouseEnter={(e) => onPartHover("left-fender", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M575.845,937.058c0,0-2.108-1.865-2.886-2.638
			c-0.221-0.221-0.362-0.42-0.575-0.667c-3.805-4.396-5.256-7.385-5.256-7.385c-0.825-1.54-1.136-2.968-1.136-2.968l-8.669-58.45
			c-1.315-7.666-2.151-15.967-2.151-24.294c0-10.898,1.117-23.552,3.077-35.439l6.671-19.511l-22.64-2.729l-12.875-0.777h-20.231
			c-3.138,0.052-62.22,3.395-81.844,7.438c-4.085,0.843-39.522,7.089-43.589,7.993c-43.728,9.724-80.488,29.038-78.35,30.372
			c0,0,70.775-9.584,87.876-12.07c0.408-0.062,0.804,0.161,0.949,0.535c0.149,0.373,0.007,0.792-0.338,1.012l-7.724,9.459
			c0,0-4.4,3.701-5.794,4.262c-0.033,0.019-23.485,5.571-23.521,5.586l-13.881,2.513c0,0-11.819,2.026-19.499,4.688
			c6.648,1.329,18.939,3.084,32.188,4.646c4.602,0.543,9.367,1.071,13.98,1.585c12.029,1.336,23.182,2.68,30.236,3.955l0.149,0.043
			c4.621,1.729,9.058,3.387,13.382,5.002c12.659-8.661,28.121-13.754,44.816-13.754c42.86,0.001,77.604,33.554,77.605,74.941
			c0,1.142,0.061,2.83,0.121,4.225c0.061,1.396,0.123,2.495,0.123,2.497v0.006l0,0c0.05,1.017,0.072,2.05,0.072,3.1
			c0,5.044-0.558,10.468-1.502,16.164l50.524-0.711L575.845,937.058z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M980.534,740.683c-9.412-8.56-22.104-17.384-33.383-23.271
			c-0.068-0.034-0.141-0.062-0.211-0.093c-8.912-3.9-21.559-6.542-21.559-6.542c-10.396-2.268-21.896-4.125-33.617-5.297
			c-40.73-4.072-83.859-4.072-89.933-4.072l-12.54,80.891c0,0,63.822-1.358,111.425-4.646c37.845-2.615,99.728-16.128,92.26-24.443
			C985.813,745.23,980.534,740.683,980.534,740.683z"
                    />
                    <g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1085.974,912.117c-0.012-0.249-0.04-0.496-0.057-0.744
					c-0.054-0.883-0.116-1.761-0.208-2.632c-0.04-0.397-0.1-0.792-0.146-1.188c-0.087-0.693-0.169-1.39-0.275-2.077
					c-0.09-0.591-0.202-1.176-0.311-1.763c-0.084-0.462-0.163-0.928-0.258-1.389c-0.16-0.789-0.346-1.573-0.536-2.354
					c-0.055-0.229-0.104-0.458-0.164-0.687c-6.309-24.606-26.649-44.078-52.183-49.975c-0.202-0.047-0.408-0.093-0.613-0.139
					c-0.807-0.179-1.618-0.342-2.434-0.493c-0.407-0.073-0.814-0.146-1.227-0.213c-0.653-0.108-1.31-0.213-1.971-0.306
					c-0.564-0.076-1.135-0.145-1.705-0.209c-0.543-0.062-1.087-0.126-1.632-0.174c-0.659-0.062-1.321-0.105-1.984-0.147
					c-0.494-0.03-0.99-0.066-1.486-0.089c-1.078-0.049-2.156-0.08-3.245-0.08c-19.478,0-37.104,7.621-49.867,19.947
					c-8.776,8.478-15.249,19.17-18.438,31.138c-0.579,2.175-1.052,4.396-1.406,6.648c-0.533,3.381-0.813,6.846-0.813,10.372
					c0,1.031,0.031,2.058,0.079,3.078c0.013,0.255,0.025,0.509,0.043,0.761c0.051,0.958,0.123,1.913,0.221,2.857
					c0.006,0.061,0.01,0.117,0.017,0.178c0.107,1.037,0.244,2.068,0.399,3.092c0.027,0.172,0.056,0.342,0.082,0.51
					c0.158,0.989,0.333,1.977,0.535,2.95c0.005,0.023,0.012,0.049,0.016,0.072c0.221,1.062,0.471,2.114,0.744,3.158
					c0.015,0.056,0.027,0.107,0.044,0.161c0.833,3.163,1.894,6.24,3.168,9.21c5.995,13.979,16.666,25.607,30.138,33.069
					c6.689,3.707,14.326,5.543,22.059,5.543h32.392c1.989,0,3.968-0.354,5.827-1.044c24.508-9.075,42.432-30.969,44.966-57.187
					c0.09-0.917,0.158-1.839,0.211-2.765c0.01-0.16,0.021-0.32,0.03-0.479c0.051-1.037,0.08-2.079,0.082-3.125
					c0-0.013,0-0.027,0-0.041c0-0.06-0.006-0.117-0.006-0.175C1086.057,914.295,1086.031,913.203,1085.974,912.117z"
                        />
                        <path
                          fill="none"
                          class="part-svg"
                          id="svg-left-rear-rim"
                          style={getPartStyle("left-rear-rim")}
                          onMouseEnter={(e) => onPartHover("left-rear-rim", e)}
                          onClick={(e) => onPartClickFunc("left-rear-rim", e)}
                          onMouseLeave={onPartLeave}
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1070.668,918.945l-0.002,0.014c0,0.021-0.003,0.042-0.005,0.063
					c-0.068,1.017-0.168,2.023-0.291,3.021c-0.036,0.269-0.071,0.533-0.106,0.8c-0.062,0.439-0.126,0.88-0.201,1.315
					c-0.097,0.596-0.206,1.188-0.323,1.776l-0.002,0.007c-0.04,0.193-0.078,0.391-0.12,0.585l-0.032,0.161h-0.007
					c-4.424,20.143-20.612,36.072-41.298,40.79v0.008l-0.762,0.164l-0.007,0.001c-0.562,0.12-1.126,0.229-1.69,0.334
					c-0.055,0.009-0.104,0.018-0.154,0.025c-1.025,0.183-2.061,0.338-3.102,0.467c-0.148,0.02-0.3,0.037-0.448,0.054
					c-0.539,0.062-1.082,0.12-1.628,0.165l-0.01,0.001l-0.798,0.067l-0.001-0.01c-1.367,0.1-2.747,0.165-4.141,0.165
					c-19.633,0-36.863-9.896-46.663-24.801l-0.015,0.005l-0.418-0.661l0,0c-0.624-0.978-1.21-1.978-1.768-2.996
					c-0.01-0.019-0.019-0.037-0.026-0.054c-0.554-1.009-1.068-2.041-1.556-3.087c0-0.004,0-0.006,0-0.006l-0.019-0.04
					c-3.067-6.636-4.786-13.979-4.786-21.715c0-13.272,5.031-25.402,13.336-34.739l-0.006-0.006l0.519-0.568l0.005-0.004
					c1.449-1.584,2.992-3.083,4.623-4.489l0.002-0.005l0.59-0.506l0.01,0.006c9.695-8.117,22.333-13.042,36.172-13.044
					c7.922,0,15.447,1.623,22.259,4.524l0.017,0.006c0,0.001,0.003,0.002,0.003,0.002c0.887,0.379,1.756,0.775,2.611,1.194
					c0.016,0.009,0.031,0.019,0.047,0.023c0.855,0.418,1.694,0.856,2.521,1.315l0.006,0.002l0.692,0.386l0,0
					c16.216,9.297,27.093,26.369,27.093,45.901C1070.789,916.704,1070.74,917.827,1070.668,918.945z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1044.519,888.808c-1.479,0.001-2.91,0.711-3.777,1.96
					c-3.267,4.711-6.052,9.379-8.217,14.959c-0.092,0.236-0.137,0.484-0.137,0.729c0,0.503,0.188,0.975,0.506,1.35
					c0.316,0.371,0.78,0.648,1.32,0.735c5.064,0.817,9.89,1.284,14.622,1.284c4.686,0,9.282-0.459,13.909-1.489
					c0.301-0.066,0.545-0.268,0.666-0.542c0.115-0.273,0.09-0.585-0.073-0.836c-3.222-4.986-6.066-8.732-8.789-11.577
					c-2.72-2.841-5.328-4.778-8.037-6.11C1045.876,888.958,1045.193,888.808,1044.519,888.808z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1053.158,896.626c2.397,2.505,4.957,5.821,7.834,10.18
					c-4.034,0.803-8.047,1.188-12.155,1.188c-4.604,0-9.323-0.455-14.309-1.259l-0.173-0.1l-0.07-0.18l0.017-0.088
					c2.1-5.408,4.795-9.932,8.019-14.584c0.502-0.729,1.35-1.145,2.199-1.144c0.387,0.001,0.771,0.084,1.132,0.263
					C1048.133,892.124,1050.549,893.905,1053.158,896.626z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1043.698,869.666L1043.698,869.666l-0.692-0.386l-0.006-0.002
					c-0.827-0.459-1.666-0.897-2.521-1.315c-0.016-0.006-0.031-0.016-0.047-0.022c-0.855-0.419-1.726-0.816-2.608-1.194
					c0,0-0.005-0.002-0.007-0.003l-0.016-0.006l-0.714-0.305l-0.012,0.016c-6.621-2.711-13.889-4.235-21.533-4.235
					c-13.839,0.002-26.478,4.928-36.172,13.044l-0.011-0.006l-0.589,0.506l-0.002,0.006c-1.631,1.405-3.174,2.904-4.623,4.488
					l-0.005,0.004l-0.519,0.568l0.006,0.006c-8.305,9.337-13.336,21.467-13.336,34.739c0,7.476,1.618,14.579,4.493,21.039
					l-0.018,0.011l0.311,0.665l0.016,0.039c0,0,0.003,0.003,0.003,0.007c0.486,1.046,1.002,2.078,1.555,3.087
					c0.01,0.018,0.019,0.035,0.027,0.055c0.557,1.019,1.144,2.019,1.767,2.995l0,0l0.419,0.661l0.015-0.005
					c9.8,14.903,27.03,24.801,46.663,24.801c1.394,0,2.771-0.065,4.141-0.164l0.001,0.01l0.798-0.068l0.01-0.001
					c0.546-0.045,1.089-0.102,1.628-0.165c0.148-0.016,0.3-0.034,0.447-0.054c1.042-0.128,2.076-0.284,3.103-0.466
					c0.052-0.01,0.101-0.019,0.153-0.026c0.565-0.104,1.13-0.214,1.691-0.334l0.007-0.001l0.761-0.164v-0.008
					c20.687-4.717,36.875-20.647,41.299-40.79h0.007l0.032-0.161l0.119-0.585l0.002-0.007c0.118-0.588,0.229-1.182,0.324-1.776
					c0.075-0.437,0.141-0.876,0.201-1.314c0.038-0.268,0.07-0.532,0.106-0.8c0.123-1,0.223-2.007,0.291-3.022
					c0.002-0.021,0.005-0.042,0.005-0.063l0.002-0.014l0.049-0.758l-0.009-0.003c0.045-0.866,0.081-1.737,0.081-2.617
					C1070.789,896.035,1059.912,878.963,1043.698,869.666z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1015.54,864.046c7.255,0.001,14.167,1.405,20.474,3.937
					l-21.489,31.11c-0.396,0.526-1.012,0.807-1.642,0.809c-0.386-0.002-0.771-0.107-1.116-0.329l-30.869-23.17
					C990.223,868.704,1002.309,864.047,1015.54,864.046z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M966.365,935.583c-2.688-6.152-4.177-12.914-4.177-20.016
					c0-12.684,4.755-24.283,12.62-33.26l23.877,29.82c0.273,0.354,0.404,0.762,0.404,1.168c-0.002,0.583-0.266,1.15-0.771,1.537
					L966.365,935.583z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1015.54,967.09c-14.738-0.001-28.068-5.766-37.725-15.091
					c-2.69-2.597-5.079-5.479-7.149-8.579l36-14.077c0.215-0.066,0.429-0.103,0.638-0.103c0.855,0.001,1.658,0.533,1.934,1.365
					l9.953,36.352C1017.982,967.035,1016.769,967.09,1015.54,967.09z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1053.265,951.999c-6.791,6.559-15.399,11.343-25.033,13.612
					l-1.003-37.744c0.029-1.061,0.934-1.905,2.026-1.905l0.056,0.002l0.012,0.001h0.002l38.314,0.691
					C1065.419,936.432,1060.337,945.169,1053.265,951.999z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1068.484,921.848c-0.031,0.244-0.064,0.489-0.097,0.733
					c-0.062,0.433-0.123,0.864-0.196,1.292c-0.053,0.321-0.113,0.639-0.175,0.955h-0.002l-38.653-0.696l0,0l-0.104-0.002
					c-2.128,0-3.874,1.639-3.925,3.703v0.026l1.014,38.152c-0.295,0.059-0.593,0.12-0.893,0.177
					c-0.023,0.004-0.049,0.008-0.074,0.012c-1.018,0.185-2.049,0.337-3.09,0.465c-0.126,0.017-0.249,0.031-0.377,0.046
					c-0.267,0.03-0.536,0.055-0.806,0.081l-10.055-36.721l-0.004-0.016c-0.532-1.618-2.076-2.648-3.743-2.648
					c-0.417,0-0.843,0.067-1.259,0.202l-0.027,0.01l-36.354,14.217c-0.151-0.249-0.301-0.496-0.449-0.742
					c-0.291-0.494-0.589-0.985-0.865-1.49c0,0-0.008-0.012-0.01-0.019c-0.414-0.761-0.79-1.541-1.165-2.321l32.272-20.956
					l0.026-0.021c0.989-0.745,1.514-1.865,1.512-2.991c0-0.784-0.255-1.586-0.778-2.264l-0.008-0.009l-24.104-30.107
					c1.071-1.139,2.194-2.229,3.365-3.271l31.211,23.425l0.023,0.016c0.67,0.437,1.437,0.646,2.188,0.646
					c1.216,0.001,2.418-0.547,3.183-1.567l0.012-0.015l21.688-31.402c0.611,0.271,1.224,0.538,1.822,0.832
					c0.004,0.002,0.008,0.004,0.008,0.004c0.602,0.294,1.185,0.608,1.765,0.922l-13.449,35.62l-0.01,0.028
					c-0.12,0.372-0.178,0.751-0.178,1.123c0,1.548,0.99,2.99,2.568,3.555l0.053,0.02l38.365,8.761
					C1068.652,920.362,1068.576,921.107,1068.484,921.848z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1068.834,917.757l-37.909-8.656
					c-0.803-0.295-1.303-1.033-1.305-1.822c0.002-0.191,0.03-0.383,0.089-0.572l13.329-35.293c3.729,2.172,7.164,4.768,10.227,7.725
					c9.657,9.326,15.627,22.201,15.629,36.432C1068.894,916.302,1068.865,917.03,1068.834,917.757z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1014.472,896.717c4.782-8.713,7.913-17.02,8.819-26.204
					c0.031-0.297-0.087-0.584-0.321-0.777c-0.231-0.195-0.546-0.271-0.843-0.2c-5.922,1.377-10.499,2.798-14.151,4.391
					c-3.651,1.596-6.384,3.372-8.545,5.435c-0.877,0.833-1.316,1.947-1.316,3.054c0,0.951,0.321,1.908,0.962,2.704
					c3.597,4.484,7.302,8.512,12.099,12.261c0.398,0.312,0.887,0.463,1.358,0.46
					C1013.304,897.838,1014.072,897.442,1014.472,896.717z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1000.576,883.994c-0.377-0.468-0.562-1.024-0.562-1.582
					c0-0.647,0.25-1.274,0.748-1.748c1.982-1.889,4.498-3.54,7.994-5.067c3.222-1.404,7.288-2.702,12.479-3.959
					c-1.035,8.376-3.961,16.058-8.439,24.222c-0.047,0.089-0.153,0.146-0.264,0.146l-0.166-0.055
					C1007.716,892.317,1004.123,888.419,1000.576,883.994z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M974.481,894.025c-0.273-0.135-0.597-0.13-0.865,0.009
					c-0.273,0.139-0.457,0.394-0.497,0.688c-0.629,4.543-0.938,8.361-0.938,11.657c-0.003,5.153,0.758,9.041,2.235,12.329
					c0.729,1.624,2.39,2.582,4.124,2.581c0.438,0,0.878-0.061,1.311-0.185c5.628-1.623,10.854-3.508,16.202-6.477
					c0.714-0.396,1.089-1.118,1.087-1.828c0.002-0.48-0.173-0.979-0.531-1.382C989.918,903.971,983.025,898.154,974.481,894.025z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M995.112,913.042c-5.188,2.877-10.248,4.706-15.803,6.31
					c-0.255,0.071-0.513,0.106-0.769,0.106c-1.021-0.001-1.975-0.562-2.382-1.479c-1.354-3.01-2.08-6.611-2.08-11.6
					c0-2.865,0.244-6.191,0.745-10.097c7.714,3.951,14.081,9.36,20.351,16.337l0.069,0.182
					C995.242,912.911,995.191,912.998,995.112,913.042z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1028.032,929.062c0,0.082,0.007,0.168,0.018,0.257h-0.002
					c1.251,9.787,3.616,18.33,8.322,26.356c0.15,0.26,0.422,0.429,0.728,0.459c0.305,0.031,0.605-0.081,0.81-0.304
					c4.036-4.402,6.944-8.103,8.991-11.431c2.048-3.328,3.229-6.292,3.779-9.182c0.05-0.258,0.071-0.516,0.071-0.769
					c-0.004-1.891-1.299-3.575-3.196-4.147c-5.609-1.684-11.036-2.934-17.189-3.35l-0.152-0.006c-0.607,0-1.157,0.248-1.544,0.63
					C1028.277,927.958,1028.032,928.484,1028.032,929.062z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1030.017,928.862l0.192-0.081l0.019,0.001
					c5.968,0.403,11.225,1.611,16.76,3.271c1.113,0.331,1.868,1.333,1.865,2.396c-0.002,0.146-0.014,0.291-0.041,0.438
					c-0.506,2.646-1.588,5.389-3.547,8.575c-1.804,2.937-4.368,6.246-7.875,10.151c-4.121-7.435-6.284-15.354-7.458-24.521
					l-0.002-0.012l-0.003-0.021L1030.017,928.862z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1007.095,931.521c-0.374-0.259-0.82-0.401-1.29-0.401
					c-0.332,0-0.675,0.074-0.991,0.232c-9.087,4.497-16.58,9.571-22.77,16.604c-0.198,0.225-0.273,0.527-0.196,0.815
					c0.073,0.288,0.286,0.521,0.572,0.631c8.888,3.362,15.153,4.819,20.36,4.823c1.431,0,2.777-0.111,4.071-0.323
					c2.186-0.364,3.71-2.219,3.71-4.308l-0.004-0.209v-0.011c-0.29-5.664-0.938-11.015-2.646-16.74
					C1007.771,932.166,1007.469,931.78,1007.095,931.521z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1008.667,949.594c0,1.227-0.904,2.305-2.133,2.5
					c-1.188,0.195-2.428,0.297-3.754,0.299c-4.588,0.001-10.293-1.242-18.385-4.221c5.812-6.293,12.771-10.976,21.284-15.19
					l0.124-0.029l0.184,0.058l0.101,0.133c1.654,5.549,2.288,10.729,2.575,16.316L1008.667,949.594z"
                        />
                      </g>
                    </g>
                    <g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M528.234,913.469c-0.013-0.241-0.04-0.481-0.056-0.724
					c-0.053-0.86-0.114-1.716-0.205-2.567c-0.04-0.386-0.1-0.771-0.146-1.157c-0.084-0.679-0.166-1.355-0.271-2.026
					c-0.091-0.575-0.2-1.145-0.308-1.716c-0.083-0.452-0.161-0.907-0.256-1.357c-0.158-0.768-0.34-1.532-0.529-2.293
					c-0.053-0.224-0.103-0.444-0.161-0.67c-6.229-23.99-26.314-42.976-51.524-48.726c-0.2-0.045-0.404-0.09-0.605-0.136
					c-0.798-0.174-1.598-0.334-2.402-0.479c-0.402-0.073-0.806-0.143-1.21-0.207c-0.646-0.106-1.293-0.207-1.946-0.298
					c-0.559-0.075-1.121-0.143-1.685-0.204c-0.535-0.061-1.072-0.124-1.61-0.17c-0.651-0.061-1.306-0.102-1.96-0.146
					c-0.489-0.029-0.979-0.063-1.469-0.086c-1.063-0.048-2.129-0.077-3.203-0.077c-19.232,0-36.637,7.431-49.239,19.448
					c-8.666,8.266-15.057,18.691-18.206,30.359c-0.573,2.123-1.037,4.284-1.39,6.482c-0.526,3.298-0.804,6.677-0.804,10.113
					c0,1.008,0.032,2.006,0.08,3.001c0.012,0.247,0.025,0.495,0.042,0.742c0.053,0.935,0.123,1.864,0.219,2.787
					c0.006,0.058,0.01,0.114,0.016,0.173c0.105,1.012,0.242,2.017,0.396,3.015c0.025,0.166,0.053,0.332,0.081,0.496
					c0.155,0.965,0.329,1.927,0.529,2.877c0.004,0.023,0.009,0.048,0.013,0.071c0.219,1.034,0.466,2.062,0.734,3.081
					c0.013,0.049,0.028,0.104,0.043,0.155c0.823,3.084,1.869,6.082,3.129,8.979c5.919,13.629,16.455,24.968,29.757,32.245
					c6.604,3.613,14.146,5.402,21.781,5.402h31.983c1.963,0,3.917-0.348,5.753-1.02c24.198-8.847,41.896-30.194,44.398-55.758
					c0.088-0.896,0.157-1.792,0.208-2.694c0.009-0.156,0.021-0.313,0.029-0.47c0.051-1.012,0.079-2.024,0.081-3.046
					c0-0.012,0-0.026,0-0.04c0-0.059-0.005-0.115-0.005-0.171C528.316,915.594,528.287,914.528,528.234,913.469z"
                        />
                        <path
                          fill="none"
                          class="part-svg"
                          id="svg-left-front-rim"
                          style={getPartStyle("left-front-rim")}
                          onMouseEnter={(e) => onPartHover("left-front-rim", e)}
                          onClick={(e) => onPartClickFunc("left-front-rim", e)}
                          onMouseLeave={onPartLeave}
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M513.119,920.129l-0.001,0.015c0,0.021-0.002,0.041-0.005,0.062
					c-0.066,0.991-0.166,1.974-0.287,2.947c-0.035,0.26-0.069,0.521-0.104,0.778c-0.062,0.43-0.125,0.857-0.198,1.283
					c-0.098,0.58-0.205,1.158-0.321,1.73l-0.003,0.009c-0.038,0.188-0.074,0.38-0.117,0.57l-0.032,0.155h-0.006
					c-4.368,19.641-20.354,35.174-40.776,39.771v0.009l-0.753,0.157l-0.008,0.002c-0.554,0.118-1.11,0.226-1.669,0.326
					c-0.051,0.009-0.101,0.017-0.151,0.025c-1.013,0.178-2.033,0.329-3.062,0.452c-0.146,0.021-0.293,0.038-0.442,0.056
					c-0.533,0.061-1.068,0.115-1.607,0.159l-0.008,0.003l-0.788,0.064l-0.002-0.009c-1.351,0.097-2.712,0.159-4.087,0.159
					c-19.387,0-36.399-9.648-46.076-24.183l-0.015,0.007l-0.414-0.645l0,0c-0.615-0.955-1.192-1.931-1.742-2.922
					c-0.011-0.021-0.02-0.037-0.028-0.053c-0.546-0.985-1.054-1.99-1.534-3.011c0-0.005,0-0.007,0-0.007l-0.02-0.038
					c-3.029-6.47-4.726-13.63-4.726-21.173c0-12.94,4.967-24.768,13.167-33.871l-0.005-0.006l0.513-0.556l0.006-0.005
					c1.431-1.542,2.955-3.004,4.563-4.375l0.002-0.004l0.583-0.492l0.01,0.004c9.572-7.916,22.052-12.716,35.716-12.717
					c7.821,0,15.252,1.582,21.978,4.408l0.016,0.009c0.003,0.001,0.005,0.001,0.005,0.001c0.875,0.366,1.732,0.755,2.577,1.162
					c0.017,0.01,0.032,0.02,0.049,0.023c0.845,0.408,1.672,0.836,2.487,1.285l0.008,0.002l0.684,0.374l0,0
					c16.011,9.063,26.75,25.711,26.75,44.756C513.24,917.943,513.192,919.039,513.119,920.129z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M487.302,890.744c-1.463,0.001-2.875,0.693-3.731,1.909
					c-3.226,4.594-5.973,9.145-8.111,14.586c-0.092,0.232-0.135,0.474-0.135,0.711c0,0.492,0.186,0.952,0.499,1.315
					c0.312,0.363,0.77,0.635,1.304,0.719c4.999,0.796,9.767,1.253,14.438,1.253c4.626,0,9.164-0.448,13.733-1.452
					c0.296-0.067,0.539-0.264,0.656-0.53c0.115-0.267,0.089-0.568-0.071-0.812c-3.182-4.864-5.99-8.518-8.679-11.288
					c-2.687-2.771-5.26-4.66-7.936-5.961C488.641,890.889,487.964,890.744,487.302,890.744z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M495.829,898.367c2.369,2.441,4.896,5.675,7.736,9.925
					c-3.984,0.78-7.945,1.159-12.001,1.159c-4.546,0-9.207-0.445-14.128-1.229l-0.169-0.096l-0.072-0.177l0.018-0.085
					c2.072-5.273,4.734-9.685,7.916-14.22c0.497-0.71,1.334-1.116,2.174-1.114c0.38,0.001,0.76,0.081,1.116,0.256
					C490.869,893.977,493.255,895.715,495.829,898.367z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M486.49,872.079L486.49,872.079l-0.683-0.374l-0.009-0.002
					c-0.815-0.449-1.644-0.877-2.487-1.285c-0.018-0.005-0.033-0.015-0.049-0.023c-0.845-0.407-1.701-0.795-2.574-1.162
					c0,0-0.004,0-0.007-0.001l-0.016-0.008l-0.705-0.297l-0.012,0.016c-6.538-2.643-13.712-4.128-21.261-4.128
					c-13.665,0.001-26.144,4.801-35.715,12.717l-0.011-0.004l-0.583,0.492l-0.002,0.004c-1.608,1.371-3.133,2.833-4.563,4.375
					l-0.005,0.005l-0.514,0.556l0.005,0.006c-8.199,9.104-13.167,20.931-13.167,33.871c0,7.289,1.598,14.217,4.437,20.515
					l-0.018,0.012l0.307,0.646l0.016,0.038c0,0,0.003,0.002,0.003,0.007c0.48,1.021,0.988,2.024,1.534,3.011
					c0.009,0.016,0.018,0.033,0.028,0.053c0.549,0.991,1.127,1.967,1.743,2.922l0,0l0.413,0.646l0.015-0.008
					c9.677,14.533,26.688,24.183,46.076,24.183c1.375,0,2.737-0.062,4.087-0.159l0.002,0.009l0.788-0.064l0.008-0.002
					c0.539-0.044,1.074-0.101,1.607-0.16c0.148-0.018,0.295-0.035,0.442-0.056c1.029-0.123,2.05-0.274,3.062-0.451
					c0.051-0.011,0.101-0.019,0.151-0.026c0.56-0.101,1.116-0.208,1.669-0.326l0.008-0.002l0.753-0.157v-0.009
					c20.423-4.599,36.408-20.132,40.776-39.771h0.006l0.032-0.155l0.117-0.57l0.003-0.009c0.117-0.572,0.225-1.15,0.321-1.729
					c0.074-0.427,0.136-0.854,0.199-1.284c0.036-0.257,0.068-0.52,0.104-0.778c0.121-0.976,0.22-1.956,0.287-2.947
					c0.003-0.021,0.005-0.041,0.005-0.062l0.001-0.015l0.05-0.738l-0.01-0.002c0.046-0.847,0.081-1.695,0.081-2.554
					C513.24,897.79,502.5,881.144,486.49,872.079z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M458.688,866.6c7.165,0,13.988,1.368,20.215,3.839l-21.219,30.334
					c-0.391,0.512-0.998,0.784-1.619,0.786c-0.383-0.001-0.765-0.104-1.104-0.32l-30.479-22.591
					C433.689,871.141,445.622,866.6,458.688,866.6z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M410.13,936.349c-2.654-6-4.123-12.591-4.123-19.515
					c0-12.365,4.694-23.678,12.463-32.43l23.575,29.076c0.271,0.346,0.398,0.743,0.398,1.139c-0.002,0.569-0.261,1.122-0.76,1.5
					L410.13,936.349z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M458.688,967.071c-14.552-0.001-27.716-5.622-37.25-14.714
					c-2.655-2.531-5.016-5.342-7.059-8.364l35.545-13.727c0.211-0.066,0.425-0.1,0.63-0.1c0.846,0,1.637,0.518,1.908,1.329
					l9.828,35.443C461.099,967.02,459.9,967.071,458.688,967.071z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M495.939,952.357c-6.706,6.394-15.208,11.059-24.721,13.271
					l-0.988-36.801c0.027-1.035,0.921-1.858,2-1.858l0.056,0.003h0.01h0.002l37.833,0.675
					C507.938,937.177,502.92,945.697,495.939,952.357z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M510.962,922.958c-0.031,0.24-0.061,0.479-0.094,0.717
					c-0.06,0.42-0.123,0.842-0.193,1.261c-0.053,0.312-0.114,0.62-0.172,0.93h-0.002l-38.168-0.677l0,0l-0.103-0.004
					c-2.102,0-3.825,1.598-3.875,3.609v0.028l1,37.199c-0.291,0.056-0.583,0.117-0.881,0.172c-0.022,0.004-0.048,0.008-0.074,0.01
					c-1.005,0.181-2.023,0.332-3.05,0.455c-0.125,0.015-0.246,0.03-0.374,0.043c-0.262,0.031-0.528,0.055-0.793,0.08l-9.929-35.805
					l-0.004-0.015c-0.524-1.578-2.05-2.584-3.695-2.583c-0.412,0-0.831,0.064-1.243,0.198l-0.025,0.009l-35.896,13.862
					c-0.15-0.243-0.298-0.484-0.444-0.725c-0.287-0.481-0.582-0.963-0.854-1.454c0,0-0.008-0.011-0.01-0.018
					c-0.409-0.741-0.781-1.503-1.151-2.265l31.866-20.432l0.029-0.021c0.975-0.729,1.492-1.818,1.491-2.917
					c0-0.766-0.252-1.545-0.77-2.207l-0.008-0.008l-23.801-29.355c1.059-1.109,2.168-2.174,3.324-3.188l30.815,22.838l0.025,0.019
					c0.66,0.424,1.417,0.63,2.16,0.63c1.199,0.001,2.386-0.535,3.141-1.528l0.011-0.016l21.415-30.619
					c0.604,0.263,1.208,0.525,1.8,0.812c0.005,0.003,0.007,0.005,0.007,0.005c0.593,0.286,1.168,0.594,1.744,0.897l-13.281,34.729
					l-0.008,0.028c-0.12,0.361-0.177,0.733-0.177,1.095c0,1.51,0.979,2.918,2.536,3.465l0.051,0.021l37.882,8.542
					C511.128,921.51,511.055,922.235,510.962,922.958z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M511.311,918.969l-37.434-8.439
					c-0.792-0.289-1.286-1.009-1.289-1.778c0.003-0.186,0.03-0.373,0.09-0.557l13.16-34.414c3.683,2.12,7.074,4.648,10.1,7.534
					c9.532,9.095,15.428,21.646,15.43,35.521C511.368,917.552,511.34,918.26,511.311,918.969z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M457.632,898.454c4.723-8.496,7.814-16.595,8.709-25.55
					c0.029-0.288-0.087-0.568-0.317-0.76c-0.23-0.188-0.538-0.26-0.832-0.194c-5.848,1.342-10.367,2.729-13.975,4.284
					c-3.604,1.554-6.301,3.285-8.438,5.296c-0.865,0.812-1.298,1.896-1.298,2.977c0,0.929,0.315,1.86,0.947,2.639
					c3.553,4.371,7.21,8.3,11.946,11.952c0.394,0.306,0.877,0.453,1.342,0.449C456.48,899.548,457.236,899.164,457.632,898.454z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M443.911,886.05c-0.372-0.455-0.555-0.999-0.555-1.543
					c0-0.632,0.246-1.242,0.737-1.704c1.958-1.842,4.442-3.45,7.895-4.939c3.181-1.372,7.196-2.634,12.322-3.86
					c-1.022,8.165-3.912,15.657-8.333,23.615c-0.046,0.089-0.151,0.143-0.261,0.144l-0.164-0.053
					C450.962,894.165,447.415,890.363,443.911,886.05z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M418.146,895.831c-0.272-0.131-0.59-0.127-0.856,0.008
					c-0.269,0.136-0.451,0.386-0.491,0.674c-0.62,4.429-0.924,8.152-0.924,11.363c-0.001,5.025,0.748,8.816,2.207,12.021
					c0.722,1.584,2.36,2.519,4.073,2.518c0.43,0,0.865-0.058,1.292-0.182c5.558-1.581,10.717-3.418,16-6.312
					c0.704-0.388,1.075-1.092,1.072-1.781c0.002-0.471-0.171-0.957-0.525-1.349C433.388,905.528,426.58,899.857,418.146,895.831z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M438.514,914.373c-5.12,2.805-10.117,4.589-15.602,6.15
					c-0.251,0.069-0.506,0.104-0.757,0.104c-1.008-0.002-1.951-0.55-2.354-1.44c-1.337-2.937-2.054-6.447-2.054-11.312
					c0-2.795,0.241-6.036,0.736-9.845c7.617,3.854,13.903,9.127,20.095,15.932l0.066,0.178
					C438.644,914.244,438.595,914.329,438.514,914.373z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M471.022,929.991c0,0.081,0.007,0.163,0.018,0.254h-0.002
					c1.236,9.541,3.57,17.87,8.217,25.697c0.15,0.251,0.417,0.415,0.718,0.448c0.302,0.029,0.599-0.082,0.798-0.295
					c3.984-4.296,6.857-7.903,8.878-11.147c2.021-3.244,3.189-6.136,3.732-8.951c0.049-0.253,0.072-0.504,0.072-0.748
					c-0.004-1.846-1.284-3.486-3.158-4.045c-5.54-1.643-10.896-2.86-16.973-3.266l-0.151-0.007c-0.601,0-1.143,0.241-1.523,0.613
					C471.263,928.916,471.022,929.43,471.022,929.991z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M472.982,929.799l0.189-0.081l0.02,0.001
					c5.892,0.395,11.083,1.573,16.548,3.191c1.099,0.323,1.845,1.3,1.842,2.339c-0.002,0.141-0.014,0.281-0.04,0.424
					c-0.5,2.579-1.566,5.253-3.502,8.361c-1.78,2.864-4.313,6.092-7.776,9.898c-4.068-7.248-6.204-14.97-7.362-23.907l-0.002-0.013
					l-0.003-0.021L472.982,929.799z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M450.349,932.39c-0.369-0.253-0.812-0.394-1.273-0.394
					c-0.329,0-0.668,0.075-0.98,0.228c-8.972,4.387-16.37,9.335-22.482,16.188c-0.195,0.219-0.27,0.516-0.193,0.795
					c0.073,0.282,0.284,0.509,0.567,0.617c8.773,3.278,14.96,4.699,20.102,4.702c1.412,0,2.744-0.109,4.021-0.316
					c2.158-0.354,3.665-2.161,3.665-4.197l-0.005-0.203V949.8c-0.286-5.523-0.928-10.74-2.615-16.323
					C451.016,933.02,450.718,932.642,450.349,932.39z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M451.902,950.012c0,1.195-0.894,2.246-2.107,2.438
					c-1.17,0.189-2.395,0.288-3.706,0.289c-4.529,0.001-10.163-1.209-18.15-4.114c5.738-6.136,12.607-10.702,21.015-14.812
					l0.124-0.03l0.18,0.058l0.1,0.13c1.633,5.41,2.258,10.463,2.542,15.908L451.902,950.012z"
                        />
                      </g>
                    </g>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M1197.291,782.342l-4.387-1.632c0,0-0.459-0.221-0.941-0.108
			l-9.016-0.111l-6.872,0.239c-13.114,0.415-29.084,1.683-41.909,2.8c-12.229,1.065-19.8,2.248-20.451,2.3
			c-1.653,0.13-2.846,0.675-2.846,0.675c-1.945,0.92-0.985,2.371-0.985,2.371c0.315,0.48,0.661,0.779,1.032,1.045
			c0.02,0.031,1.06,0.817,2.901,1.601c2.117,0.899,3.878,1.593,3.878,1.593c1.504,0.604,3.812,0.989,3.812,0.989
			c7.571,0.648,14.557,1.882,19.89,2.848c0,0,0.775,0.16,1.14,0.229c10.354,1.952,23.079,6.58,25.688,7.273l1.996,0.672
			c9.621,3.164,19.937,7.686,19.937,7.686s0.649,0.425,1.724,0.565c0.754,0.1,2.115-0.207,2.115-0.207l7.038-1.283l0.412-0.017
			c0,0,0.185-0.007,0.312-0.188l0.917-1.284c0,0,0.185-0.223,0.35-0.659c0.07-0.188,0-1.081,0-1.081l-1.396-20.615
			c0,0,0.071-1.692-0.499-2.939C1200.4,783.493,1197.291,782.342,1197.291,782.342z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M303.92,835.551c-0.003,0.006-7.427,4.594-7.431,4.598
			c-0.433,0.578-0.215,13.815-0.382,14.2c1.758-0.931,10.258-4.88,10.341-4.88l15.614-5.12l58.298-13.148l5.794-4.262
			c0.36-0.331,6.905-10.854,7.112-11.006l-87.876,12.07C305.302,831.466,304.087,835.327,303.92,835.551z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M554.861,784.494c0.012-0.008-1.747-0.252,0.168-1.993
			c0.039-0.036,0.092-0.061,0.143-0.09c11.943-6.811,79.442-44.156,100.771-53.874c35.863-16.339,94.303-29.412,145.857-27.129
			c0.236,0.011,24.084,0,58.557,1.677c25.368,1.235,48.491,3.916,65.026,7.692c8.119,1.854,18.829,4.688,24.353,8.004
			c9.785,5.879,27.837,15.175,50.061,42.375l159.993-4.555l-44.415-4.264c0,0-87.179-41.027-174.357-51.152
			c-13.611-1.58-77.639-5.764-145.224-7.127c-13.373-0.271-92.769,4.818-141.139,27.021
			c-16.018,7.352-110.438,60.755-112.867,61.868L554.861,784.494z"
                    />
                  </g>
                </g>
              </svg>
            </div> */}

            {/* Slide 4 - Right side view */}
            <div className="order-4 flex-shrink-0 w-full h-full snap-center flex items-center justify-center rotate-180 overflow-hidden">
              <svg
                ref={svgRefs[4]}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="-28.5 -400 1584 1086"
                className={isInModal ? "h-auto w-full" : "max-h-full w-full"}
              >
                <g>
                  <path
                    fill="none"
                    stroke="#000000"
                    class="part-svg"
                    id="svg-right-running-board"
                    style={getPartStyle("right-running-board")}
                    onMouseEnter={(e) => onPartHover("right-running-board", e)}
                    onMouseLeave={onPartLeave}
                    onClick={(e) => onPartClickFunc("right-running-board", e)}
                    stroke-miterlimit="10"
                    d="M901.042,61.038c-4.942-5.125-11.219-7.59-18.407-7.383
		c0,0-8.218-0.274-14.009-0.47l-73.844-2.471l-226.74-7.307c-2.101-0.074-3.357-0.27-4.205,0.696c0,0,6.221-5.726,12.2-11.264
		l298.872,5.987c0.568,3.084,5.357,5.008,7.807,6.281C894.135,51.05,901.042,61.038,901.042,61.038z"
                  />
                  <g>
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-rear-door"
                      style={getPartStyle("right-rear-door")}
                      onMouseEnter={(e) => onPartHover("right-rear-door", e)}
                      onClick={(e) => onPartClickFunc("right-rear-door", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M775.734,74.869l1.632-24.735l107.581,3.6
			c7.155,0.238,13.771,3.74,17.828,9.438l78.377,110.016c8.159,12.084,12.191,22.78,12.188,32.034
			c-0.002,4.354-1.474,10.181-2.664,12.151c-0.025,0.043-0.101,0.103-0.188,0.209c-17.395,21.22-33.119,32.853-50.279,42.396
			c-2.311,1.283-6.949,3.846-23.945,7.774c-24.119,5.577-58.578,8.526-123.583,9.369l-12.507-80.791
			c-4.015-13.093-7.184-27.553-8.233-41.611c-0.006-0.78-0.009-1.568-0.009-2.366c0-0.065-0.001-0.132-0.001-0.198
			c0-4.026,0.054-8.229,0.148-12.596c0.419-18.918,1.719-40.883,3.653-64.675L775.734,74.869z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M911.948,184.083c0.67,0.58,1.587,1.038,2.734,1.362l0.005,0.001
			l11.094,3.047c0.207,0.056,0.418,0.085,0.63,0.085c0.222,0,0.442-0.031,0.659-0.094l-0.252-0.812l0.252,0.812l1.949-0.564
			l0.214-0.03l0.061,0.002l15.504,1.15c0.264,0.022,0.521,0.107,0.737,0.253l1.261,0.84c0.477,0.316,1.038,0.485,1.616,0.485h13.242
			c0.532,0,1.052-0.16,1.481-0.459l2.37-1.637c0.504-0.35,0.803-0.91,0.803-1.513v-2.093c0-0.669-0.317-1.299-0.863-1.711
			l-1.286-0.964c-0.797-0.6-1.735-0.99-2.733-1.142l-11.433-1.723c-0.159-0.021-0.318-0.033-0.477-0.033
			c-0.731,0-1.446,0.25-2.012,0.718l-0.996,0.822c-0.21,0.175-0.479,0.271-0.755,0.271l-0.065-0.002l-14.889-0.799
			c-0.515-0.027-1-0.241-1.361-0.599l-1.646-1.625c-0.495-0.489-1.171-0.76-1.875-0.76c-0.107,0-0.215,0.006-0.324,0.018
			l-10.774,1.307l-0.077,0.011c-1.177,0.25-2.103,0.639-2.784,1.175c-0.676,0.531-1.115,1.271-1.11,2.066
			C910.849,182.767,911.284,183.509,911.948,184.083z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-rear-window"
                      style={getPartStyle("right-rear-window")}
                      onMouseEnter={(e) => onPartHover("right-rear-window", e)}
                      onClick={(e) => onPartClickFunc("right-rear-window", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M914.428,206.137c-10.534-2.46-101.449-6.837-115.956-5.958
			c-0.89,0.054-0.974,0.911-0.974,0.911l4.926,67.962c0.117,1.383,1.308,2.458,2.75,2.459l0.033-0.001
			c2.726,0.502,70.22,1.631,111.929-8.777c1.739-0.435,3.041-1.798,3.044-3.515l-0.007-0.195l-4.214-51.134
			C915.935,207.474,914.428,206.137,914.428,206.137z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-quarter-glass"
                      style={getPartStyle("right-quarter-glass")}
                      onMouseEnter={(e) =>
                        onPartHover("right-quarter-glass", e)
                      }
                      onClick={(e) => onPartClickFunc("right-quarter-glass", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M978.51,222.89c-8.075-8.059-58.845-16.02-58.845-16.02
			c-0.259-0.029-0.516,0.055-0.702,0.229c-0.188,0.175-0.287,0.417-0.271,0.668l4.007,51.829c-0.066,1.988,3.07,0.994,3.219,0.961
			c6.994-1.623,34.53-15.32,52.809-35.815C979.402,223.984,978.51,222.89,978.51,222.89z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-front-door"
                      style={getPartStyle("right-front-door")}
                      onMouseEnter={(e) => onPartHover("right-front-door", e)}
                      onClick={(e) => onPartClickFunc("right-front-door", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M772.069,154.752l0.001,0.036
			c0.722,14.184,4.166,28.673,8.104,41.54c0,0-169.952-14.366-224.375-3.508l-6.731-19.886c-1.965-10.816-3.016-26.792-3.016-34.01
			c0-8.254,0.616-16.183,1.915-23.751l0.002-0.019l8.904-60.03c0.33-1.448,1.112-3.121,2.278-4.946
			c1.168-1.832,2.717-3.815,4.548-5.904c0.521-0.596,1.276-0.939,2.067-0.939l0.088,0.003l211.642,6.738l-1.628,24.677
			c-2.086,25.646-3.435,49.175-3.739,69.09c-0.021,1.422-0.038,2.827-0.05,4.21c-0.011,1.388-0.017,2.753-0.017,4.1l0,0
			C772.061,153.026,772.063,153.893,772.069,154.752z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M768.577,175.402c0-0.668-0.32-1.3-0.867-1.708l-1.284-0.968
			c-0.796-0.598-1.736-0.989-2.732-1.14l-11.434-1.723c-0.159-0.023-0.317-0.035-0.476-0.035c-0.733,0-1.447,0.252-2.012,0.719
			l-0.995,0.824c-0.211,0.175-0.48,0.27-0.758,0.269h-0.062h-0.002l-14.889-0.8c-0.515-0.025-1-0.24-1.362-0.597l-1.646-1.624
			c-0.497-0.49-1.174-0.762-1.874-0.762c-0.108,0-0.218,0.006-0.327,0.019l-10.773,1.305l-0.078,0.015
			c-1.176,0.247-2.103,0.636-2.784,1.175c-0.676,0.529-1.114,1.27-1.11,2.064c0.001,0.815,0.438,1.557,1.101,2.132
			c0.67,0.578,1.585,1.035,2.734,1.359l0.006,0.001l11.093,3.047c0.205,0.058,0.417,0.085,0.628,0.085
			c0.222,0,0.444-0.029,0.659-0.093l-0.252-0.812l0.253,0.812l1.949-0.564l0.213-0.029l0.061,0.002l15.504,1.152
			c0.265,0.019,0.52,0.104,0.737,0.251l1.261,0.84c0.476,0.315,1.039,0.484,1.615,0.484h13.243c0.531,0,1.051-0.159,1.482-0.46
			l2.366-1.636c0.506-0.347,0.808-0.912,0.808-1.512v-2.093H768.577z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-front-window"
                      style={getPartStyle("right-front-window")}
                      onMouseEnter={(e) => onPartHover("right-front-window", e)}
                      onClick={(e) => onPartClickFunc("right-front-window", e)}
                      onMouseLeave={onPartLeave}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M780.577,271.328c-7.368,1.293-95.544,0.021-164.995-43.441
			c-1.4-0.876-4.104-2.446-4.327-3.896c-0.143-0.916-0.639-29.966-0.639-29.966c0.201-1.504,1.376-1.746,2.663-1.97l0,0
			c34.46-1.391,149.642,6.364,149.642,6.364c0.573,0.049,1.054,0.443,1.197,0.98l18.077,69.771
			C782.417,270.388,781.534,271.16,780.577,271.328z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M555.798,192.82c59.182-10.99,224.375,3.508,224.375,3.508
			l12.507,80.791c-14.035,0.955-78.432,1.202-141.53-25.223c0,0-10.857-4.462-39.852-20.05
			c-15.808-8.498-35.178-18.979-51.458-28.007c-12.77-7.083-11.379-6.333-13.479-7.513l-0.311-0.212
			c-0.988-0.7-1.596-1.889-0.312-2.083C546.768,193.878,555.798,192.82,555.798,192.82z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M556.609,197.206c7.455,4.782,39.036,21.364,49.679,25.972
			c1.388,0.601,2.479-0.912,2.581-2.412l-0.396-25.628c0.008-1.583-0.423-2.88-1.909-2.918c-29.416-0.732-49.134,2.979-49.703,3.231
			C556.288,195.706,556.085,196.869,556.609,197.206z"
                    />
                    <g>
                      <path
                        fill="none"
                        class="part-svg"
                        id="svg-right-quarter-panel"
                        style={getPartStyle("right-quarter-panel")}
                        onMouseEnter={(e) =>
                          onPartHover("right-quarter-panel", e)
                        }
                        onClick={(e) =>
                          onPartClickFunc("right-quarter-panel", e)
                        }
                        onMouseLeave={onPartLeave}
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1113.703,184.271c-1.875,0.182-3.731,0.607-5.488,1.277
				c-1.457,0.559-2.74,1.092-3.815,1.597c-1.08,0.509-1.947,0.985-2.604,1.464c-0.436,0.323-0.785,0.638-1.032,1.045
				c-0.138,0.232-0.242,0.512-0.242,0.824c-0.005,0.342,0.139,0.684,0.348,0.928c0.208,0.246,0.462,0.42,0.745,0.562
				c0.562,0.285,1.274,0.473,2.213,0.628l0.024,0.003c30.266,4.196,69.402,5.449,69.402,5.449l8.656-0.146
				c2.018,0.031,1.172,0.183,2.879-0.467l3.382-1.249c1.188-0.115,1.161,0.881,1.161,0.881l-1.593,15.785
				c-0.207,1.266,0.448,2.413,0.948,2.946c0,0,3.47,4.122,3.62,4.426c0.158,0.309,0.204,0.53,0.201,0.638
				c-0.006,0.108-0.207,0.271-0.442,0.344l-40.02,0.722l-161.373-4.555c1.724-3.845,2.664-7.567,2.664-12.151
				c0-9.742-4.508-21.221-13.138-33.367L901.763,62c-13.538-18.508-25.908-17.476-26.854-23.17l56.755-0.045l-0.921,11.607
				c-0.601,3.763-0.914,7.614-0.914,11.538c0,41.547,34.875,75.229,77.897,75.229c19.041,0,36.484-6.603,50.016-17.56l83.585,59.938
				C1132.859,181.693,1123.698,183.31,1113.703,184.271z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1189.877,114.758l17.425,22.18
				c-0.263,0.687-11.518,19.517-11.518,19.517c-0.878,1.521-0.235,0.104-0.94,8.682c-0.138,1.666-1.827,1.553-1.827,1.553
				l-1.104-0.044l-7.794-1.421c-1.563-0.334-2.619,0.294-2.913,0.422c-14.528,6.403-29.15,10.661-29.15,10.661
				s-0.009,0.003-0.013,0.005c-1.186,0.411-2.397,0.803-3.614,1.189c-0.559,0.177-1.115,0.354-1.68,0.523
				c-0.409,0.127-0.83,0.243-1.242,0.365c-1.35,0.396-2.716,0.781-4.099,1.149l-83.587-59.938
				c10.711-9.074,18.763-21.012,23.054-34.49l94.161,9.062l10.407,14.943L1189.877,114.758z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1083.643,69.779c0-0.01,0-0.02,0.002-0.029
				c0.062-0.804,0.104-1.612,0.14-2.423c0.008-0.196,0.017-0.394,0.021-0.589c0.028-0.824,0.048-1.654,0.048-2.485l3.361-8.573
				l14.346,1.332l49.374,6.482c0,0,27.972,4.113,41.114,9.438c3.681,1.491,7.369,5.867,7.369,5.867l-48.483-5.867l6.602,6.995
				l12.272,6.829l5.146,7.414l-94.161-9.062C1082.198,80.308,1083.252,74.92,1083.643,69.779z"
                      />
                      <path
                        fill="none"
                        stroke="#000000"
                        stroke-miterlimit="10"
                        d="M1207.222,136.937l-21.857-27.822l-15.556-22.357l-12.272-6.829
				l-6.602-6.995l48.483,5.867c4.126,1.479,5.252,6.153,5.252,7.695c0,0,4.211,46.007,4.211,46.519
				c0.001,1.043-0.848,2.36-0.97,2.659L1207.222,136.937z"
                      />
                    </g>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M338.48,46.499l-0.139-0.004c-1.706,0-2.792,1.418-3.622,2.843
			c-10.605,18.2-20.518,32.984-27.991,41.977c-2.767,3.329-5.063,6.357-7.801,9.194c-5.729,6.083-10.986,11.465-14.897,14.665
			c-2.45,2.005-5.358,2.643-5.416,2.316v-10.924V62.34c0,0,0-3.197-4.138-6.393c-2.703-2.09,19.035-7.729,19.035-7.729
			s34.692-7.658,36.142-7.726c2.175-0.101,36.599-0.181,38.807-0.001c2.207,0.177,6.438,1.598,6.15,7.062L338.48,46.499z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M339.816,130.713c0,0-2.184,0.278-2.185,0.277l-8.43,1.165
			l-4.066,0.563c-5.57,0.771-10.796,1.821-10.796,1.821c-10.621-2.664-19.571-6.52-19.588-6.528c0,0-16.308-7.246-16.139-10.521
			c0,0,0.117,0.142,2.438-0.496c3.287-0.904,17.876-16.485,17.876-16.485c3.75-4.245,10.1-11.995,10.1-11.995
			c4.348-5.394,11.355-15.985,11.356-15.987c4.117-5.791,12.83-20.729,14.336-23.188c1.328-2.166,2.208-2.837,3.881-2.837
			l0.845,0.019l35.163,1.037c-0.138,0.878-0.268,1.748-0.39,2.604c-0.002,0.017-0.004,0.033-0.006,0.049
			c-0.059,0.414-0.116,0.826-0.171,1.23c-0.014,0.109-0.027,0.215-0.042,0.323s-0.028,0.22-0.043,0.328
			c-0.059,0.452-0.114,0.897-0.166,1.332c-0.006,0.061-0.015,0.12-0.021,0.181c-0.056,0.468-0.104,0.919-0.149,1.362
			c-0.011,0.111-0.021,0.219-0.032,0.328c-0.087,0.897-0.151,1.733-0.188,2.493c-0.004,0.081-0.007,0.163-0.011,0.243
			c-0.016,0.373-0.026,0.73-0.026,1.062c0,0.724,0.014,1.444,0.035,2.163c0.006,0.229,0.018,0.455,0.026,0.684
			c0.02,0.495,0.042,0.991,0.071,1.486c0.015,0.255,0.032,0.511,0.05,0.768c0.033,0.478,0.071,0.954,0.115,1.43
			c0.021,0.242,0.042,0.483,0.066,0.724c0.054,0.542,0.114,1.083,0.182,1.623c0.02,0.161,0.036,0.326,0.057,0.488
			c0.095,0.719,0.196,1.435,0.31,2.146c0.007,0.04,0.015,0.08,0.021,0.117c0.108,0.666,0.226,1.328,0.352,1.987
			c0.03,0.162,0.063,0.324,0.097,0.485c0.108,0.55,0.222,1.099,0.344,1.645c0.037,0.166,0.073,0.331,0.111,0.497
			c0.132,0.576,0.271,1.149,0.416,1.718c0.029,0.113,0.056,0.227,0.085,0.339c0.556,2.142,1.209,4.244,1.951,6.309
			c0.005,0.013,0.009,0.023,0.014,0.038c5.126,14.223,14.375,26.711,26.614,35.57c-3.969,1.483-9.311,3.467-13.531,5.045
			C375.155,126.979,357.429,128.478,339.816,130.713z"
                    />
                    <path
                      fill="none"
                      class="part-svg"
                      id="svg-right-fender"
                      style={getPartStyle("right-fender")}
                      onMouseEnter={(e) => onPartHover("right-fender", e)}
                      onMouseLeave={onPartLeave}
                      onClick={(e) => onPartClickFunc("right-fender", e)}
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M566.723,41.469c0,0-2.108,1.865-2.886,2.638
			c-0.221,0.221-0.362,0.42-0.575,0.667c-3.805,4.396-5.256,7.385-5.256,7.385c-0.825,1.54-1.136,2.968-1.136,2.968l-8.669,58.45
			c-1.315,7.666-2.151,15.967-2.151,24.294c0,10.897,1.117,23.552,3.077,35.438l6.671,19.512l-22.64,2.729l-12.875,0.777h-20.231
			c-3.138-0.052-62.22-3.396-81.844-7.438c-4.085-0.842-39.522-7.088-43.589-7.993c-43.727-9.723-80.488-29.037-78.35-30.371
			c0,0,70.775,9.584,87.876,12.07c0.408,0.062,0.804-0.162,0.949-0.536c0.149-0.373,0.007-0.792-0.338-1.011l-7.724-9.459
			c0,0-4.4-3.702-5.794-4.262c-0.033-0.02-23.485-5.571-23.521-5.586l-13.881-2.514c0,0-11.819-2.025-19.499-4.688
			c6.648-1.329,18.939-3.084,32.188-4.646c4.602-0.543,9.367-1.07,13.98-1.585c12.029-1.336,23.182-2.679,30.236-3.955l0.149-0.043
			c4.622-1.728,9.058-3.386,13.382-5.002c12.659,8.662,28.121,13.754,44.816,13.754c42.86-0.001,77.604-33.553,77.605-74.94
			c0-1.143,0.061-2.83,0.121-4.226c0.061-1.395,0.123-2.494,0.123-2.496v-0.007l0,0c0.05-1.016,0.072-2.049,0.072-3.099
			c0-5.044-0.558-10.468-1.502-16.164l50.524,0.711L566.723,41.469z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M971.413,237.844c-9.412,8.56-22.104,17.383-33.383,23.271
			c-0.068,0.034-0.141,0.062-0.21,0.093c-8.913,3.9-21.56,6.542-21.56,6.542c-10.396,2.267-21.896,4.125-33.617,5.297
			c-40.73,4.072-83.859,4.072-89.933,4.072l-12.54-80.892c0,0,63.822,1.359,111.425,4.647c37.845,2.615,99.728,16.128,92.26,24.443
			C976.693,233.298,971.413,237.844,971.413,237.844z"
                    />
                    <g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1076.853,66.41c-0.012,0.249-0.04,0.496-0.057,0.744
					c-0.054,0.883-0.116,1.761-0.208,2.632c-0.04,0.396-0.1,0.792-0.146,1.188c-0.088,0.694-0.17,1.391-0.276,2.078
					c-0.09,0.59-0.202,1.175-0.311,1.763c-0.084,0.462-0.163,0.928-0.258,1.389c-0.16,0.789-0.346,1.573-0.536,2.353
					c-0.055,0.229-0.104,0.458-0.164,0.688c-6.309,24.605-26.649,44.078-52.183,49.975c-0.202,0.046-0.408,0.092-0.613,0.139
					c-0.807,0.179-1.617,0.342-2.434,0.492c-0.407,0.074-0.814,0.146-1.226,0.213c-0.654,0.109-1.311,0.214-1.972,0.307
					c-0.564,0.076-1.135,0.144-1.705,0.209c-0.543,0.062-1.087,0.126-1.632,0.174c-0.659,0.062-1.321,0.105-1.984,0.147
					c-0.494,0.029-0.99,0.066-1.486,0.089c-1.078,0.049-2.156,0.08-3.245,0.08c-19.478,0-37.104-7.621-49.867-19.947
					c-8.776-8.479-15.249-19.17-18.438-31.138c-0.58-2.176-1.053-4.396-1.407-6.648c-0.533-3.381-0.813-6.846-0.813-10.372
					c0-1.032,0.031-2.058,0.079-3.078c0.014-0.255,0.025-0.509,0.043-0.761c0.051-0.958,0.123-1.913,0.222-2.858
					c0.005-0.06,0.009-0.116,0.016-0.177c0.108-1.037,0.244-2.069,0.4-3.092c0.026-0.172,0.055-0.342,0.081-0.51
					c0.158-0.989,0.334-1.977,0.535-2.95c0.005-0.024,0.012-0.049,0.016-0.072c0.221-1.062,0.471-2.115,0.744-3.159
					c0.015-0.055,0.027-0.106,0.044-0.161c0.833-3.162,1.894-6.239,3.168-9.21c5.995-13.978,16.666-25.606,30.138-33.068
					c6.689-3.707,14.326-5.543,22.059-5.543h32.392c1.99,0,3.968,0.354,5.827,1.044c24.508,9.075,42.432,30.969,44.966,57.187
					c0.09,0.917,0.158,1.839,0.211,2.765c0.01,0.159,0.021,0.32,0.03,0.479c0.051,1.037,0.08,2.079,0.082,3.125
					c0,0.013,0,0.027,0,0.041c0,0.06-0.006,0.117-0.006,0.175C1076.936,64.232,1076.909,65.324,1076.853,66.41z"
                        />
                        <path
                          fill="none"
                          class="part-svg"
                          id="svg-right-rear-rim"
                          style={getPartStyle("right-rear-rim")}
                          onMouseEnter={(e) => onPartHover("right-rear-rim", e)}
                          onClick={(e) => onPartClickFunc("right-rear-rim", e)}
                          onMouseLeave={onPartLeave}
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1061.547,59.582l-0.002-0.014c0-0.021-0.003-0.042-0.005-0.063
					c-0.068-1.017-0.168-2.023-0.291-3.021c-0.036-0.269-0.071-0.533-0.106-0.801c-0.062-0.438-0.126-0.879-0.2-1.314
					c-0.098-0.597-0.206-1.188-0.324-1.776l-0.002-0.007c-0.04-0.194-0.078-0.391-0.12-0.585l-0.032-0.161h-0.007
					c-4.424-20.143-20.612-36.073-41.298-40.79v-0.008l-0.762-0.164l-0.007-0.001c-0.562-0.12-1.126-0.23-1.69-0.334
					c-0.055-0.009-0.104-0.018-0.154-0.026c-1.025-0.182-2.06-0.338-3.102-0.466c-0.148-0.02-0.3-0.038-0.448-0.054
					c-0.539-0.063-1.082-0.12-1.628-0.165l-0.009-0.001l-0.798-0.068l-0.002,0.01c-1.367-0.099-2.746-0.164-4.141-0.164
					c-19.633,0-36.863,9.896-46.663,24.801l-0.015-0.005l-0.417,0.659l0,0c-0.624,0.979-1.21,1.979-1.768,2.996
					c-0.01,0.02-0.019,0.037-0.026,0.055c-0.554,1.009-1.068,2.041-1.556,3.087c0,0.004,0,0.006,0,0.006l-0.019,0.04
					c-3.067,6.636-4.786,13.979-4.786,21.715c0,13.272,5.031,25.402,13.336,34.739l-0.006,0.006l0.519,0.568l0.006,0.003
					c1.448,1.584,2.991,3.083,4.622,4.488l0.003,0.005l0.589,0.507l0.01-0.007c9.695,8.117,22.333,13.042,36.172,13.045
					c7.922,0,15.447-1.623,22.259-4.524l0.017-0.006c0.001-0.001,0.003-0.002,0.003-0.002c0.887-0.379,1.756-0.776,2.611-1.195
					c0.016-0.008,0.031-0.018,0.047-0.022c0.855-0.418,1.694-0.856,2.521-1.315l0.006-0.002l0.692-0.386l0,0
					c16.216-9.297,27.093-26.369,27.093-45.901C1061.668,61.823,1061.618,60.7,1061.547,59.582z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1035.398,89.719c-1.479-0.001-2.91-0.711-3.777-1.96
					c-3.267-4.711-6.051-9.379-8.216-14.959c-0.093-0.236-0.138-0.484-0.138-0.729c0-0.503,0.188-0.976,0.506-1.35
					c0.316-0.372,0.78-0.648,1.32-0.735c5.064-0.817,9.891-1.284,14.622-1.284c4.687,0,9.282,0.459,13.909,1.489
					c0.301,0.066,0.545,0.268,0.666,0.542c0.115,0.273,0.09,0.585-0.073,0.836c-3.222,4.985-6.065,8.732-8.789,11.577
					c-2.72,2.841-5.328,4.778-8.037,6.11C1036.755,89.569,1036.072,89.719,1035.398,89.719z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1044.036,81.901c2.397-2.506,4.957-5.821,7.834-10.181
					c-4.034-0.802-8.047-1.188-12.155-1.188c-4.603,0-9.323,0.455-14.309,1.259l-0.173,0.099l-0.07,0.181l0.017,0.088
					c2.1,5.408,4.795,9.932,8.019,14.584c0.502,0.728,1.35,1.145,2.199,1.144c0.387-0.001,0.771-0.084,1.132-0.263
					C1039.012,86.403,1041.428,84.622,1044.036,81.901z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1034.576,108.861L1034.576,108.861l-0.692,0.386l-0.006,0.002
					c-0.827,0.459-1.666,0.896-2.521,1.315c-0.016,0.006-0.031,0.016-0.047,0.022c-0.855,0.419-1.726,0.816-2.608,1.194
					c0,0-0.005,0.002-0.006,0.003l-0.017,0.006l-0.714,0.305l-0.012-0.016c-6.621,2.711-13.888,4.235-21.533,4.235
					c-13.839-0.003-26.477-4.928-36.172-13.045l-0.01,0.007l-0.589-0.507l-0.003-0.005c-1.631-1.405-3.174-2.904-4.622-4.488
					l-0.006-0.005l-0.519-0.567l0.006-0.006c-8.305-9.337-13.336-21.467-13.336-34.739c0-7.476,1.618-14.579,4.493-21.039
					l-0.018-0.011l0.311-0.665l0.016-0.039c0,0,0.003-0.003,0.003-0.007c0.486-1.046,1.002-2.078,1.556-3.087
					c0.009-0.018,0.018-0.035,0.026-0.055c0.558-1.019,1.144-2.019,1.768-2.996l0,0l0.418-0.66l0.015,0.005
					c9.8-14.903,27.03-24.802,46.663-24.802c1.394,0,2.772,0.066,4.141,0.165l0.001-0.01l0.799,0.068l0.009,0.001
					c0.546,0.045,1.089,0.102,1.628,0.165c0.148,0.016,0.3,0.034,0.447,0.054c1.043,0.128,2.076,0.284,3.103,0.466
					c0.052,0.009,0.101,0.019,0.154,0.026c0.564,0.104,1.129,0.214,1.69,0.334l0.007,0.001l0.762,0.164v0.008
					c20.686,4.717,36.874,20.647,41.298,40.79h0.007l0.032,0.161l0.12,0.585l0.002,0.007c0.117,0.588,0.228,1.181,0.323,1.776
					c0.075,0.437,0.141,0.876,0.201,1.314c0.038,0.268,0.07,0.532,0.106,0.8c0.123,0.999,0.223,2.007,0.291,3.022
					c0.002,0.021,0.005,0.042,0.005,0.063l0.002,0.014l0.049,0.758l-0.009,0.003c0.045,0.866,0.081,1.737,0.081,2.617
					C1061.668,82.492,1050.791,99.565,1034.576,108.861z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1006.419,114.482c7.255-0.001,14.167-1.405,20.474-3.937
					l-21.489-31.111c-0.396-0.525-1.012-0.806-1.642-0.808c-0.386,0.002-0.771,0.107-1.116,0.329l-30.868,23.17
					C981.102,109.823,993.188,114.481,1006.419,114.482z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M957.243,42.944c-2.688,6.152-4.176,12.914-4.176,20.016
					c0,12.683,4.754,24.283,12.619,33.26l23.877-29.821c0.273-0.354,0.404-0.762,0.404-1.169c-0.002-0.583-0.266-1.149-0.771-1.536
					L957.243,42.944z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1006.419,11.437c-14.738,0.002-28.068,5.767-37.725,15.092
					c-2.69,2.597-5.079,5.479-7.149,8.579l36,14.076c0.215,0.067,0.429,0.104,0.638,0.104c0.855-0.001,1.658-0.533,1.934-1.365
					l9.953-36.352C1008.862,11.492,1007.648,11.437,1006.419,11.437z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1044.144,26.528c-6.791-6.559-15.399-11.344-25.033-13.612
					l-1.002,37.744c0.028,1.061,0.933,1.905,2.025,1.905l0.057-0.003l0.011-0.001h0.002l38.314-0.69
					C1056.298,42.095,1051.216,33.358,1044.144,26.528z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1059.363,56.679c-0.031-0.245-0.064-0.489-0.097-0.733
					c-0.062-0.433-0.123-0.864-0.195-1.292c-0.054-0.321-0.114-0.639-0.176-0.955h-0.002l-38.653,0.695l0,0l-0.104,0.002
					c-2.128,0-3.874-1.638-3.925-3.702v-0.026l1.014-38.152c-0.295-0.059-0.593-0.121-0.893-0.177
					c-0.023-0.004-0.049-0.008-0.074-0.012c-1.018-0.186-2.049-0.337-3.09-0.465c-0.126-0.017-0.249-0.031-0.377-0.046
					c-0.266-0.03-0.536-0.055-0.805-0.081l-10.056,36.721l-0.004,0.016c-0.532,1.618-2.076,2.648-3.743,2.648
					c-0.417,0-0.843-0.067-1.259-0.202l-0.026-0.01l-36.354-14.217c-0.151,0.249-0.301,0.495-0.449,0.742
					c-0.291,0.494-0.589,0.985-0.864,1.49c0,0-0.009,0.012-0.011,0.019c-0.414,0.761-0.79,1.541-1.165,2.321l32.272,20.956
					l0.026,0.021c0.989,0.745,1.514,1.864,1.512,2.991c0,0.784-0.254,1.586-0.778,2.264l-0.008,0.009l-24.104,30.107
					c1.071,1.139,2.194,2.229,3.365,3.271l31.211-23.425l0.023-0.016c0.67-0.437,1.437-0.646,2.188-0.646
					c1.216-0.001,2.418,0.547,3.184,1.567l0.011,0.015l21.688,31.402c0.611-0.271,1.224-0.538,1.822-0.832
					c0.004-0.002,0.008-0.004,0.008-0.004c0.602-0.294,1.185-0.608,1.766-0.923l-13.45-35.619l-0.009-0.028
					c-0.121-0.372-0.179-0.751-0.179-1.123c0-1.548,0.99-2.99,2.568-3.555l0.053-0.02l38.365-8.761
					C1059.531,58.165,1059.455,57.42,1059.363,56.679z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1059.713,60.77l-37.909,8.657
					c-0.803,0.295-1.303,1.033-1.305,1.822c0.002,0.191,0.03,0.383,0.089,0.572l13.329,35.293c3.729-2.172,7.164-4.768,10.227-7.726
					c9.657-9.325,15.627-22.2,15.629-36.431C1059.773,62.224,1059.743,61.497,1059.713,60.77z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1005.351,81.81c4.782,8.714,7.913,17.021,8.819,26.204
					c0.031,0.297-0.087,0.584-0.321,0.778c-0.231,0.195-0.546,0.27-0.843,0.2c-5.922-1.377-10.499-2.798-14.151-4.392
					c-3.651-1.595-6.384-3.372-8.545-5.434c-0.877-0.833-1.316-1.947-1.316-3.054c0-0.951,0.322-1.908,0.962-2.704
					c3.597-4.484,7.302-8.512,12.099-12.261c0.398-0.312,0.887-0.464,1.358-0.46C1004.183,80.69,1004.95,81.085,1005.351,81.81z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M991.455,94.533c-0.377,0.468-0.562,1.023-0.562,1.582
					c0,0.647,0.25,1.274,0.748,1.748c1.982,1.889,4.498,3.54,7.994,5.067c3.222,1.404,7.288,2.702,12.479,3.959
					c-1.035-8.376-3.961-16.058-8.439-24.222c-0.047-0.089-0.153-0.146-0.264-0.146l-0.166,0.055
					C998.595,86.21,995.002,90.108,991.455,94.533z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M965.361,84.502c-0.274,0.135-0.598,0.13-0.866-0.009
					c-0.273-0.14-0.457-0.395-0.497-0.688c-0.629-4.542-0.938-8.36-0.938-11.656c-0.003-5.153,0.758-9.041,2.235-12.329
					c0.729-1.624,2.39-2.582,4.124-2.581c0.438,0,0.878,0.061,1.311,0.185c5.628,1.623,10.854,3.507,16.202,6.477
					c0.714,0.396,1.089,1.117,1.087,1.827c0.002,0.481-0.173,0.98-0.53,1.383C980.797,74.555,973.905,80.373,965.361,84.502z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M985.99,65.485c-5.188-2.877-10.248-4.706-15.803-6.31
					c-0.255-0.072-0.513-0.106-0.769-0.106c-1.02,0.001-1.974,0.562-2.382,1.479c-1.354,3.009-2.08,6.611-2.08,11.599
					c0,2.866,0.244,6.192,0.745,10.098c7.714-3.951,14.081-9.36,20.351-16.337l0.069-0.183
					C986.12,65.616,986.071,65.529,985.99,65.485z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1018.91,49.466c0-0.082,0.007-0.168,0.018-0.257h-0.002
					c1.251-9.787,3.616-18.33,8.322-26.357c0.151-0.259,0.422-0.428,0.728-0.459c0.305-0.03,0.605,0.081,0.81,0.305
					c4.036,4.401,6.944,8.103,8.991,11.431c2.048,3.328,3.229,6.292,3.779,9.181c0.051,0.259,0.071,0.517,0.071,0.77
					c-0.004,1.891-1.299,3.575-3.196,4.146c-5.609,1.685-11.035,2.935-17.189,3.351l-0.152,0.006c-0.607,0-1.157-0.248-1.544-0.63
					C1019.156,50.569,1018.91,50.043,1018.91,49.466z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M1020.896,49.665l0.192,0.081l0.019-0.001
					c5.968-0.403,11.225-1.611,16.76-3.272c1.113-0.331,1.869-1.333,1.865-2.396c-0.002-0.146-0.014-0.291-0.041-0.438
					c-0.506-2.645-1.588-5.388-3.547-8.574c-1.804-2.937-4.367-6.246-7.875-10.151c-4.121,7.434-6.283,15.353-7.458,24.521
					l-0.002,0.013l-0.003,0.021L1020.896,49.665z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M997.974,47.007c-0.374,0.258-0.819,0.401-1.29,0.401
					c-0.332,0-0.675-0.074-0.99-0.232c-9.087-4.497-16.58-9.572-22.771-16.604c-0.198-0.226-0.273-0.527-0.196-0.815
					c0.073-0.288,0.286-0.521,0.572-0.631c8.888-3.362,15.153-4.82,20.36-4.823c1.431,0,2.777,0.111,4.072,0.323
					c2.185,0.364,3.71,2.219,3.71,4.307l-0.005,0.209v0.012c-0.289,5.664-0.938,11.015-2.646,16.739
					C998.65,46.361,998.348,46.747,997.974,47.007z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M999.546,28.932c0-1.226-0.904-2.304-2.133-2.5
					c-1.188-0.194-2.427-0.296-3.754-0.298c-4.588-0.001-10.293,1.242-18.385,4.22c5.812,6.294,12.771,10.977,21.284,15.191
					l0.124,0.029l0.184-0.059l0.101-0.132c1.654-5.549,2.288-10.729,2.575-16.316L999.546,28.932z"
                        />
                      </g>
                    </g>
                    <g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M519.113,65.057c-0.012,0.242-0.04,0.482-0.055,0.725
					c-0.054,0.859-0.115,1.716-0.206,2.566c-0.04,0.387-0.1,0.772-0.146,1.158c-0.084,0.678-0.166,1.355-0.271,2.026
					c-0.091,0.575-0.2,1.145-0.308,1.716c-0.083,0.452-0.161,0.907-0.256,1.357c-0.158,0.768-0.34,1.531-0.529,2.293
					c-0.053,0.224-0.103,0.444-0.16,0.67c-6.229,23.989-26.315,42.976-51.525,48.726c-0.2,0.045-0.404,0.09-0.605,0.135
					c-0.798,0.174-1.598,0.334-2.402,0.48c-0.402,0.073-0.806,0.142-1.21,0.207c-0.646,0.105-1.293,0.207-1.946,0.298
					c-0.558,0.075-1.121,0.142-1.684,0.204c-0.536,0.061-1.073,0.124-1.611,0.17c-0.651,0.06-1.305,0.102-1.96,0.145
					c-0.489,0.03-0.979,0.064-1.469,0.086c-1.063,0.049-2.128,0.078-3.203,0.078c-19.232,0-36.637-7.431-49.239-19.448
					c-8.666-8.266-15.057-18.691-18.206-30.359c-0.573-2.123-1.037-4.284-1.39-6.483c-0.526-3.297-0.804-6.676-0.804-10.112
					c0-1.009,0.032-2.007,0.08-3.001c0.012-0.247,0.025-0.495,0.042-0.742c0.053-0.936,0.123-1.864,0.219-2.787
					c0.006-0.058,0.01-0.114,0.016-0.173c0.106-1.013,0.242-2.018,0.396-3.016c0.024-0.166,0.052-0.332,0.08-0.496
					c0.155-0.965,0.329-1.927,0.529-2.877c0.004-0.022,0.009-0.048,0.013-0.07c0.219-1.034,0.466-2.062,0.734-3.081
					c0.013-0.049,0.028-0.104,0.043-0.155c0.823-3.084,1.869-6.082,3.129-8.979c5.919-13.63,16.455-24.969,29.757-32.245
					c6.604-3.613,14.146-5.402,21.781-5.402h31.983c1.963,0,3.917,0.347,5.753,1.019c24.199,8.848,41.896,30.194,44.398,55.759
					c0.088,0.895,0.157,1.792,0.208,2.693c0.01,0.157,0.021,0.314,0.029,0.471c0.051,1.012,0.079,2.024,0.081,3.046
					c0,0.012,0,0.025,0,0.04c0,0.059-0.005,0.114-0.005,0.171C519.195,62.933,519.166,63.999,519.113,65.057z"
                        />
                        <path
                          fill="none"
                          class="part-svg"
                          id="svg-right-front-rim"
                          style={getPartStyle("right-front-rim")}
                          onMouseEnter={(e) =>
                            onPartHover("right-front-rim", e)
                          }
                          onClick={(e) => onPartClickFunc("right-front-rim", e)}
                          onMouseLeave={onPartLeave}
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M503.998,58.398l-0.001-0.015c0-0.021-0.002-0.041-0.005-0.062
					c-0.066-0.991-0.166-1.975-0.287-2.947c-0.035-0.26-0.069-0.521-0.104-0.778c-0.062-0.43-0.125-0.857-0.199-1.284
					c-0.098-0.579-0.205-1.157-0.321-1.729l-0.003-0.009c-0.038-0.188-0.074-0.38-0.117-0.57l-0.032-0.155h-0.006
					c-4.368-19.641-20.354-35.174-40.776-39.771v-0.009l-0.752-0.157l-0.009-0.002c-0.554-0.118-1.11-0.227-1.669-0.326
					c-0.051-0.009-0.101-0.017-0.151-0.026c-1.013-0.177-2.033-0.329-3.062-0.452c-0.146-0.02-0.293-0.037-0.441-0.055
					c-0.534-0.061-1.069-0.116-1.608-0.16l-0.008-0.002l-0.788-0.064l-0.002,0.009c-1.351-0.097-2.712-0.159-4.087-0.159
					c-19.387,0-36.399,9.648-46.076,24.182l-0.014-0.007l-0.414,0.646l0,0c-0.616,0.955-1.193,1.931-1.743,2.922
					c-0.01,0.02-0.02,0.037-0.028,0.053c-0.546,0.985-1.054,1.99-1.534,3.01c0,0.006,0,0.008,0,0.008l-0.02,0.038
					c-3.029,6.47-4.726,13.63-4.726,21.173c0,12.94,4.968,24.768,13.167,33.871l-0.005,0.006l0.514,0.556l0.005,0.005
					c1.431,1.542,2.955,3.004,4.563,4.375l0.002,0.004l0.583,0.492l0.01-0.004c9.572,7.916,22.052,12.716,35.716,12.717
					c7.821,0,15.252-1.582,21.978-4.409l0.017-0.008c0.002-0.001,0.004-0.001,0.004-0.001c0.875-0.367,1.732-0.755,2.577-1.162
					c0.017-0.01,0.032-0.02,0.049-0.023c0.845-0.408,1.672-0.836,2.487-1.285l0.009-0.002l0.683-0.374l0,0
					c16.011-9.064,26.75-25.711,26.75-44.756C504.119,60.584,504.071,59.488,503.998,58.398z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M478.18,87.783c-1.463-0.001-2.875-0.693-3.731-1.909
					c-3.225-4.594-5.973-9.146-8.111-14.586c-0.092-0.233-0.135-0.474-0.135-0.711c0-0.492,0.186-0.952,0.499-1.315
					c0.312-0.363,0.77-0.635,1.304-0.719c4.999-0.796,9.767-1.253,14.438-1.253c4.626,0,9.164,0.448,13.733,1.452
					c0.296,0.066,0.539,0.264,0.656,0.529c0.115,0.268,0.089,0.569-0.071,0.813c-3.182,4.864-5.99,8.517-8.679,11.288
					c-2.687,2.77-5.26,4.66-7.936,5.961C479.52,87.638,478.843,87.783,478.18,87.783z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M486.708,80.16c2.369-2.441,4.896-5.675,7.736-9.925
					c-3.984-0.78-7.945-1.159-12.001-1.159c-4.546,0-9.207,0.444-14.128,1.229l-0.169,0.097l-0.072,0.177l0.018,0.085
					c2.072,5.272,4.734,9.684,7.916,14.22c0.497,0.71,1.334,1.116,2.175,1.114c0.379-0.001,0.759-0.081,1.115-0.256
					C481.748,84.551,484.134,82.812,486.708,80.16z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M477.368,106.448L477.368,106.448l-0.683,0.374l-0.009,0.002
					c-0.815,0.449-1.644,0.877-2.487,1.285c-0.018,0.005-0.033,0.015-0.048,0.023c-0.846,0.407-1.702,0.795-2.575,1.162
					c0,0-0.004,0-0.006,0.001l-0.017,0.008l-0.705,0.296l-0.012-0.015c-6.538,2.642-13.712,4.128-21.261,4.128
					c-13.665-0.001-26.144-4.801-35.715-12.717L413.841,101l-0.583-0.492l-0.002-0.004c-1.608-1.371-3.133-2.833-4.563-4.375
					l-0.005-0.005l-0.514-0.556l0.005-0.007c-8.199-9.104-13.167-20.93-13.167-33.87c0-7.289,1.598-14.218,4.437-20.515
					l-0.017-0.012l0.306-0.647l0.016-0.038c0,0,0.003-0.002,0.003-0.007c0.48-1.02,0.988-2.023,1.534-3.01
					c0.009-0.016,0.019-0.033,0.028-0.053c0.549-0.991,1.127-1.967,1.743-2.922l0,0l0.414-0.646l0.014,0.007
					c9.677-14.532,26.688-24.182,46.076-24.182c1.375,0,2.737,0.062,4.087,0.159l0.002-0.009l0.788,0.064l0.008,0.002
					c0.539,0.044,1.074,0.101,1.608,0.16c0.147,0.017,0.294,0.035,0.441,0.055c1.029,0.123,2.05,0.275,3.062,0.452
					c0.051,0.01,0.101,0.019,0.151,0.026c0.56,0.101,1.116,0.208,1.669,0.326l0.009,0.002l0.752,0.156v0.01
					c20.423,4.599,36.408,20.132,40.776,39.771h0.006l0.032,0.155l0.117,0.57l0.003,0.009c0.117,0.572,0.226,1.149,0.321,1.729
					c0.074,0.427,0.136,0.854,0.199,1.284c0.036,0.257,0.068,0.52,0.104,0.778c0.121,0.976,0.22,1.956,0.287,2.947
					c0.003,0.021,0.005,0.041,0.005,0.062l0.001,0.014l0.05,0.739l-0.01,0.002c0.046,0.846,0.081,1.695,0.081,2.553
					C504.119,80.737,493.379,97.384,477.368,106.448z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M449.567,111.927c7.165,0,13.988-1.369,20.215-3.839
					l-21.219-30.334c-0.391-0.512-0.998-0.785-1.619-0.786c-0.383,0.001-0.765,0.104-1.104,0.32L415.36,99.88
					C424.568,107.386,436.501,111.927,449.567,111.927z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M401.009,42.178c-2.654,6-4.123,12.59-4.123,19.515
					c0,12.364,4.694,23.678,12.464,32.43l23.574-29.077c0.271-0.346,0.398-0.743,0.398-1.139c-0.002-0.569-0.261-1.122-0.76-1.5
					L401.009,42.178z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M449.567,11.456c-14.551,0.001-27.715,5.622-37.25,14.714
					c-2.655,2.531-5.016,5.342-7.059,8.364l35.545,13.727c0.211,0.066,0.425,0.1,0.631,0.1c0.845,0,1.636-0.518,1.907-1.329
					l9.828-35.443C451.978,11.508,450.779,11.456,449.567,11.456z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M486.818,26.17c-6.706-6.394-15.208-11.059-24.721-13.271
					L461.109,49.7c0.027,1.035,0.921,1.857,2,1.857l0.056-0.002l0.01-0.001h0.002l37.833-0.674
					C498.817,41.349,493.799,32.83,486.818,26.17z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M501.841,55.569c-0.03-0.24-0.061-0.479-0.094-0.718
					c-0.06-0.42-0.123-0.841-0.193-1.26c-0.053-0.312-0.114-0.621-0.172-0.93h-0.002l-38.168,0.677l0,0l-0.103,0.004
					c-2.102,0-3.825-1.598-3.875-3.609v-0.028l1-37.199c-0.291-0.056-0.583-0.118-0.881-0.172c-0.022-0.004-0.048-0.008-0.074-0.01
					c-1.005-0.182-2.023-0.332-3.05-0.455c-0.125-0.016-0.246-0.03-0.374-0.043c-0.262-0.031-0.528-0.056-0.793-0.08l-9.929,35.805
					l-0.004,0.015c-0.524,1.577-2.05,2.584-3.694,2.583c-0.413,0-0.832-0.064-1.244-0.198l-0.025-0.009l-35.896-13.862
					c-0.15,0.243-0.297,0.483-0.444,0.725c-0.287,0.481-0.582,0.962-0.853,1.454c0,0-0.009,0.01-0.011,0.018
					c-0.409,0.74-0.78,1.503-1.151,2.265l31.866,20.432l0.029,0.021c0.976,0.728,1.492,1.817,1.491,2.917
					c0,0.766-0.252,1.545-0.77,2.207l-0.008,0.008L410.62,95.479c1.059,1.11,2.168,2.175,3.324,3.188L444.76,75.83l0.025-0.02
					c0.66-0.423,1.417-0.629,2.16-0.629c1.199-0.001,2.386,0.535,3.141,1.528l0.011,0.015l21.415,30.62
					c0.604-0.263,1.208-0.525,1.8-0.812c0.005-0.003,0.007-0.005,0.007-0.005c0.593-0.286,1.168-0.595,1.744-0.897l-13.281-34.729
					l-0.008-0.028c-0.12-0.361-0.177-0.733-0.177-1.095c0-1.511,0.979-2.918,2.536-3.466l0.052-0.021l37.881-8.542
					C502.007,57.016,501.935,56.292,501.841,55.569z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M502.19,59.557l-37.434,8.44
					c-0.792,0.289-1.286,1.009-1.289,1.778c0.003,0.186,0.03,0.373,0.091,0.557l13.159,34.414c3.683-2.12,7.074-4.649,10.1-7.534
					c9.533-9.095,15.428-21.646,15.43-35.521C502.246,60.975,502.219,60.266,502.19,59.557z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M448.511,80.073c4.723,8.496,7.814,16.595,8.709,25.55
					c0.029,0.288-0.087,0.568-0.317,0.76c-0.23,0.188-0.538,0.259-0.832,0.194c-5.848-1.342-10.367-2.729-13.975-4.284
					c-3.604-1.554-6.301-3.285-8.438-5.296c-0.865-0.812-1.298-1.897-1.298-2.978c0-0.928,0.315-1.859,0.948-2.638
					c3.552-4.371,7.209-8.3,11.945-11.952c0.394-0.306,0.877-0.454,1.342-0.45C447.359,78.979,448.115,79.363,448.511,80.073z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M434.79,92.477c-0.372,0.455-0.555,0.999-0.555,1.543
					c0,0.633,0.246,1.243,0.738,1.704c1.958,1.843,4.441,3.451,7.894,4.94c3.181,1.372,7.196,2.634,12.322,3.86
					c-1.022-8.165-3.912-15.657-8.333-23.615c-0.046-0.089-0.151-0.144-0.261-0.145l-0.163,0.054
					C441.841,84.362,438.294,88.164,434.79,92.477z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M409.025,82.696c-0.272,0.131-0.589,0.127-0.856-0.009
					c-0.269-0.135-0.451-0.385-0.491-0.674c-0.619-4.428-0.924-8.151-0.924-11.362c-0.001-5.025,0.748-8.816,2.207-12.021
					c0.723-1.584,2.36-2.519,4.073-2.518c0.43,0,0.865,0.058,1.292,0.182c5.558,1.581,10.717,3.418,16,6.312
					c0.704,0.388,1.075,1.092,1.072,1.781c0.002,0.471-0.17,0.957-0.525,1.349C424.268,72.999,417.459,78.67,409.025,82.696z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M429.394,64.154c-5.121-2.806-10.118-4.589-15.603-6.15
					c-0.251-0.07-0.506-0.104-0.757-0.104c-1.008,0.002-1.951,0.55-2.354,1.44c-1.337,2.937-2.054,6.447-2.054,11.312
					c0,2.795,0.241,6.035,0.736,9.845c7.617-3.854,13.904-9.127,20.095-15.932l0.066-0.178
					C429.523,64.283,429.474,64.198,429.394,64.154z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M461.901,48.536c0-0.081,0.007-0.163,0.018-0.254h-0.002
					c1.236-9.541,3.57-17.87,8.217-25.697c0.15-0.251,0.417-0.415,0.719-0.448c0.301-0.029,0.598,0.082,0.797,0.295
					c3.984,4.295,6.857,7.903,8.878,11.147c2.021,3.244,3.189,6.136,3.732,8.951c0.05,0.253,0.072,0.504,0.072,0.748
					c-0.004,1.846-1.284,3.486-3.158,4.045c-5.54,1.643-10.896,2.86-16.973,3.266l-0.151,0.007c-0.601,0-1.143-0.242-1.523-0.613
					C462.142,49.611,461.901,49.097,461.901,48.536z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M463.861,48.728l0.189,0.081l0.02-0.001
					c5.892-0.394,11.083-1.572,16.548-3.19c1.099-0.323,1.845-1.3,1.842-2.339c-0.002-0.142-0.014-0.281-0.04-0.425
					c-0.5-2.578-1.566-5.253-3.502-8.36c-1.78-2.864-4.313-6.092-7.775-9.898c-4.069,7.248-6.205,14.97-7.363,23.907l-0.002,0.013
					l-0.003,0.021L463.861,48.728z"
                        />
                      </g>
                      <g>
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M441.228,46.137c-0.369,0.252-0.812,0.394-1.273,0.394
					c-0.329,0-0.668-0.075-0.979-0.228c-8.973-4.387-16.371-9.335-22.483-16.188c-0.195-0.22-0.27-0.517-0.193-0.795
					c0.073-0.282,0.284-0.51,0.567-0.617c8.773-3.278,14.96-4.699,20.102-4.702c1.412,0,2.744,0.109,4.021,0.316
					c2.158,0.354,3.665,2.16,3.665,4.197l-0.005,0.203v0.01c-0.286,5.524-0.928,10.741-2.615,16.324
					C441.895,45.508,441.597,45.885,441.228,46.137z"
                        />
                        <path
                          fill="none"
                          stroke="#000000"
                          stroke-miterlimit="10"
                          d="M442.781,28.514c0-1.194-0.894-2.246-2.107-2.438
					c-1.17-0.189-2.395-0.288-3.706-0.289c-4.529-0.001-10.163,1.209-18.15,4.114c5.739,6.136,12.607,10.701,21.015,14.812
					l0.124,0.03l0.18-0.059l0.1-0.13c1.633-5.41,2.258-10.462,2.542-15.907L442.781,28.514z"
                        />
                      </g>
                    </g>
                  </g>
                  <g>
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M1188.17,196.185l-4.387,1.633c0,0-0.459,0.221-0.941,0.108
			l-9.016,0.111l-6.872-0.239c-13.114-0.415-29.084-1.683-41.909-2.8c-12.229-1.066-19.8-2.248-20.451-2.3
			c-1.653-0.13-2.846-0.675-2.846-0.675c-1.945-0.921-0.985-2.371-0.985-2.371c0.315-0.48,0.661-0.779,1.032-1.045
			c0.021-0.031,1.06-0.817,2.901-1.601c2.117-0.899,3.878-1.593,3.878-1.593c1.504-0.605,3.812-0.989,3.812-0.989
			c7.571-0.648,14.558-1.882,19.891-2.848c0,0,0.774-0.16,1.139-0.229c10.354-1.952,23.079-6.58,25.688-7.272l1.996-0.672
			c9.621-3.164,19.937-7.687,19.937-7.687s0.649-0.424,1.724-0.564c0.754-0.101,2.115,0.207,2.115,0.207l7.038,1.282l0.412,0.018
			c0,0,0.185,0.007,0.312,0.188l0.917,1.285c0,0,0.186,0.222,0.351,0.659c0.069,0.187,0,1.081,0,1.081l-1.396,20.615
			c0,0,0.071,1.691-0.499,2.939C1191.279,195.034,1188.17,196.185,1188.17,196.185z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M294.799,142.976c-0.003-0.005-7.427-4.593-7.431-4.597
			c-0.433-0.578-0.215-13.815-0.382-14.2c1.758,0.931,10.258,4.879,10.341,4.879l15.614,5.121l58.298,13.148l5.794,4.262
			c0.36,0.331,6.905,10.854,7.112,11.006l-87.876-12.07C296.182,147.061,294.966,143.2,294.799,142.976z"
                    />
                    <path
                      fill="none"
                      stroke="#000000"
                      stroke-miterlimit="10"
                      d="M545.74,194.033c0.012,0.008-1.747,0.252,0.168,1.993
			c0.039,0.035,0.092,0.061,0.143,0.09c11.943,6.811,79.442,44.155,100.771,53.874c35.863,16.339,94.303,29.412,145.857,27.129
			c0.236-0.011,24.084,0,58.558-1.677c25.367-1.235,48.49-3.916,65.025-7.692c8.119-1.854,18.829-4.688,24.353-8.004
			c9.786-5.879,27.837-15.175,50.061-42.375l159.993,4.555l-44.415,4.263c0,0-87.179,41.028-174.357,51.153
			c-13.611,1.58-77.639,5.763-145.224,7.127c-13.373,0.271-92.768-4.818-141.139-27.021
			c-16.018-7.352-110.438-60.755-112.867-61.868L545.74,194.033z"
                    />
                  </g>
                </g>
              </svg>
            </div>

            {/* Slide 5 - Top view */}
            <div className="order-5 flex-shrink-0 w-full h-full snap-center px-4 overflow-hidden ">
              <svg
                ref={svgRefs[4]}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox={isInModal ? "-10 20 1584 1086" : "-320 -60 1584 1086"}
                className={isInModal ? "h-auto w-full" : "h-full w-auto"}
              >
                <g transform="translate(160,60)" data-id="car-top">
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M937.818,277.706c0.5,0.075,65.709,6.973,72.764,7.994    c-0.309,1.701-0.607,5.321,1.875,6.971c0.127,0.107,11.193,9.465,18.135,20.755c-2.773-2.273-5.057-2.795-5.875-2.913    c-1.762-2.366-14.592-18.718-36.199-23.188c-0.305-0.062-46.334-5.857-54.215-7.398    C934.9,278.953,936.043,277.594,937.818,277.706z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M849.162,269.842c31.145,0.968,61.135,4.228,85.285,7.236    c-0.588,0.514-1.111,1.164-1.553,1.953c-0.211,0.375-0.229,0.833-0.045,1.22c0.18,0.386,0.537,0.665,0.957,0.746    c7.387,1.436,55.246,8.328,55.557,8.392c18.768,3.882,30.742,16.976,34.32,21.381l-68.432-3.454    c-21.498-12.5-45.363-17.431-69.146-18.49c-5.746-0.255-11.176-0.497-16.312-0.882c-7.111-0.533-14.23-1.035-21.355-1.516    C848.746,283.459,849.627,274.144,849.162,269.842z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M706.354,545.908l-152.828,3.156    c-2.467-16.365-11.806-80.496-12.172-116.451c0.366-35.965,9.705-100.099,12.172-116.463l152.834,3.158    c0.344,0.002,10.75,0.079,26.143,0.079c35.914,0,62.102,0.395,89.084-1.129c1.154,16.083,5.365,74.951,5.729,114.342    c-0.363,39.402-4.574,97.947-5.729,114.033C785.088,544.801,707.504,545.896,706.354,545.908z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-bonnet"
                    style={getPartStyle("bonnet")}
                    onMouseEnter={(e) => onPartHover("bonnet", e)}
                    onClick={(e) => onPartClickFunc("bonnet", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M326.34,570.389c-3.417-0.131-6.897-0.246-10.508-0.361    c-33.826-1.092-64.914,4.029-108.119-11.326c-24.961-10.594-34.886-25.873-42.608-38.311c0,0-11.687-23.949-12.694-43.805    c0,0-2.06-24.359-2.06-39.977c0-15.619-1.445-16.568,0.062-28.386c0,0,4.486-41.68,10.64-53.96c0,0,11.531-29.155,36.866-42.953    c27.815-18.73,84.086-15.033,117.912-16.125c3.61-0.117,7.09-0.229,10.508-0.36l-0.332,0.113l55.577-0.113    c-7.772,18.264-11.1,40.429-14.318,60.582c-0.327,2.044-0.651,4.073-0.979,6.082c-3.92,24.14-6.029,48.568-6.27,72.612v0.018    c0.24,24.045,2.35,48.475,6.27,72.613c0.327,2.006,0.651,4.037,0.979,6.084c3.22,20.148,6.546,40.973,14.318,59.234l-55.578-1.775    L326.34,570.389z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-front-windshield"
                    style={getPartStyle("front-windshield")}
                    onMouseEnter={(e) => onPartHover("front-windshield", e)}
                    onClick={(e) => onPartClickFunc("front-windshield", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M550.771,316c-2.799,16.524-13.273,80.597-13.686,116.613    c0.413,36.002,10.887,100.076,13.686,116.6c-39.219,5.883-132.723,22.99-133.824,23.02c-13.836,0.385-29.13,1.891-35.13-11.609    c-2.87-3.641-10.745-43.016-10.745-43.016c-3.797-27.748-7.101-56.67-8.375-85.002c1.274-28.333,4.142-57.019,7.939-84.765    c0.886-6.483,5.936-31.982,9.686-42.232c3.304-12.667,21.497-13.042,35.333-12.667C416.757,292.972,511.487,310.108,550.771,316z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-rear-windshield"
                    style={getPartStyle("rear-windshield")}
                    onMouseEnter={(e) => onPartHover("rear-windshield", e)}
                    onClick={(e) => onPartClickFunc("rear-windshield", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M830.674,432.6c-0.398-41.268-5.43-107.222-5.979-114.292    l96.936-7.537c2.332,1.73,23.08,17.758,30.637,44.637c0.08,0.174,8.451,17.801,8.867,55.322v43.747    c-0.416,37.527-8.785,55.156-8.916,55.447c-7.51,26.766-28.256,42.785-30.588,44.518l-96.934-7.555    C825.252,539.727,830.275,473.846,830.674,432.6z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M869.676,289.492c5.16,0.388,10.602,0.629,16.363,0.886    c22.758,1.013,48.414,6.097,66.818,16.919l-73.297,2.928c-0.002-0.037,0.01-3.982,0.008-4.019    c-0.076-0.905-0.766-1.573-1.672-1.627l-29.623-1.721l-0.162-0.004l-0.045,0.004c-4.572,0.284-9.209,0.504-13.729,0.718    c3.406-3.315,11.545-11.469,13.697-15.62C855.256,288.442,862.471,288.952,869.676,289.492z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M524.094,280.555c0.64,0.007,63.938,0.799,68.852,0.799    c0.253-0.005,25.046-0.463,33.273-0.013c4.436,2.571,11.268,8.336,15.648,20.631l-60.887-1.567    c-28.099-0.949-51.818-1.908-51.971-1.909l-66.481-10.032l-3.51-9.163L524.094,280.555z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M794.604,302.886c-2.867,0.028-5.887,0.077-8.932,0.127    c-4.371,0.07-8.777,0.144-12.811,0.157c-11.988,0.042-23.77-0.019-33.709-0.081c-12.518-0.079-25.033-0.196-37.549-0.332    c-10.949-0.118-21.895-0.252-32.842-0.398c-0.908-8.314-4.721-15.984-7.166-20.164c7.332,0.169,41.408,0.952,51.715,1.233    c0.4,0.015,40.168,1.439,46.939,1.597c0,0,15.291,0.764,22.094,1.097L794.604,302.886z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M667.248,302.727c-3.783-0.05-7.244-0.098-10.307-0.141l0.131-0.038    c-2.297-8.012-9.234-18.707-12.721-22.693c4.707,0.092,9.416,0.194,14.129,0.294C659.73,282.002,666.133,291.96,667.248,302.727z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M655.451,302.565c-5.758-0.08-9.891-0.143-11.787-0.172    c-4.809-14.257-12.676-20.372-17.434-22.874c5.33,0.093,10.67,0.195,16.008,0.297C643.699,281.16,652.469,292.699,655.451,302.565    z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M831.508,301.807c-11.211,0.792-30.029,0.94-33,0.96l-11.385-16.532    c8.234,0.491,46.561,2.775,55.424,3.313C840.176,292.933,835.961,298.428,831.508,301.807z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M771.48,266.834c0.195-0.222,2.529-2.671,8.828-2.351    c3.699,0.071,7.396,0.16,11.096,0.26c2.959,0.079,5.914,0.212,8.877,0.344l0.363,0.018c0.783,0.033,1.596,0.039,2.416,0.042    c2.137,0.011,4.344,0.021,6.246,0.599c1.344,0.407,2.621,1.085,3.822,1.989c-1.77,1.721-4.398,2.275-5.9,2.445    c-0.545,0.062-1.088,0.089-1.633,0.118c-0.523,0.025-1.051,0.053-1.576,0.106c-0.855,0.088-1.447,0.008-1.859-0.253    c-0.385-0.241-0.701-0.735-1.037-1.262c-0.6-0.931-1.342-2.088-2.832-2.15c-1.715-0.07-3.682-0.185-5.643-0.296    c-2.334-0.133-4.66-0.267-6.562-0.332c-1.197-0.042-2.219,0.568-2.803,1.586l-0.277,0.478c-0.266,0.444-0.525,0.891-0.744,1.338    c-3.422-0.069-6.896-0.475-10.254-0.864l-1.031-0.121c-0.332-0.038-0.527-0.145-0.475-0.182L771.48,266.834z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M800.705,270.926c-7.041,0.452-14.191-0.418-16.99-0.823    c0.191-0.379,0.4-0.759,0.625-1.138l0.289-0.493c0.297-0.518,0.787-0.788,1.404-0.811c1.893,0.066,4.207,0.199,6.527,0.331    c1.971,0.113,3.941,0.228,5.666,0.298c0.65,0.027,1.027,0.564,1.588,1.438C800.084,270.145,800.363,270.563,800.705,270.926z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M610.674,262.743c2.578,0.015,4.619,0.053,5.443,0.134l0.111,0.011    l0.115-0.024c0.037-0.007,3.562-0.658,7.547,2.136c-1.709,1.787-4.322,2.433-5.822,2.654c-0.539,0.081-1.082,0.126-1.627,0.172    c-0.52,0.047-1.045,0.092-1.568,0.162c-0.855,0.117-1.449,0.059-1.869-0.188c-0.393-0.229-0.729-0.712-1.08-1.226    c-0.4-0.579-0.867-1.247-1.535-1.66C610.301,264.118,610.496,263.29,610.674,262.743z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M611.576,268.623c-7.023,0.693-14.195,0.066-17.007-0.242    c0.177-0.387,0.377-0.772,0.586-1.159l0.271-0.502c0.28-0.527,0.808-0.855,1.375-0.855c1.885,0,4.189,0.053,6.5,0.105    c1.984,0.045,3.975,0.092,5.709,0.104c0.654,0.004,1.049,0.528,1.641,1.383C610.93,267.863,611.225,268.271,611.576,268.623z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M582.228,265.535c0.217-0.255,2.518-2.758,8.745-2.651    c0.157-0.003,11.375-0.166,18.89-0.144c-0.148,0.508-0.279,1.166-0.27,1.852c-0.18-0.041-0.369-0.067-0.574-0.068    c-1.725-0.014-3.707-0.059-5.686-0.104c-2.322-0.053-4.641-0.105-6.533-0.105c-1.146,0-2.197,0.644-2.745,1.68l-0.265,0.488    c-0.246,0.453-0.491,0.907-0.695,1.362c-3.411,0.054-6.902-0.236-10.273-0.514l-1.039-0.084c-0.334-0.025-0.531-0.128-0.495-0.146    C581.294,267.094,582.087,265.771,582.228,265.535z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M474.81,268.087c2.177,0.054,3.757,0.092,4.334,0.079    c10.699-0.254,21.405-0.575,32.113-0.897c22.61-0.677,45.973-1.37,68.96-1.397l-0.251,0.418c-0.421,0.705-0.254,1.253-0.117,1.521    c0.284,0.552,0.944,0.91,1.81,0.979l1.038,0.084c3.458,0.284,7.029,0.58,10.584,0.516c0.287-0.004,0.558-0.113,0.776-0.288    c1.707,0.2,5.837,0.61,10.683,0.61c2.49,0,5.166-0.122,7.799-0.426c0.521,0.23,1.074,0.312,1.578,0.312    c0.355,0,0.688-0.036,0.967-0.075c0.498-0.068,0.996-0.11,1.492-0.153c0.574-0.049,1.15-0.099,1.721-0.183    c2.213-0.327,5.18-1.223,7.088-3.539l5.578-0.02c45.355-0.165,92.252-0.335,138.053,3.377c0.002,0.006,0.006,0.016,0.008,0.021    c0.268,0.562,0.912,0.942,1.773,1.042l1.027,0.119c3.451,0.402,7.018,0.818,10.566,0.878c0.275-0.005,0.547-0.102,0.775-0.262    c2,0.303,7.41,1.024,13.4,1.024c1.666,0,3.373-0.074,5.066-0.212c0.586,0.283,1.211,0.374,1.771,0.374    c0.277,0,0.541-0.02,0.771-0.044c0.5-0.051,1.002-0.077,1.498-0.102c0.576-0.03,1.15-0.061,1.725-0.125    c2.186-0.247,5.111-1.019,7.098-3.178c3.117,0.917,13.523,1.026,13.508,1.02c6.561-0.041,13.109,0.053,19.621,0.239    c0.393,3.114-0.176,11.121-0.734,16.523c-0.518-0.035-1.039-0.063-1.557-0.098c-0.027-0.005-0.057-0.004-0.082-0.005    c-20.211-1.339-40.479-2.475-60.764-3.44c-0.066-0.015-0.135-0.014-0.203-0.01c-41.748-1.988-83.582-3.265-125.248-4.162    c-0.074-0.015-0.15-0.013-0.229-0.005c-5.428-0.117-10.854-0.23-16.275-0.335c-0.008-0.001-0.021-0.013-0.031-0.013v0.012    c-42.387-0.815-84.557-1.286-126.211-1.745c-21.303-0.235-42.602-0.47-63.898-0.75c-0.085-0.101-0.201-0.177-0.278-0.285    c-1.616-2.31-0.729-5.804-0.416-6.833c0.372-0.146,0.762-0.287,1.177-0.419C456.988,267.65,469.636,267.96,474.81,268.087z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-right-mirror"
                    style={getPartStyle("right-mirror")}
                    onMouseEnter={(e) => onPartHover("right-mirror", e)}
                    onClick={(e) => onPartClickFunc("right-mirror", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M440.391,261.871c2.28-11.231,5.118-25.211,13.237-32.525    c2.927-2.639,6.519-4.56,10.392-5.558c1.417-0.366,2.538-0.655,3.207-0.655c0.537,0,0.833,0,1.132,2.44    c0.427,3.477-5.284,35.768-6.485,39.224c-1.49,0.192-6.128,0.857-9.646,2.02c-0.718,0.137-1.18,0.302-1.473,0.494    c-0.806,0.044-3.477,0.174-6.593,0.174c-1.802,0-3.45-0.042-4.914-0.128C439.632,265.61,440.006,263.772,440.391,261.871z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M439.059,268.893c1.521,0.091,3.227,0.141,5.103,0.141    c2.448,0,4.62-0.079,5.863-0.136c-0.414,1.688-0.89,4.959,0.768,7.405c1.264,1.867,3.468,2.865,6.555,2.965l4.296,11.213    c-14.176-2.566-28.053-5.221-39.889-7.542c-0.441-1.216-1.721-4.878-1.995-7.1l13.513-0.508    C437.575,274.826,438.895,271.28,439.059,268.893z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M419.984,282.591c-11.499-2.264-20.919-4.18-26.571-5.342    l24.784-1.339C418.431,277.921,419.393,280.897,419.984,282.591z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M217.419,284.775c1.814-0.392,3.38-0.731,4.451-1.021    c27.319-7.416,49.934-11.517,71.171-12.907c22.425-1.471,70.127-2.979,92.228-2.261l8.045,0.257h44.189    c-0.115,1.441-0.78,4.521-4.353,4.943l-14.098,0.527l-0.003-0.004l-30.532,1.651c-0.472,0.025-0.842,0.389-0.877,0.859    c-0.035,0.466,0.271,0.875,0.731,0.971c11.646,2.425,114.791,23.723,150.188,25.332c1.125,0.029,112.77,2.985,142.254,3.406    h114.168c10.428,0,21.004-0.515,31.238-1.014l7.139-0.342c4.854-0.229,9.867-0.464,14.826-0.771l29.617,1.722    c0.051,0.004,0.203,0.014,0.219,0.215c0.021,0.201-0.131,0.237-0.178,0.249l0.18,4.182c-85.635,7.119-339.456,0.713-343.198,0.601    l-118.521-19.514c-7-1.528-95.057,1.6-100.525,1.776c-10.148,0.329-47.442,1.809-58.003,2.63    c-3.713-2.931-7.302-3.659-11.068-4.245c-0.416-0.064-0.783-0.122-1.088-0.173c-7.71-1.319-15.702-1.462-23.112-0.419    c-6.045,0.851-11.688,2.841-17.146,4.767c-1.837,0.648-3.684,1.299-5.559,1.914c-1.043-0.121-6.612-0.765-8.655-1.002    C192.36,290.196,209.192,286.555,217.419,284.775z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M185.524,301.71c0.102-0.109,4.29-4.355,4.535-4.405    c0.074,0.009,9.586,1.108,9.586,1.108l0.169,0.02l0.161-0.053c1.942-0.634,3.852-1.309,5.752-1.979    c5.633-1.987,10.955-3.864,16.847-4.694c7.252-1.021,15.08-0.878,22.635,0.413c0.312,0.054,0.686,0.111,1.109,0.178    c3.015,0.469,9.457,1.477,11.458,3.967c-24.271,2.01-48.469,6.687-67.785,19.695c-9.347,6.294-18.926,19.006-25.034,29.479    l2.507-10.447C171.378,323.904,173.444,314.398,185.524,301.71z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M166.3,541.873c-9.14-13.17-18.925-41.68-19.026-41.979    c-3.025-8.443-4.567-17.477-5.675-25.199c-0.281-1.943-0.568-3.896-0.859-5.85c-1.765-11.861-3.59-24.127-3.001-36.201    l-0.774-0.039l0.774-0.037c-0.589-12.074,1.236-24.34,3.001-36.202c0.291-1.954,0.579-3.906,0.859-5.85    c1.106-7.723,2.649-16.756,5.681-25.212c0.097-0.286,9.881-28.794,19.01-41.952c0.079-0.107,7.069-8.38,12.694-13.826    c-5.44,7.539-8.364,16.026-11.154,23.941l-2.873,11.971c-6.264,12.551-10.554,27.812-12.932,45.991    c-1.581,12.088-2.316,25.17-2.316,41.175c0,16.004,0.735,29.088,2.316,41.174c2.378,18.182,6.667,33.441,12.932,45.99    l4.339,13.316c2.774,7.859,5.815,15.182,10.498,22.988C174.169,550.627,166.371,541.969,166.3,541.873z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M185.978,564.01c-0.196-0.223-0.352-0.398-0.454-0.506    c-8.859-9.543-13.296-22.111-17.188-33.123l-2.791-9.127c6.107,10.471,15.1,21.705,24.445,27.998    c19.316,13.006,43.248,17.98,67.517,19.99c-2,2.49-8.174,3.201-11.19,3.67c-0.424,0.064-0.8,0.127-1.11,0.18    c-7.556,1.291-15.38,1.434-22.635,0.41c-5.891-0.826-11.208-2.703-16.842-4.691c-1.901-0.672-3.812-1.344-5.757-1.979    l-0.161-0.055l-0.169,0.021c0,0-9.473,1.094-9.529,1.105C189.748,567.846,188.731,567.123,185.978,564.01z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M393.292,596.369l-8.022,0.258    c-22.101,0.719-69.738-1.076-92.165-2.545c-21.234-1.391-43.849-5.492-71.171-12.908c-1.07-0.289-2.636-0.627-4.451-1.023    c-8.227-1.777-25.059-5.418-26.268-12.328c2.047-0.234,7.615-0.879,8.656-1c1.876,0.613,3.724,1.268,5.562,1.916    c5.456,1.924,11.096,3.914,17.14,4.764c3.263,0.461,6.637,0.689,10.053,0.689c4.339,0,8.743-0.371,13.06-1.105    c0.305-0.053,0.672-0.111,1.089-0.176c3.767-0.584,8.729-1.053,10.397-3.693c10.562,0.816,48.461,2.033,58.611,2.363    c5.468,0.176,10.637,0.344,15.727,0.58c1.4,0.445,31.188,1.912,42.827,2.018c13.473,0.121,29.369,1.549,49.396-1.607    l111.089-18.727c3.604-0.109,259.07-6.531,344.765,0.598l-1.74,4.182c0.047,0.012,0.199,0.047,0.178,0.25    c-0.016,0.199-0.168,0.211-0.219,0.213l-29.645,1.723c-4.939-0.305-9.967-0.541-14.83-0.773l-7.107-0.34    c-10.234-0.498-20.812-1.014-31.238-1.014H680.803c-29.473,0.42-141.117,3.377-142.255,3.408    c-35.339,1.605-138.522,22.906-150.173,25.328c-0.46,0.098-0.768,0.508-0.731,0.977c0.035,0.469,0.405,0.83,0.878,0.855    l30.205,1.631c0.018,0.004,0.035,0.004,0.053,0.004c0.039,0,0.075-0.018,0.113-0.021l-0.001,0.027l14.2,0.527    c3.6,0.422,4.286,3.506,4.408,4.949h-44.208V596.369z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M592.931,583.859c-4.899,0-68.198,0.791-68.842,0.799l-65.07,1.254    l3.51-9.162l66.396-10.023c0.236-0.01,23.957-0.971,52.048-1.916l60.894-1.566c-4.381,12.293-11.213,18.059-15.646,20.629    C617.992,584.322,593.198,583.863,592.931,583.859z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M643.664,562.818c1.896-0.029,6.029-0.09,11.789-0.172    c-2.984,9.867-11.756,21.408-13.215,22.748c-5.34,0.104-10.678,0.205-16.01,0.299C630.986,583.189,638.855,577.08,643.664,562.818    z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M657.072,562.666l-0.129-0.041    c3.061-0.041,6.521-0.092,10.305-0.143c-1.115,10.764-7.52,20.729-8.77,22.58c-4.711,0.102-9.42,0.201-14.127,0.295    C647.836,581.371,654.773,570.678,657.072,562.666z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M760.271,580.186c-6.793,0.158-46.561,1.582-46.953,1.598    c-10.312,0.281-44.391,1.064-51.723,1.23c2.445-4.176,6.26-11.846,7.166-20.162c10.949-0.145,21.893-0.277,32.842-0.398    c12.518-0.137,25.033-0.252,37.551-0.332c10.008-0.061,21.842-0.123,33.709-0.08c4.021,0.016,8.422,0.088,12.785,0.158    c3.053,0.051,6.08,0.098,8.955,0.129l-12.26,16.762C775.549,579.42,760.279,580.186,760.271,580.186z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M842.547,575.668c-8.861,0.535-47.189,2.818-55.422,3.311    l11.385-16.531c2.967,0.02,21.787,0.168,32.998,0.961C835.961,566.787,840.174,572.277,842.547,575.668z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M419.984,582.623c-0.591,1.693-1.554,4.672-1.787,6.68l-24.783-1.34    C399.067,586.799,408.485,584.885,419.984,582.623z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M433.21,589.875l-13.451-0.5c0.273-2.223,1.553-5.883,1.995-7.1    c11.835-2.32,25.712-4.977,39.888-7.545l-4.295,11.215c-3.087,0.102-5.292,1.096-6.555,2.967c-1.659,2.445-1.18,5.723-0.769,7.402    c-1.244-0.057-3.416-0.137-5.862-0.137c-1.876,0-3.581,0.051-5.104,0.141C438.895,593.932,437.569,590.385,433.21,589.875z"
                  />
                  <path
                    fill="none"
                    class="part-svg"
                    id="svg-left-mirror"
                    style={getPartStyle("left-mirror")}
                    onMouseEnter={(e) => onPartHover("left-mirror", e)}
                    onClick={(e) => onPartClickFunc("left-mirror", e)}
                    onMouseLeave={onPartLeave}
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M468.359,639.639c-0.3,2.434-0.594,2.441-1.126,2.441    c-0.001,0-0.003,0-0.005,0c-0.669,0-1.79-0.289-3.208-0.656c-3.873-0.998-7.465-2.92-10.392-5.557    c-8.119-7.316-10.957-21.297-13.237-32.525c-0.385-1.902-0.759-3.74-1.143-5.484c1.464-0.086,3.112-0.129,4.913-0.129    c3.138,0,5.824,0.133,6.609,0.174c0.29,0.186,0.739,0.348,1.423,0.482c3.569,1.184,8.304,1.854,9.682,2.035    C463.081,603.898,468.786,636.168,468.359,639.639z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M590.95,602.33c-6.174,0.111-8.534-2.439-8.726-2.658    c-0.154-0.25-0.979-1.588-1.013-1.535c0.04-0.043,0.237-0.143,0.571-0.168l1.021-0.086c3.376-0.275,6.859-0.561,10.291-0.512    c0.203,0.455,0.449,0.908,0.695,1.361l0.265,0.486c0.548,1.035,1.601,1.68,2.744,1.68c1.902,0,4.229-0.051,6.559-0.105    c1.969-0.045,3.941-0.092,5.66-0.102c0.205-0.004,0.396-0.027,0.576-0.068c-0.012,0.684,0.119,1.34,0.268,1.85    C602.346,602.498,591.125,602.33,590.95,602.33z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M594.569,596.832c2.812-0.307,9.984-0.936,17.007-0.242    c-0.352,0.352-0.646,0.76-0.928,1.168c-0.59,0.854-0.984,1.377-1.641,1.381c-1.727,0.018-3.707,0.062-5.686,0.105    c-2.316,0.053-4.631,0.105-6.521,0.105c-0.568,0-1.095-0.33-1.375-0.857l-0.271-0.498    C594.944,597.605,594.746,597.219,594.569,596.832z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M616.344,602.348l-0.113-0.021l-0.113,0.01    c-0.824,0.08-2.865,0.121-5.443,0.133c-0.178-0.545-0.373-1.373-0.285-2.17c0.67-0.414,1.137-1.078,1.535-1.658    c0.354-0.514,0.689-0.998,1.082-1.229c0.422-0.246,1.014-0.305,1.869-0.188c0.521,0.07,1.045,0.115,1.566,0.162    c0.545,0.047,1.09,0.096,1.627,0.172c1.5,0.221,4.115,0.867,5.824,2.656C619.943,602.982,616.494,602.377,616.344,602.348z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M809.307,599.465c-1.902,0.578-4.111,0.59-6.248,0.6    c-0.822,0.004-1.631,0.01-2.414,0.045l-0.479,0.02c-2.92,0.131-5.84,0.262-8.764,0.336c-3.699,0.104-7.395,0.191-11.115,0.264    c-6.314,0.318-8.693-2.223-8.822-2.365l-1.057-1.49c0.039-0.045,0.234-0.15,0.566-0.189l1.031-0.119    c3.357-0.391,6.832-0.797,10.254-0.867c0.219,0.449,0.479,0.893,0.738,1.338l0.283,0.48c0.584,1.018,1.658,1.633,2.801,1.584    c1.896-0.064,4.221-0.195,6.545-0.33c1.971-0.111,3.939-0.225,5.66-0.297c1.492-0.061,2.234-1.219,2.832-2.15    c0.338-0.523,0.654-1.02,1.039-1.264c0.412-0.258,1.004-0.338,1.859-0.25c0.525,0.055,1.053,0.08,1.576,0.107    c0.545,0.025,1.088,0.055,1.633,0.117c1.502,0.17,4.131,0.725,5.9,2.443C811.928,598.381,810.65,599.059,809.307,599.465z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M783.715,595.107c2.799-0.402,9.943-1.273,16.99-0.82    c-0.344,0.363-0.621,0.781-0.891,1.197c-0.561,0.875-0.938,1.41-1.588,1.438c-1.73,0.072-3.711,0.188-5.688,0.297    c-2.312,0.135-4.621,0.268-6.51,0.332c-0.533-0.014-1.105-0.291-1.402-0.809l-0.291-0.498    C784.113,595.867,783.902,595.486,783.715,595.107z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M847.627,595.416c-6.512,0.184-13.062,0.273-19.623,0.232    c-0.047,0.037-10.383,0.094-13.51,1.018c-1.982-2.158-4.91-2.932-7.096-3.176c-0.572-0.064-1.146-0.096-1.725-0.127    c-0.496-0.023-0.996-0.051-1.496-0.1c-0.672-0.07-1.629-0.078-2.492,0.32c-7.807-0.637-15.963,0.432-18.516,0.818    c-0.232-0.168-0.512-0.27-0.779-0.26c-3.549,0.062-7.115,0.479-10.564,0.879l-1.025,0.119c-0.865,0.1-1.512,0.479-1.777,1.041    c-0.002,0.006-0.006,0.018-0.008,0.021c-45.818,3.719-92.729,3.547-138.1,3.379l-5.531-0.021    c-1.908-2.314-4.875-3.213-7.088-3.541c-0.57-0.082-1.146-0.133-1.721-0.182c-0.496-0.045-0.996-0.084-1.492-0.154    c-0.674-0.09-1.625-0.133-2.506,0.236c-7.771-0.896-15.947-0.111-18.521,0.186c-0.22-0.172-0.489-0.279-0.776-0.285    c-3.562-0.064-7.138,0.232-10.601,0.518l-1.021,0.082c-0.864,0.068-1.524,0.426-1.811,0.979c-0.137,0.268-0.304,0.816,0.121,1.531    l0.247,0.408c-22.999-0.025-46.374-0.719-68.998-1.396c-10.696-0.32-21.391-0.643-32.076-0.896    c-0.575-0.008-2.148,0.027-4.32,0.078c-5.177,0.131-17.828,0.439-21.947-0.146c-0.414-0.137-0.807-0.275-1.179-0.424    c-0.312-1.029-1.199-4.521,0.416-6.83c0.077-0.109,0.192-0.186,0.278-0.285c21.291-0.281,42.585-0.516,63.881-0.75    c41.662-0.461,83.837-0.93,126.23-1.748v0.016c0.008,0,0.021-0.016,0.029-0.016c5.432-0.104,10.865-0.217,16.303-0.334    c0.018,0.002,0.033,0.018,0.051,0.018c0.027,0,0.051-0.018,0.078-0.02c41.686-0.896,83.545-2.178,125.311-4.164    c0.016,0,0.031,0.018,0.045,0.018c0.031,0,0.061-0.021,0.09-0.023c20.285-0.967,40.551-2.1,60.762-3.439    c0.008,0,0.018,0.008,0.023,0.008c0.014,0,0.025-0.01,0.037-0.01c0.555-0.037,1.107-0.068,1.66-0.105    C847.453,584.293,848.021,592.299,847.627,595.416z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M848.035,577.254c-2.152-4.15-10.291-12.305-13.697-15.617    c4.521,0.213,9.16,0.434,13.73,0.719l0.164,0.002l29.664-1.725c0.908-0.053,2.824-4.641,2.9-5.547    c0.002-0.035-0.01-0.064-0.008-0.102l73.297,2.926c-18.408,10.822-45.291,15.912-68.049,16.924    c-5.762,0.258-11.201,0.498-16.361,0.885C862.471,576.26,855.256,576.77,848.035,577.254z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M849.16,595.373c0.469-4.301-0.414-13.619-0.723-16.59    c7.125-0.482,14.244-0.984,21.355-1.52c5.137-0.381,10.562-0.625,16.311-0.877c23.785-1.061,50.594-6.326,69.461-18.508    l68.123-3.438c-3.566,4.402-15.531,17.494-34.324,21.387c-0.309,0.064-48.17,6.949-55.557,8.387    c-0.418,0.084-0.777,0.359-0.955,0.746c-0.184,0.387-0.168,0.844,0.045,1.223c0.438,0.783,0.959,1.438,1.547,1.951    C910.289,591.143,880.301,594.41,849.16,595.373z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M1012.523,572.49c-2.545,1.699-2.25,5.322-1.941,7.021    c-7.055,1.023-72.979,8.312-73.412,8.381c-1.818,0.082-2.984-1.238-3.584-2.213c7.877-1.541,54.625-7.729,54.93-7.793    c21.615-4.473,34.451-20.834,36.201-23.191c0.891-0.133,3.152-0.688,5.871-2.906    C1023.65,563.074,1012.594,572.424,1012.523,572.49z"
                  />
                  <path
                    fill="none"
                    stroke="#000000"
                    stroke-miterlimit="10"
                    d="M1051.031,481.475c-1.676,29.746-10.08,50.646-10.193,50.934    c-5.703,19.973-15.379,20.77-15.541,20.781l-99.648,1.795c5.943-5.117,22.35-16.176,28.92-40.768    c0.084-0.186,8.592-18.922,9.018-58.668v-45.895c-0.426-39.736-8.934-58.475-8.971-58.533    c-6.609-24.696-23.283-35.233-29.225-40.35l99.973,1.247c0.096,0.004,9.77,0.806,15.502,20.854    c0.086,0.208,8.488,21.09,10.166,50.867c0.023,0.338,2.555,34.104,2.555,48.861    C1053.584,447.357,1051.055,481.123,1051.031,481.475z"
                  />
                </g>
              </svg>
            </div>
          </div>
        </div>

        {/* View Indicators */}
        {/* <div className="flex justify-center gap-2 mt-4 ">
          {views.map((view) => (
            <button
              key={view.id}
              onClick={() => scrollToView(view.id)}
              className={`w-2 h-2 rounded-full transition-all ${
                activeView === view.id
                  ? "bg-blue-600 w-8"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to ${view.label}`}
            />
          ))}
        </div> */}
      </div>
    );
  }
);

CarSVG.displayName = "CarSVG";
